/*
 * Updated for Dog Breed Quiz v2
 * - Embeds ranking engine v2 and quiz copy v2 so no Liquid changes are required.
 * - Keeps the original asset name for drop-in replacement.
 */

/*!
 * Dog Quiz Ranking Engine v2
 *
 * Purpose:
 * - Replaces simplistic quiz scoring with a multi-factor compatibility engine.
 * - Separates hard constraints from soft preferences.
 * - Scores breeds on fit, risk, archetype alignment, and household capability.
 * - Generates reasons, cautions, category breakdowns, and confidence.
 *
 * Expected breed data:
 * Provide normalized trait values in the 0..1 range when possible.
 * Missing traits are derived when feasible and otherwise default to neutral values.
 *
 * Canonical trait keys:
 * size, power, exerciseNeed, mentalNeed, workingDrive, indoorCalm, independence,
 * aloneTimeTolerance, apartmentFriendly, barkiness, alertness, protectiveness,
 * strangerFriendliness, guestEase, dogSociability, childFriendliness,
 * toddlerTolerance, catCompatibility, preyDrive, recallPotential, roamingRisk,
 * trainability, handlerFocus, confidence, sensitivity, cuddliness, playfulness,
 * retrieveDrive, mouthiness, chewingRisk, noviceFriendliness, shedding,
 * groomingNeed, drool, odor, allergyFriendliness, healthRobustness,
 * heatTolerance, coldTolerance
 *
 * Browser usage:
 *   const results = window.DogQuizRankingEngine.rankBreeds({
 *     answers: selectedOptionIds,
 *     breeds: breedProfiles,
 *     limit: 5
 *   });
 *
 * Node usage:
 *   const engine = require('./dog-quiz-ranking-engine-v2');
 */

(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.DogQuizRankingEngine = factory();
  }
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const VERSION = '2.0.1';
  const OPTION_MODEL_ID = 'dog-breed-quiz-current-option-model-v1';
  const TOP_RESULTS_COUNT = 5;
  const RANKING_SCORE_EPSILON = 1e-9;

  const CANONICAL_TRAITS = Object.freeze([
    'size',
    'power',
    'exerciseNeed',
    'mentalNeed',
    'workingDrive',
    'indoorCalm',
    'independence',
    'aloneTimeTolerance',
    'apartmentFriendly',
    'barkiness',
    'alertness',
    'protectiveness',
    'strangerFriendliness',
    'guestEase',
    'dogSociability',
    'childFriendliness',
    'toddlerTolerance',
    'catCompatibility',
    'preyDrive',
    'recallPotential',
    'roamingRisk',
    'trainability',
    'handlerFocus',
    'confidence',
    'sensitivity',
    'cuddliness',
    'playfulness',
    'retrieveDrive',
    'mouthiness',
    'chewingRisk',
    'noviceFriendliness',
    'shedding',
    'groomingNeed',
    'drool',
    'odor',
    'allergyFriendliness',
    'healthRobustness',
    'heatTolerance',
    'coldTolerance'
  ]);

  const CATEGORY_DEFAULTS = Object.freeze([
    'living',
    'activity',
    'training',
    'social',
    'safety',
    'maintenance',
    'health'
  ]);

  const DEFAULT_CONTEXT = Object.freeze({
    experience: 0.5,
    behaviorWorkTolerance: 0.5,
    homeNoise: 0.5,
    kidsExposure: 0.0,
    kidChaos: 0.0,
    daySupport: 0.5,
    aloneHours: 0.35,
    quickTripEase: 0.5,
    outdoorAccess: 0.5,
    containmentQuality: 0.75,
    spaceLevel: 0.5,
    sharedWalls: 0.0,
    exerciseBudget: 0.5,
    mentalBudget: 0.5,
    projectTolerance: 0.5,
    powerComfort: 0.5,
    liftCapacity: 0.5,
    desiredProtection: 0.35,
    desiredAlerting: 0.35,
    desiredStrangerFriendliness: 0.6,
    desiredGuestEase: 0.6,
    recallNeed: 0.45,
    escapeConcern: 0.45,
    preyTolerance: 0.5,
    dogSocialNeed: 0.5,
    smallPetSafetyNeed: 0.5,
    kidPriority: 0.0,
    kidPriorityStrictness: 0.5,
    noviceHousehold: 0.5,
    quietPriority: 0.4,
    tieBreak: null,
    climate: 'mild'
  });

  const ENUM_NORMALIZERS = Object.freeze({
    size: Object.freeze({
      toy: 0.08,
      tiny: 0.08,
      xs: 0.08,
      small: 0.2,
      compact: 0.25,
      medium: 0.5,
      midsize: 0.5,
      large: 0.8,
      big: 0.8,
      giant: 0.95,
      xl: 0.95
    })
  });

  const TRAIT_ALIASES = Object.freeze({
    size: ['size', 'sizeNormalized', 'sizeIndex', 'weightClass'],
    power: ['power', 'strength', 'pullingPower', 'physicality'],
    exerciseNeed: ['exerciseNeed', 'exercise', 'energy', 'energyLevel', 'activityNeed', 'activityLevel'],
    mentalNeed: ['mentalNeed', 'mentalStimulation', 'enrichmentNeed', 'brainNeed'],
    workingDrive: ['workingDrive', 'workDrive', 'drive', 'jobNeed'],
    indoorCalm: ['indoorCalm', 'settlesIndoors', 'calmness', 'offSwitch'],
    independence: ['independence', 'independent', 'selfReliance'],
    aloneTimeTolerance: ['aloneTimeTolerance', 'aloneTolerance', 'separationTolerance'],
    apartmentFriendly: ['apartmentFriendly', 'apartmentSuitability', 'cityFriendly'],
    barkiness: ['barkiness', 'barking', 'vocality', 'vocal'],
    alertness: ['alertness', 'watchdog', 'watchdogTendency', 'alerting'],
    protectiveness: ['protectiveness', 'guardian', 'guarding', 'protective'],
    strangerFriendliness: ['strangerFriendliness', 'strangerFriendly', 'friendliness', 'peopleFriendly'],
    guestEase: ['guestEase', 'guestFriendly', 'guestManners'],
    dogSociability: ['dogSociability', 'dogFriendly', 'dogFriendliness', 'socialWithDogs'],
    childFriendliness: ['childFriendliness', 'childFriendly', 'kidFriendly', 'familyFriendly'],
    toddlerTolerance: ['toddlerTolerance', 'youngChildFriendly', 'smallChildTolerance'],
    catCompatibility: ['catCompatibility', 'catFriendly', 'smallPetCompatible', 'smallPetCompatibility'],
    preyDrive: ['preyDrive', 'chaseDrive', 'chasing'],
    recallPotential: ['recallPotential', 'recall', 'offLeashReliability'],
    roamingRisk: ['roamingRisk', 'wanderlust', 'escaping', 'escapeRisk'],
    trainability: ['trainability', 'obedience', 'learnability', 'easyToTrain'],
    handlerFocus: ['handlerFocus', 'biddability', 'handlerOrientation', 'responsiveness'],
    confidence: ['confidence', 'stability', 'boldness', 'resilience'],
    sensitivity: ['sensitivity', 'softness', 'noiseSensitivity', 'sensitive'],
    cuddliness: ['cuddliness', 'affection', 'affectionLevel', 'clinginess'],
    playfulness: ['playfulness', 'playDrive', 'play'],
    retrieveDrive: ['retrieveDrive', 'retrieve', 'fetchDrive', 'retrieving'],
    mouthiness: ['mouthiness', 'mouthy', 'nippiness', 'mouthyTendency'],
    chewingRisk: ['chewingRisk', 'destructiveness', 'destructive', 'chewDrive'],
    noviceFriendliness: ['noviceFriendliness', 'beginnerFriendly', 'firstDogFriendly', 'ownerFriendliness'],
    shedding: ['shedding', 'shedLevel', 'coatShedding'],
    groomingNeed: ['groomingNeed', 'grooming', 'coatMaintenance'],
    drool: ['drool', 'drooling', 'slobber'],
    odor: ['odor', 'dogOdor', 'smell'],
    allergyFriendliness: ['allergyFriendliness', 'allergyFriendly', 'hypoallergenic'],
    healthRobustness: ['healthRobustness', 'hardiness', 'healthScore', 'overallHealth'],
    heatTolerance: ['heatTolerance', 'hotClimateFit', 'warmWeatherFit'],
    coldTolerance: ['coldTolerance', 'coldClimateFit', 'winterFit']
  });

  const CURRENT_OPTION_MODEL_DATA = {
  "Q01_A": [
    {
      "type": "context",
      "key": "homeType",
      "value": "apartment"
    },
    {
      "type": "context",
      "key": "sharedWalls",
      "value": 1
    },
    {
      "type": "context",
      "key": "spaceLevel",
      "value": 0.15
    },
    {
      "type": "min",
      "trait": "apartmentFriendly",
      "value": 0.78,
      "importance": 1.15,
      "label": "shared-wall suitability",
      "category": "living"
    },
    {
      "type": "min",
      "trait": "indoorCalm",
      "value": 0.62,
      "importance": 0.75,
      "label": "settles indoors",
      "category": "living"
    }
  ],
  "Q01_B": [
    {
      "type": "context",
      "key": "homeType",
      "value": "townhouse"
    },
    {
      "type": "context",
      "key": "sharedWalls",
      "value": 0.75
    },
    {
      "type": "context",
      "key": "spaceLevel",
      "value": 0.35
    },
    {
      "type": "min",
      "trait": "apartmentFriendly",
      "value": 0.65,
      "importance": 0.95,
      "label": "close-neighbor suitability",
      "category": "living"
    },
    {
      "type": "min",
      "trait": "indoorCalm",
      "value": 0.55,
      "importance": 0.55,
      "label": "manageable indoor energy",
      "category": "living"
    }
  ],
  "Q01_C": [
    {
      "type": "context",
      "key": "homeType",
      "value": "house"
    },
    {
      "type": "context",
      "key": "sharedWalls",
      "value": 0.15
    },
    {
      "type": "context",
      "key": "spaceLevel",
      "value": 0.7
    },
    {
      "type": "context",
      "key": "yardPotential",
      "value": 0.75
    }
  ],
  "Q01_D": [
    {
      "type": "context",
      "key": "homeType",
      "value": "rural"
    },
    {
      "type": "context",
      "key": "sharedWalls",
      "value": 0.0
    },
    {
      "type": "context",
      "key": "spaceLevel",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "yardPotential",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "freeRoamPotential",
      "value": 0.85
    }
  ],
  "Q02_A": [
    {
      "type": "max",
      "trait": "barkiness",
      "value": 0.15,
      "importance": 1.45,
      "label": "very low barking tolerance",
      "category": "living",
      "hard": true,
      "critical": true
    },
    {
      "type": "max",
      "trait": "alertness",
      "value": 0.28,
      "importance": 0.8,
      "label": "low alert barking",
      "category": "living"
    },
    {
      "type": "context",
      "key": "quietPriority",
      "value": 1.0
    }
  ],
  "Q02_B": [
    {
      "type": "max",
      "trait": "barkiness",
      "value": 0.35,
      "importance": 1.2,
      "label": "occasional barking at most",
      "category": "living",
      "hard": true
    },
    {
      "type": "context",
      "key": "quietPriority",
      "value": 0.75
    }
  ],
  "Q02_C": [
    {
      "type": "target",
      "trait": "barkiness",
      "value": 0.5,
      "importance": 0.45,
      "label": "some barking is acceptable",
      "category": "living"
    },
    {
      "type": "context",
      "key": "quietPriority",
      "value": 0.35
    }
  ],
  "Q02_D": [
    {
      "type": "context",
      "key": "barkTolerance",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "quietPriority",
      "value": 0.05
    }
  ],
  "Q03_A": [
    {
      "type": "context",
      "key": "quickTripEase",
      "value": 1.0
    }
  ],
  "Q03_B": [
    {
      "type": "context",
      "key": "quickTripEase",
      "value": 0.65
    },
    {
      "type": "min",
      "trait": "indoorCalm",
      "value": 0.55,
      "importance": 0.35,
      "label": "moderate indoor settling",
      "category": "living"
    }
  ],
  "Q03_C": [
    {
      "type": "context",
      "key": "quickTripEase",
      "value": 0.3
    },
    {
      "type": "min",
      "trait": "indoorCalm",
      "value": 0.72,
      "importance": 0.75,
      "label": "good indoor settling",
      "category": "living"
    },
    {
      "type": "max",
      "trait": "exerciseNeed",
      "value": 0.6,
      "importance": 0.55,
      "label": "manageable without constant quick trips",
      "category": "living"
    }
  ],
  "Q03_D": [
    {
      "type": "context",
      "key": "quickTripEase",
      "value": 0.1
    },
    {
      "type": "min",
      "trait": "indoorCalm",
      "value": 0.84,
      "importance": 1.2,
      "label": "excellent indoor settling",
      "category": "living",
      "hard": true
    },
    {
      "type": "max",
      "trait": "exerciseNeed",
      "value": 0.42,
      "importance": 0.85,
      "label": "lower daily exercise need",
      "category": "activity"
    },
    {
      "type": "min",
      "trait": "apartmentFriendly",
      "value": 0.82,
      "importance": 0.9,
      "label": "content with harder outdoor access",
      "category": "living"
    }
  ],
  "Q04_A": [
    {
      "type": "min",
      "trait": "indoorCalm",
      "value": 0.86,
      "importance": 1.3,
      "label": "very calm indoors",
      "category": "living"
    },
    {
      "type": "target",
      "trait": "playfulness",
      "value": 0.22,
      "importance": 0.55,
      "label": "lower indoor play intensity",
      "category": "activity"
    }
  ],
  "Q04_B": [
    {
      "type": "target",
      "trait": "indoorCalm",
      "value": 0.72,
      "importance": 1.05,
      "label": "balanced indoor energy",
      "category": "living"
    },
    {
      "type": "target",
      "trait": "playfulness",
      "value": 0.48,
      "importance": 0.45,
      "label": "play bursts, then settle",
      "category": "activity"
    }
  ],
  "Q04_C": [
    {
      "type": "target",
      "trait": "indoorCalm",
      "value": 0.52,
      "importance": 0.8,
      "label": "some indoor activity is okay",
      "category": "living"
    },
    {
      "type": "target",
      "trait": "playfulness",
      "value": 0.68,
      "importance": 0.65,
      "label": "playful home energy",
      "category": "activity"
    }
  ],
  "Q04_D": [
    {
      "type": "target",
      "trait": "indoorCalm",
      "value": 0.28,
      "importance": 0.65,
      "label": "busy indoor energy is acceptable",
      "category": "living"
    },
    {
      "type": "min",
      "trait": "playfulness",
      "value": 0.78,
      "importance": 0.9,
      "label": "very playful temperament",
      "category": "activity"
    }
  ],
  "Q05_A": [
    {
      "type": "max",
      "trait": "barkiness",
      "value": 0.2,
      "importance": 1.3,
      "label": "low tolerance for passerby barking",
      "category": "living",
      "hard": true
    },
    {
      "type": "max",
      "trait": "alertness",
      "value": 0.25,
      "importance": 0.8,
      "label": "low alerting behavior",
      "category": "social"
    },
    {
      "type": "context",
      "key": "quietPriority",
      "value": 1.0
    }
  ],
  "Q05_B": [
    {
      "type": "max",
      "trait": "barkiness",
      "value": 0.38,
      "importance": 1.0,
      "label": "brief barking only",
      "category": "living"
    },
    {
      "type": "context",
      "key": "quietPriority",
      "value": 0.7
    }
  ],
  "Q05_C": [
    {
      "type": "target",
      "trait": "alertness",
      "value": 0.62,
      "importance": 0.95,
      "label": "watchdog-style alerting",
      "category": "social"
    },
    {
      "type": "target",
      "trait": "barkiness",
      "value": 0.48,
      "importance": 0.45,
      "label": "some barking is useful",
      "category": "living"
    },
    {
      "type": "archetype",
      "key": "watchdog",
      "value": 0.45
    },
    {
      "type": "context",
      "key": "quietPriority",
      "value": 0.35
    },
    {
      "type": "context",
      "key": "desiredAlerting",
      "value": 0.65
    }
  ],
  "Q05_D": [
    {
      "type": "context",
      "key": "barkTolerance",
      "value": 0.85
    },
    {
      "type": "context",
      "key": "quietPriority",
      "value": 0.1
    }
  ],
  "Q06_A": [
    {
      "type": "context",
      "key": "outdoorAccess",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "privateYard",
      "value": 1.0
    }
  ],
  "Q06_B": [
    {
      "type": "context",
      "key": "outdoorAccess",
      "value": 0.7
    },
    {
      "type": "context",
      "key": "privateYard",
      "value": 0.55
    },
    {
      "type": "context",
      "key": "containmentQuality",
      "value": 0.45
    }
  ],
  "Q06_C": [
    {
      "type": "context",
      "key": "outdoorAccess",
      "value": 0.4
    },
    {
      "type": "context",
      "key": "privateYard",
      "value": 0.0
    },
    {
      "type": "min",
      "trait": "apartmentFriendly",
      "value": 0.6,
      "importance": 0.55,
      "label": "works without a private yard",
      "category": "living"
    }
  ],
  "Q06_D": [
    {
      "type": "context",
      "key": "outdoorAccess",
      "value": 0.15
    },
    {
      "type": "context",
      "key": "privateYard",
      "value": 0.0
    },
    {
      "type": "min",
      "trait": "indoorCalm",
      "value": 0.82,
      "importance": 1.0,
      "label": "content with limited outdoor access",
      "category": "living",
      "hard": true
    },
    {
      "type": "max",
      "trait": "exerciseNeed",
      "value": 0.45,
      "importance": 0.85,
      "label": "lower outdoor exercise demand",
      "category": "activity"
    },
    {
      "type": "min",
      "trait": "apartmentFriendly",
      "value": 0.82,
      "importance": 0.75,
      "label": "adapts to limited outdoor access",
      "category": "living"
    }
  ],
  "Q07_A": [
    {
      "type": "context",
      "key": "containmentQuality",
      "value": 1.0
    }
  ],
  "Q07_B": [
    {
      "type": "context",
      "key": "containmentQuality",
      "value": 0.6
    }
  ],
  "Q07_C": [
    {
      "type": "context",
      "key": "containmentQuality",
      "value": 0.8
    },
    {
      "type": "context",
      "key": "leashLifestyle",
      "value": 0.85
    }
  ],
  "Q07_D": [
    {
      "type": "context",
      "key": "containmentQuality",
      "value": 0.2
    },
    {
      "type": "context",
      "key": "freeRoamPotential",
      "value": 1.0
    }
  ],
  "Q08_A": [
    {
      "type": "max",
      "trait": "preyDrive",
      "value": 0.18,
      "importance": 1.5,
      "label": "very low chase risk around animals",
      "category": "safety",
      "hard": true,
      "critical": true
    },
    {
      "type": "min",
      "trait": "catCompatibility",
      "value": 0.76,
      "importance": 0.9,
      "label": "safe around other animals",
      "category": "safety",
      "hard": true
    }
  ],
  "Q08_B": [
    {
      "type": "max",
      "trait": "preyDrive",
      "value": 0.35,
      "importance": 1.15,
      "label": "prefer lower chase drive",
      "category": "safety",
      "hard": true
    }
  ],
  "Q08_C": [
    {
      "type": "max",
      "trait": "preyDrive",
      "value": 0.62,
      "importance": 0.55,
      "label": "manageable chase instinct",
      "category": "safety"
    }
  ],
  "Q08_D": [
    {
      "type": "context",
      "key": "preyTolerance",
      "value": 0.9
    }
  ],
  "Q09_A": [
    {
      "type": "archetype",
      "key": "companion",
      "value": 0.9
    },
    {
      "type": "archetype",
      "key": "family",
      "value": 0.35
    },
    {
      "type": "min",
      "trait": "noviceFriendliness",
      "value": 0.55,
      "importance": 0.6,
      "label": "easygoing temperament",
      "category": "training"
    },
    {
      "type": "target",
      "trait": "cuddliness",
      "value": 0.7,
      "importance": 0.55,
      "label": "companion energy",
      "category": "social"
    }
  ],
  "Q09_B": [
    {
      "type": "archetype",
      "key": "watchdog",
      "value": 0.9
    },
    {
      "type": "target",
      "trait": "alertness",
      "value": 0.72,
      "importance": 0.9,
      "label": "watchdog alerting",
      "category": "social"
    }
  ],
  "Q09_C": [
    {
      "type": "archetype",
      "key": "guardian",
      "value": 0.9
    },
    {
      "type": "target",
      "trait": "protectiveness",
      "value": 0.78,
      "importance": 1.0,
      "label": "protective presence",
      "category": "social"
    }
  ],
  "Q09_D": [
    {
      "type": "archetype",
      "key": "adventure",
      "value": 0.9
    },
    {
      "type": "archetype",
      "key": "sport",
      "value": 0.55
    },
    {
      "type": "min",
      "trait": "exerciseNeed",
      "value": 0.72,
      "importance": 0.85,
      "label": "adventure stamina",
      "category": "activity"
    },
    {
      "type": "min",
      "trait": "workingDrive",
      "value": 0.62,
      "importance": 0.7,
      "label": "enjoys active work",
      "category": "training"
    }
  ],
  "Q10_A": [
    {
      "type": "context",
      "key": "climate",
      "value": "hot"
    },
    {
      "type": "min",
      "trait": "heatTolerance",
      "value": 0.72,
      "importance": 1.2,
      "label": "handles heat well",
      "category": "living",
      "hard": true
    }
  ],
  "Q10_B": [
    {
      "type": "context",
      "key": "climate",
      "value": "cold"
    },
    {
      "type": "min",
      "trait": "coldTolerance",
      "value": 0.72,
      "importance": 1.2,
      "label": "handles cold well",
      "category": "living",
      "hard": true
    }
  ],
  "Q10_C": [
    {
      "type": "context",
      "key": "climate",
      "value": "mixed"
    },
    {
      "type": "min",
      "trait": "heatTolerance",
      "value": 0.48,
      "importance": 0.8,
      "label": "handles warmer weather",
      "category": "living"
    },
    {
      "type": "min",
      "trait": "coldTolerance",
      "value": 0.48,
      "importance": 0.8,
      "label": "handles colder weather",
      "category": "living"
    }
  ],
  "Q10_D": [
    {
      "type": "context",
      "key": "climate",
      "value": "mild"
    }
  ],
  "Q11_A": [
    {
      "type": "range",
      "trait": "size",
      "min": 0.0,
      "max": 0.28,
      "importance": 1.35,
      "label": "small size range",
      "category": "living",
      "hard": true
    },
    {
      "type": "max",
      "trait": "power",
      "value": 0.42,
      "importance": 0.85,
      "label": "easy to physically manage",
      "category": "safety"
    }
  ],
  "Q11_B": [
    {
      "type": "range",
      "trait": "size",
      "min": 0.32,
      "max": 0.65,
      "importance": 1.1,
      "label": "medium size range",
      "category": "living"
    }
  ],
  "Q11_C": [
    {
      "type": "min",
      "trait": "size",
      "value": 0.68,
      "importance": 1.0,
      "label": "large size preference",
      "category": "living"
    }
  ],
  "Q11_D": [
    {
      "type": "context",
      "key": "sizeFlexible",
      "value": 1.0
    }
  ],
  "Q12_A": [
    {
      "type": "max",
      "trait": "power",
      "value": 0.35,
      "importance": 1.35,
      "label": "easy leash handling",
      "category": "safety",
      "hard": true
    },
    {
      "type": "min",
      "trait": "trainability",
      "value": 0.58,
      "importance": 0.55,
      "label": "improves easily on leash",
      "category": "training"
    },
    {
      "type": "context",
      "key": "powerComfort",
      "value": 0.2
    }
  ],
  "Q12_B": [
    {
      "type": "max",
      "trait": "power",
      "value": 0.55,
      "importance": 0.95,
      "label": "moderate leash handling",
      "category": "safety"
    },
    {
      "type": "min",
      "trait": "trainability",
      "value": 0.5,
      "importance": 0.4,
      "label": "trainable leash manners",
      "category": "training"
    },
    {
      "type": "context",
      "key": "powerComfort",
      "value": 0.45
    }
  ],
  "Q12_C": [
    {
      "type": "max",
      "trait": "power",
      "value": 0.82,
      "importance": 0.35,
      "label": "can manage a stronger dog",
      "category": "safety"
    },
    {
      "type": "context",
      "key": "powerComfort",
      "value": 0.75
    }
  ],
  "Q12_D": [
    {
      "type": "context",
      "key": "handlingComfort",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "powerComfort",
      "value": 1.0
    }
  ],
  "Q13_A": [
    {
      "type": "max",
      "trait": "size",
      "value": 0.3,
      "importance": 1.15,
      "label": "carryable size in an emergency",
      "category": "safety",
      "hard": true
    },
    {
      "type": "context",
      "key": "liftCapacity",
      "value": 0.2
    }
  ],
  "Q13_B": [
    {
      "type": "max",
      "trait": "size",
      "value": 0.6,
      "importance": 0.95,
      "label": "liftable medium size",
      "category": "safety"
    },
    {
      "type": "context",
      "key": "liftCapacity",
      "value": 0.45
    }
  ],
  "Q13_C": [
    {
      "type": "max",
      "trait": "size",
      "value": 0.88,
      "importance": 0.5,
      "label": "can physically manage a large dog",
      "category": "safety"
    },
    {
      "type": "context",
      "key": "liftCapacity",
      "value": 0.75
    }
  ],
  "Q13_D": [
    {
      "type": "context",
      "key": "liftFlexibility",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "liftCapacity",
      "value": 1.0
    }
  ],
  "Q14_A": [
    {
      "type": "context",
      "key": "aloneHours",
      "value": 0.15
    }
  ],
  "Q14_B": [
    {
      "type": "context",
      "key": "aloneHours",
      "value": 0.38
    }
  ],
  "Q14_C": [
    {
      "type": "context",
      "key": "aloneHours",
      "value": 0.72
    },
    {
      "type": "min",
      "trait": "aloneTimeTolerance",
      "value": 0.58,
      "importance": 0.95,
      "label": "handles a longer weekday alone stretch",
      "category": "living",
      "hard": true
    }
  ],
  "Q14_D": [
    {
      "type": "context",
      "key": "aloneHours",
      "value": 1.0
    },
    {
      "type": "min",
      "trait": "aloneTimeTolerance",
      "value": 0.82,
      "importance": 1.45,
      "label": "handles very long alone stretches",
      "category": "living",
      "hard": true,
      "critical": true
    },
    {
      "type": "min",
      "trait": "independence",
      "value": 0.58,
      "importance": 0.75,
      "label": "comfortable being more independent",
      "category": "social"
    },
    {
      "type": "max",
      "trait": "barkiness",
      "value": 0.45,
      "importance": 0.55,
      "label": "less likely to struggle noisily when alone",
      "category": "living"
    }
  ],
  "Q15_A": [
    {
      "type": "context",
      "key": "daySupport",
      "value": 1.0
    }
  ],
  "Q15_B": [
    {
      "type": "context",
      "key": "daySupport",
      "value": 0.72
    }
  ],
  "Q15_C": [
    {
      "type": "context",
      "key": "daySupport",
      "value": 0.5
    },
    {
      "type": "context",
      "key": "externalSupport",
      "value": 0.6
    }
  ],
  "Q15_D": [
    {
      "type": "context",
      "key": "daySupport",
      "value": 0.0
    }
  ],
  "Q16_A": [
    {
      "type": "max",
      "trait": "exerciseNeed",
      "value": 0.25,
      "importance": 1.45,
      "label": "fits a very low weekday exercise budget",
      "category": "activity",
      "hard": true
    },
    {
      "type": "min",
      "trait": "indoorCalm",
      "value": 0.78,
      "importance": 0.7,
      "label": "settles with lighter exercise",
      "category": "living"
    },
    {
      "type": "context",
      "key": "exerciseBudget",
      "value": 0.2
    }
  ],
  "Q16_B": [
    {
      "type": "max",
      "trait": "exerciseNeed",
      "value": 0.55,
      "importance": 1.1,
      "label": "fits a moderate weekday exercise budget",
      "category": "activity",
      "hard": true
    },
    {
      "type": "context",
      "key": "exerciseBudget",
      "value": 0.45
    }
  ],
  "Q16_C": [
    {
      "type": "target",
      "trait": "exerciseNeed",
      "value": 0.72,
      "importance": 0.85,
      "label": "fits a strong weekday exercise routine",
      "category": "activity"
    },
    {
      "type": "context",
      "key": "exerciseBudget",
      "value": 0.75
    }
  ],
  "Q16_D": [
    {
      "type": "min",
      "trait": "exerciseNeed",
      "value": 0.72,
      "importance": 0.95,
      "label": "benefits from lots of daily exercise",
      "category": "activity"
    },
    {
      "type": "archetype",
      "key": "adventure",
      "value": 0.35
    },
    {
      "type": "context",
      "key": "exerciseBudget",
      "value": 1.0
    }
  ],
  "Q17_A": [
    {
      "type": "target",
      "trait": "exerciseNeed",
      "value": 0.32,
      "importance": 0.75,
      "label": "relaxed walk/sniff lifestyle",
      "category": "activity"
    },
    {
      "type": "target",
      "trait": "playfulness",
      "value": 0.38,
      "importance": 0.35,
      "label": "lower-key activity style",
      "category": "activity"
    }
  ],
  "Q17_B": [
    {
      "type": "target",
      "trait": "exerciseNeed",
      "value": 0.58,
      "importance": 0.7,
      "label": "brisk walk/jog partner",
      "category": "activity"
    },
    {
      "type": "archetype",
      "key": "adventure",
      "value": 0.35
    }
  ],
  "Q17_C": [
    {
      "type": "min",
      "trait": "exerciseNeed",
      "value": 0.75,
      "importance": 0.95,
      "label": "hiking and adventure stamina",
      "category": "activity"
    },
    {
      "type": "min",
      "trait": "confidence",
      "value": 0.58,
      "importance": 0.45,
      "label": "confident in new environments",
      "category": "social"
    },
    {
      "type": "archetype",
      "key": "adventure",
      "value": 0.7
    }
  ],
  "Q17_D": [
    {
      "type": "min",
      "trait": "workingDrive",
      "value": 0.72,
      "importance": 1.0,
      "label": "enjoys games and structured training",
      "category": "training"
    },
    {
      "type": "min",
      "trait": "trainability",
      "value": 0.7,
      "importance": 0.8,
      "label": "likes to learn and work with you",
      "category": "training"
    },
    {
      "type": "min",
      "trait": "playfulness",
      "value": 0.68,
      "importance": 0.55,
      "label": "wants interactive play",
      "category": "activity"
    },
    {
      "type": "archetype",
      "key": "sport",
      "value": 0.9
    }
  ],
  "Q18_A": [
    {
      "type": "max",
      "trait": "mentalNeed",
      "value": 0.32,
      "importance": 1.2,
      "label": "lower mental-enrichment demand",
      "category": "training",
      "hard": true
    },
    {
      "type": "max",
      "trait": "workingDrive",
      "value": 0.35,
      "importance": 0.9,
      "label": "not dependent on complex jobs",
      "category": "training"
    },
    {
      "type": "context",
      "key": "mentalBudget",
      "value": 0.2
    }
  ],
  "Q18_B": [
    {
      "type": "max",
      "trait": "mentalNeed",
      "value": 0.55,
      "importance": 0.95,
      "label": "fits short daily enrichment",
      "category": "training"
    },
    {
      "type": "context",
      "key": "mentalBudget",
      "value": 0.45
    }
  ],
  "Q18_C": [
    {
      "type": "target",
      "trait": "mentalNeed",
      "value": 0.72,
      "importance": 0.78,
      "label": "benefits from regular enrichment",
      "category": "training"
    },
    {
      "type": "context",
      "key": "mentalBudget",
      "value": 0.75
    }
  ],
  "Q18_D": [
    {
      "type": "min",
      "trait": "mentalNeed",
      "value": 0.82,
      "importance": 1.05,
      "label": "thrives with advanced training",
      "category": "training"
    },
    {
      "type": "min",
      "trait": "workingDrive",
      "value": 0.82,
      "importance": 1.1,
      "label": "enjoys sports/advanced work",
      "category": "training"
    },
    {
      "type": "min",
      "trait": "trainability",
      "value": 0.78,
      "importance": 0.85,
      "label": "high training ceiling",
      "category": "training"
    },
    {
      "type": "archetype",
      "key": "sport",
      "value": 0.75
    },
    {
      "type": "context",
      "key": "mentalBudget",
      "value": 1.0
    }
  ],
  "Q19_A": [
    {
      "type": "context",
      "key": "experience",
      "value": 0.1
    },
    {
      "type": "min",
      "trait": "noviceFriendliness",
      "value": 0.82,
      "importance": 1.45,
      "label": "first-dog friendly temperament",
      "category": "training",
      "hard": true,
      "critical": true
    },
    {
      "type": "context",
      "key": "noviceHousehold",
      "value": 1.0
    }
  ],
  "Q19_B": [
    {
      "type": "context",
      "key": "experience",
      "value": 0.42
    },
    {
      "type": "min",
      "trait": "noviceFriendliness",
      "value": 0.6,
      "importance": 1.0,
      "label": "reasonably approachable for a less experienced owner",
      "category": "training",
      "hard": true
    },
    {
      "type": "context",
      "key": "noviceHousehold",
      "value": 0.65
    }
  ],
  "Q19_C": [
    {
      "type": "context",
      "key": "experience",
      "value": 0.78
    },
    {
      "type": "context",
      "key": "noviceHousehold",
      "value": 0.2
    }
  ],
  "Q19_D": [
    {
      "type": "context",
      "key": "experience",
      "value": 1.0
    },
    {
      "type": "archetype",
      "key": "sport",
      "value": 0.15
    },
    {
      "type": "archetype",
      "key": "guardian",
      "value": 0.15
    },
    {
      "type": "context",
      "key": "noviceHousehold",
      "value": 0.0
    }
  ],
  "Q20_A": [
    {
      "type": "context",
      "key": "behaviorWorkTolerance",
      "value": 0.15
    },
    {
      "type": "min",
      "trait": "noviceFriendliness",
      "value": 0.76,
      "importance": 1.15,
      "label": "low-drama temperament",
      "category": "training",
      "hard": true
    },
    {
      "type": "max",
      "trait": "chewingRisk",
      "value": 0.25,
      "importance": 1.0,
      "label": "low destructive phase risk",
      "category": "training",
      "hard": true
    },
    {
      "type": "max",
      "trait": "mouthiness",
      "value": 0.25,
      "importance": 0.95,
      "label": "low mouthiness",
      "category": "training"
    },
    {
      "type": "context",
      "key": "projectTolerance",
      "value": 0.1
    }
  ],
  "Q20_B": [
    {
      "type": "context",
      "key": "behaviorWorkTolerance",
      "value": 0.45
    },
    {
      "type": "min",
      "trait": "noviceFriendliness",
      "value": 0.58,
      "importance": 0.75,
      "label": "manageable year-one behavior work",
      "category": "training"
    },
    {
      "type": "max",
      "trait": "chewingRisk",
      "value": 0.52,
      "importance": 0.55,
      "label": "moderate destructiveness",
      "category": "training"
    },
    {
      "type": "context",
      "key": "projectTolerance",
      "value": 0.4
    }
  ],
  "Q20_C": [
    {
      "type": "context",
      "key": "behaviorWorkTolerance",
      "value": 0.78
    },
    {
      "type": "context",
      "key": "projectTolerance",
      "value": 0.75
    }
  ],
  "Q20_D": [
    {
      "type": "context",
      "key": "behaviorWorkTolerance",
      "value": 1.0
    },
    {
      "type": "min",
      "trait": "workingDrive",
      "value": 0.72,
      "importance": 0.55,
      "label": "high-drive project okay",
      "category": "training"
    },
    {
      "type": "archetype",
      "key": "sport",
      "value": 0.3
    },
    {
      "type": "context",
      "key": "projectTolerance",
      "value": 1.0
    }
  ],
  "Q21_A": [
    {
      "type": "min",
      "trait": "trainability",
      "value": 0.78,
      "importance": 1.35,
      "label": "easy basics and everyday manners",
      "category": "training",
      "hard": true
    },
    {
      "type": "min",
      "trait": "handlerFocus",
      "value": 0.62,
      "importance": 0.65,
      "label": "responsive to the handler",
      "category": "training"
    }
  ],
  "Q21_B": [
    {
      "type": "min",
      "trait": "trainability",
      "value": 0.58,
      "importance": 0.95,
      "label": "solid training with effort",
      "category": "training"
    }
  ],
  "Q21_C": [
    {
      "type": "context",
      "key": "easyTrainNeed",
      "value": 0.2
    }
  ],
  "Q21_D": [
    {
      "type": "min",
      "trait": "workingDrive",
      "value": 0.78,
      "importance": 0.95,
      "label": "advanced training potential",
      "category": "training"
    },
    {
      "type": "min",
      "trait": "trainability",
      "value": 0.72,
      "importance": 0.75,
      "label": "good learning ceiling",
      "category": "training"
    },
    {
      "type": "archetype",
      "key": "sport",
      "value": 0.55
    }
  ],
  "Q22_A": [
    {
      "type": "min",
      "trait": "handlerFocus",
      "value": 0.76,
      "importance": 1.1,
      "label": "quick cue response",
      "category": "training",
      "hard": true
    },
    {
      "type": "min",
      "trait": "recallPotential",
      "value": 0.7,
      "importance": 0.7,
      "label": "strong responsiveness",
      "category": "training"
    },
    {
      "type": "max",
      "trait": "independence",
      "value": 0.45,
      "importance": 0.6,
      "label": "less independent style",
      "category": "social"
    }
  ],
  "Q22_B": [
    {
      "type": "min",
      "trait": "handlerFocus",
      "value": 0.6,
      "importance": 0.85,
      "label": "generally responsive",
      "category": "training"
    }
  ],
  "Q22_C": [
    {
      "type": "target",
      "trait": "independence",
      "value": 0.65,
      "importance": 0.65,
      "label": "independent streak is okay",
      "category": "social"
    }
  ],
  "Q22_D": [
    {
      "type": "min",
      "trait": "independence",
      "value": 0.76,
      "importance": 0.95,
      "label": "independent thinker",
      "category": "social"
    },
    {
      "type": "max",
      "trait": "handlerFocus",
      "value": 0.45,
      "importance": 0.55,
      "label": "not overly handler-focused",
      "category": "training"
    }
  ],
  "Q23_A": [
    {
      "type": "min",
      "trait": "indoorCalm",
      "value": 0.86,
      "importance": 1.15,
      "label": "evening lounging and calm",
      "category": "living"
    },
    {
      "type": "min",
      "trait": "cuddliness",
      "value": 0.64,
      "importance": 0.75,
      "label": "couch-companion affection",
      "category": "social"
    },
    {
      "type": "max",
      "trait": "playfulness",
      "value": 0.42,
      "importance": 0.45,
      "label": "lower evening activity",
      "category": "activity"
    },
    {
      "type": "archetype",
      "key": "companion",
      "value": 0.35
    }
  ],
  "Q23_B": [
    {
      "type": "target",
      "trait": "indoorCalm",
      "value": 0.72,
      "importance": 0.95,
      "label": "calm evenings with short play bursts",
      "category": "living"
    },
    {
      "type": "target",
      "trait": "playfulness",
      "value": 0.52,
      "importance": 0.4,
      "label": "balanced evening energy",
      "category": "activity"
    }
  ],
  "Q23_C": [
    {
      "type": "target",
      "trait": "playfulness",
      "value": 0.72,
      "importance": 0.8,
      "label": "playful evenings",
      "category": "activity"
    }
  ],
  "Q23_D": [
    {
      "type": "min",
      "trait": "playfulness",
      "value": 0.86,
      "importance": 1.0,
      "label": "very active evening play",
      "category": "activity"
    },
    {
      "type": "max",
      "trait": "indoorCalm",
      "value": 0.45,
      "importance": 0.3,
      "label": "less evening downtime is fine",
      "category": "living"
    }
  ],
  "Q24_A": [
    {
      "type": "max",
      "trait": "barkiness",
      "value": 0.15,
      "importance": 1.25,
      "label": "minimal doorbell barking",
      "category": "living",
      "hard": true
    },
    {
      "type": "context",
      "key": "quietPriority",
      "value": 1.0
    }
  ],
  "Q24_B": [
    {
      "type": "max",
      "trait": "barkiness",
      "value": 0.35,
      "importance": 0.95,
      "label": "brief doorbell barking only",
      "category": "living"
    },
    {
      "type": "context",
      "key": "quietPriority",
      "value": 0.7
    }
  ],
  "Q24_C": [
    {
      "type": "target",
      "trait": "barkiness",
      "value": 0.48,
      "importance": 0.35,
      "label": "normal barking is acceptable",
      "category": "living"
    },
    {
      "type": "context",
      "key": "quietPriority",
      "value": 0.35
    }
  ],
  "Q24_D": [
    {
      "type": "min",
      "trait": "alertness",
      "value": 0.68,
      "importance": 0.95,
      "label": "likes to announce visitors",
      "category": "social"
    },
    {
      "type": "min",
      "trait": "barkiness",
      "value": 0.45,
      "importance": 0.5,
      "label": "alert barking is welcome",
      "category": "living"
    },
    {
      "type": "archetype",
      "key": "watchdog",
      "value": 0.35
    },
    {
      "type": "context",
      "key": "desiredAlerting",
      "value": 0.75
    },
    {
      "type": "context",
      "key": "quietPriority",
      "value": 0.2
    }
  ],
  "Q25_A": [
    {
      "type": "max",
      "trait": "chewingRisk",
      "value": 0.15,
      "importance": 1.2,
      "label": "very low chewing/destructive risk",
      "category": "training",
      "hard": true
    }
  ],
  "Q25_B": [
    {
      "type": "max",
      "trait": "chewingRisk",
      "value": 0.35,
      "importance": 0.9,
      "label": "limited destructiveness",
      "category": "training"
    }
  ],
  "Q25_C": [
    {
      "type": "max",
      "trait": "chewingRisk",
      "value": 0.58,
      "importance": 0.55,
      "label": "moderate destructiveness okay",
      "category": "training"
    }
  ],
  "Q25_D": [
    {
      "type": "context",
      "key": "chewTolerance",
      "value": 1.0
    }
  ],
  "Q26_A": [
    {
      "type": "context",
      "key": "homeNoise",
      "value": 0.15
    },
    {
      "type": "context",
      "key": "sensitiveDogOkay",
      "value": 1.0
    }
  ],
  "Q26_B": [
    {
      "type": "context",
      "key": "homeNoise",
      "value": 0.5
    }
  ],
  "Q26_C": [
    {
      "type": "context",
      "key": "homeNoise",
      "value": 0.9
    },
    {
      "type": "min",
      "trait": "confidence",
      "value": 0.72,
      "importance": 1.05,
      "label": "confident in a busy/loud household",
      "category": "social",
      "hard": true
    },
    {
      "type": "max",
      "trait": "sensitivity",
      "value": 0.38,
      "importance": 0.9,
      "label": "not overly noise-sensitive",
      "category": "social",
      "hard": true
    }
  ],
  "Q26_D": [
    {
      "type": "context",
      "key": "homeNoise",
      "value": 0.5
    },
    {
      "type": "context",
      "key": "adaptableTemperamentOkay",
      "value": 1.0
    }
  ],
  "Q27_A": [
    {
      "type": "min",
      "trait": "cuddliness",
      "value": 0.82,
      "importance": 1.0,
      "label": "very cuddly relationship",
      "category": "social"
    }
  ],
  "Q27_B": [
    {
      "type": "target",
      "trait": "cuddliness",
      "value": 0.64,
      "importance": 0.85,
      "label": "affectionate without being clingy",
      "category": "social"
    }
  ],
  "Q27_C": [
    {
      "type": "target",
      "trait": "cuddliness",
      "value": 0.28,
      "importance": 0.8,
      "label": "more independent companionship",
      "category": "social"
    },
    {
      "type": "min",
      "trait": "independence",
      "value": 0.6,
      "importance": 0.45,
      "label": "comfortable with some personal space",
      "category": "social"
    }
  ],
  "Q27_D": [
    {
      "type": "context",
      "key": "affectionFlexible",
      "value": 1.0
    }
  ],
  "Q28_A": [
    {
      "type": "target",
      "trait": "playfulness",
      "value": 0.25,
      "importance": 0.9,
      "label": "low-play chill style",
      "category": "activity"
    }
  ],
  "Q28_B": [
    {
      "type": "target",
      "trait": "playfulness",
      "value": 0.52,
      "importance": 0.8,
      "label": "moderate playfulness",
      "category": "activity"
    }
  ],
  "Q28_C": [
    {
      "type": "min",
      "trait": "retrieveDrive",
      "value": 0.72,
      "importance": 1.0,
      "label": "enjoys fetch/retrieve games",
      "category": "activity"
    },
    {
      "type": "target",
      "trait": "playfulness",
      "value": 0.68,
      "importance": 0.55,
      "label": "engaging play style",
      "category": "activity"
    }
  ],
  "Q28_D": [
    {
      "type": "min",
      "trait": "playfulness",
      "value": 0.86,
      "importance": 1.05,
      "label": "very playful/high-drive energy",
      "category": "activity"
    },
    {
      "type": "min",
      "trait": "workingDrive",
      "value": 0.65,
      "importance": 0.55,
      "label": "high-drive engagement",
      "category": "training"
    }
  ],
  "Q29_A": [
    {
      "type": "context",
      "key": "kidsExposure",
      "value": 0.0
    },
    {
      "type": "context",
      "key": "kidPriority",
      "value": 0.0
    }
  ],
  "Q29_B": [
    {
      "type": "context",
      "key": "kidsExposure",
      "value": 0.35
    },
    {
      "type": "min",
      "trait": "childFriendliness",
      "value": 0.55,
      "importance": 0.45,
      "label": "comfortable around visiting kids",
      "category": "social"
    },
    {
      "type": "context",
      "key": "kidPriority",
      "value": 0.4
    }
  ],
  "Q29_C": [
    {
      "type": "context",
      "key": "kidsExposure",
      "value": 0.75
    },
    {
      "type": "min",
      "trait": "childFriendliness",
      "value": 0.74,
      "importance": 1.0,
      "label": "strong fit for school-age kids",
      "category": "social",
      "hard": true
    },
    {
      "type": "max",
      "trait": "mouthiness",
      "value": 0.58,
      "importance": 0.45,
      "label": "manageable mouthiness around kids",
      "category": "safety"
    },
    {
      "type": "archetype",
      "key": "family",
      "value": 0.55
    },
    {
      "type": "context",
      "key": "kidPriority",
      "value": 0.75
    }
  ],
  "Q29_D": [
    {
      "type": "context",
      "key": "kidsExposure",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "toddlersPresent",
      "value": 1.0
    },
    {
      "type": "min",
      "trait": "childFriendliness",
      "value": 0.82,
      "importance": 1.1,
      "label": "strong fit for young kids",
      "category": "social",
      "hard": true,
      "critical": true
    },
    {
      "type": "min",
      "trait": "toddlerTolerance",
      "value": 0.82,
      "importance": 1.25,
      "label": "excellent toddler tolerance",
      "category": "social",
      "hard": true,
      "critical": true
    },
    {
      "type": "max",
      "trait": "mouthiness",
      "value": 0.32,
      "importance": 0.95,
      "label": "low mouthiness around toddlers",
      "category": "safety",
      "hard": true
    },
    {
      "type": "archetype",
      "key": "family",
      "value": 0.8
    },
    {
      "type": "context",
      "key": "kidPriority",
      "value": 1.0
    }
  ],
  "Q30_A": [
    {
      "type": "context",
      "key": "kidChaos",
      "value": 0.2
    }
  ],
  "Q30_B": [
    {
      "type": "context",
      "key": "kidChaos",
      "value": 0.5
    }
  ],
  "Q30_C": [
    {
      "type": "context",
      "key": "kidChaos",
      "value": 0.82
    },
    {
      "type": "min",
      "trait": "confidence",
      "value": 0.58,
      "importance": 0.75,
      "label": "handles a high-energy family home",
      "category": "social"
    },
    {
      "type": "max",
      "trait": "sensitivity",
      "value": 0.58,
      "importance": 0.6,
      "label": "not too easily overwhelmed",
      "category": "social"
    }
  ],
  "Q30_D": [
    {
      "type": "context",
      "key": "kidChaos",
      "value": 1.0
    },
    {
      "type": "min",
      "trait": "confidence",
      "value": 0.72,
      "importance": 0.95,
      "label": "stable in unpredictable toddler chaos",
      "category": "social",
      "hard": true
    },
    {
      "type": "max",
      "trait": "sensitivity",
      "value": 0.42,
      "importance": 0.8,
      "label": "resilient around noise and motion",
      "category": "social",
      "hard": true
    },
    {
      "type": "min",
      "trait": "toddlerTolerance",
      "value": 0.86,
      "importance": 1.0,
      "label": "very patient with toddlers",
      "category": "social",
      "hard": true
    }
  ],
  "Q31_A": [
    {
      "type": "max",
      "trait": "mouthiness",
      "value": 0.15,
      "importance": 1.25,
      "label": "very low mouthiness needed",
      "category": "safety",
      "hard": true,
      "critical": true
    }
  ],
  "Q31_B": [
    {
      "type": "max",
      "trait": "mouthiness",
      "value": 0.35,
      "importance": 1.0,
      "label": "mouthiness should improve quickly",
      "category": "safety",
      "hard": true
    }
  ],
  "Q31_C": [
    {
      "type": "max",
      "trait": "mouthiness",
      "value": 0.65,
      "importance": 0.55,
      "label": "normal puppy mouthiness is okay",
      "category": "safety"
    }
  ],
  "Q31_D": [
    {
      "type": "context",
      "key": "mouthinessTolerance",
      "value": 1.0
    }
  ],
  "Q32_A": [
    {
      "type": "min",
      "trait": "childFriendliness",
      "value": 0.86,
      "importance": 1.2,
      "label": "top-tier kid-friendly temperament",
      "category": "social",
      "hard": true,
      "critical": true
    },
    {
      "type": "min",
      "trait": "toddlerTolerance",
      "value": 0.78,
      "importance": 0.8,
      "label": "excellent patience with kids",
      "category": "social",
      "hard": true
    },
    {
      "type": "context",
      "key": "kidPriorityStrictness",
      "value": 1.0
    }
  ],
  "Q32_B": [
    {
      "type": "min",
      "trait": "childFriendliness",
      "value": 0.72,
      "importance": 0.9,
      "label": "strong kid-friendly temperament",
      "category": "social",
      "hard": true
    },
    {
      "type": "context",
      "key": "kidPriorityStrictness",
      "value": 0.75
    }
  ],
  "Q32_C": [
    {
      "type": "min",
      "trait": "childFriendliness",
      "value": 0.5,
      "importance": 0.5,
      "label": "moderate kid-friendliness",
      "category": "social"
    },
    {
      "type": "context",
      "key": "kidPriorityStrictness",
      "value": 0.45
    }
  ],
  "Q32_D": [
    {
      "type": "context",
      "key": "kidPriorityReduced",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "kidPriorityStrictness",
      "value": 0.2
    }
  ],
  "Q33_A": [
    {
      "type": "context",
      "key": "petHousehold",
      "value": "none"
    },
    {
      "type": "context",
      "key": "dogHousehold",
      "value": 0.0
    },
    {
      "type": "context",
      "key": "catHousehold",
      "value": 0.0
    }
  ],
  "Q33_B": [
    {
      "type": "context",
      "key": "petHousehold",
      "value": "dogs"
    },
    {
      "type": "min",
      "trait": "dogSociability",
      "value": 0.58,
      "importance": 0.75,
      "label": "lives well with other dogs",
      "category": "social",
      "hard": true
    },
    {
      "type": "context",
      "key": "dogHousehold",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "catHousehold",
      "value": 0.0
    }
  ],
  "Q33_C": [
    {
      "type": "context",
      "key": "petHousehold",
      "value": "cats"
    },
    {
      "type": "min",
      "trait": "catCompatibility",
      "value": 0.72,
      "importance": 0.9,
      "label": "lives safely with cats/small pets",
      "category": "safety",
      "hard": true
    },
    {
      "type": "context",
      "key": "dogHousehold",
      "value": 0.0
    },
    {
      "type": "context",
      "key": "catHousehold",
      "value": 1.0
    }
  ],
  "Q33_D": [
    {
      "type": "context",
      "key": "petHousehold",
      "value": "mixed"
    },
    {
      "type": "min",
      "trait": "dogSociability",
      "value": 0.58,
      "importance": 0.75,
      "label": "good with other dogs",
      "category": "social",
      "hard": true
    },
    {
      "type": "min",
      "trait": "catCompatibility",
      "value": 0.72,
      "importance": 0.9,
      "label": "safe with cats/small pets",
      "category": "safety",
      "hard": true
    },
    {
      "type": "context",
      "key": "dogHousehold",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "catHousehold",
      "value": 1.0
    }
  ],
  "Q34_A": [
    {
      "type": "min",
      "trait": "dogSociability",
      "value": 0.82,
      "importance": 1.15,
      "label": "highly dog-social lifestyle",
      "category": "social",
      "hard": true
    },
    {
      "type": "context",
      "key": "dogSocialNeed",
      "value": 1.0
    }
  ],
  "Q34_B": [
    {
      "type": "min",
      "trait": "dogSociability",
      "value": 0.66,
      "importance": 0.95,
      "label": "friendly with most dogs",
      "category": "social",
      "hard": true
    },
    {
      "type": "context",
      "key": "dogSocialNeed",
      "value": 0.75
    }
  ],
  "Q34_C": [
    {
      "type": "min",
      "trait": "dogSociability",
      "value": 0.42,
      "importance": 0.45,
      "label": "occasional dog interactions",
      "category": "social"
    },
    {
      "type": "context",
      "key": "dogSocialNeed",
      "value": 0.45
    }
  ],
  "Q34_D": [
    {
      "type": "target",
      "trait": "dogSociability",
      "value": 0.3,
      "importance": 0.45,
      "label": "lower need for dog socializing",
      "category": "social"
    },
    {
      "type": "context",
      "key": "dogSocialNeed",
      "value": 0.15
    }
  ],
  "Q35_A": [
    {
      "type": "min",
      "trait": "catCompatibility",
      "value": 0.86,
      "importance": 1.3,
      "label": "excellent cat/small-pet compatibility",
      "category": "safety",
      "hard": true,
      "critical": true
    },
    {
      "type": "max",
      "trait": "preyDrive",
      "value": 0.25,
      "importance": 1.1,
      "label": "very low chasing around small pets",
      "category": "safety",
      "hard": true,
      "critical": true
    },
    {
      "type": "context",
      "key": "smallPetSafetyNeed",
      "value": 1.0
    }
  ],
  "Q35_B": [
    {
      "type": "min",
      "trait": "catCompatibility",
      "value": 0.64,
      "importance": 0.9,
      "label": "cat compatibility with management",
      "category": "safety",
      "hard": true
    },
    {
      "type": "max",
      "trait": "preyDrive",
      "value": 0.55,
      "importance": 0.75,
      "label": "manageable prey drive around small pets",
      "category": "safety"
    },
    {
      "type": "context",
      "key": "smallPetSafetyNeed",
      "value": 0.75
    }
  ],
  "Q35_C": [
    {
      "type": "min",
      "trait": "catCompatibility",
      "value": 0.35,
      "importance": 0.45,
      "label": "some compatibility helps",
      "category": "safety"
    },
    {
      "type": "context",
      "key": "smallPetSafetyNeed",
      "value": 0.45
    }
  ],
  "Q35_D": [
    {
      "type": "context",
      "key": "catSeparateOkay",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "smallPetSafetyNeed",
      "value": 0.15
    }
  ],
  "Q36_A": [
    {
      "type": "min",
      "trait": "catCompatibility",
      "value": 0.86,
      "importance": 1.2,
      "label": "small-pet safety comes first",
      "category": "safety",
      "hard": true,
      "critical": true
    },
    {
      "type": "max",
      "trait": "preyDrive",
      "value": 0.25,
      "importance": 1.0,
      "label": "very low small-pet chase risk",
      "category": "safety",
      "hard": true,
      "critical": true
    },
    {
      "type": "context",
      "key": "smallPetSafetyNeed",
      "value": 1.0
    }
  ],
  "Q36_B": [
    {
      "type": "min",
      "trait": "dogSociability",
      "value": 0.78,
      "importance": 1.0,
      "label": "dog-to-dog harmony comes first",
      "category": "social",
      "hard": true
    },
    {
      "type": "context",
      "key": "dogSocialNeed",
      "value": 1.0
    }
  ],
  "Q36_C": [
    {
      "type": "min",
      "trait": "dogSociability",
      "value": 0.66,
      "importance": 0.85,
      "label": "balanced dog friendliness",
      "category": "social",
      "hard": true
    },
    {
      "type": "min",
      "trait": "catCompatibility",
      "value": 0.72,
      "importance": 0.9,
      "label": "balanced cat compatibility",
      "category": "safety",
      "hard": true
    },
    {
      "type": "context",
      "key": "smallPetSafetyNeed",
      "value": 0.8
    },
    {
      "type": "context",
      "key": "dogSocialNeed",
      "value": 0.8
    }
  ],
  "Q36_D": [
    {
      "type": "context",
      "key": "petManagementFlexible",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "petManagementFlexibility",
      "value": 1.0
    }
  ],
  "Q37_A": [
    {
      "type": "max",
      "trait": "preyDrive",
      "value": 0.2,
      "importance": 1.25,
      "label": "very low chase instinct on walks",
      "category": "safety",
      "hard": true
    },
    {
      "type": "context",
      "key": "preyTolerance",
      "value": 0.05
    }
  ],
  "Q37_B": [
    {
      "type": "max",
      "trait": "preyDrive",
      "value": 0.5,
      "importance": 0.9,
      "label": "manageable chase instinct",
      "category": "safety"
    },
    {
      "type": "context",
      "key": "preyTolerance",
      "value": 0.45
    }
  ],
  "Q37_C": [
    {
      "type": "min",
      "trait": "preyDrive",
      "value": 0.48,
      "importance": 0.35,
      "label": "strong chase instinct is acceptable",
      "category": "activity"
    },
    {
      "type": "context",
      "key": "preyTolerance",
      "value": 0.75
    }
  ],
  "Q37_D": [
    {
      "type": "min",
      "trait": "preyDrive",
      "value": 0.76,
      "importance": 0.95,
      "label": "high chase drive is a plus",
      "category": "activity"
    },
    {
      "type": "min",
      "trait": "workingDrive",
      "value": 0.62,
      "importance": 0.5,
      "label": "enjoys driven work",
      "category": "training"
    },
    {
      "type": "archetype",
      "key": "sport",
      "value": 0.35
    },
    {
      "type": "context",
      "key": "preyTolerance",
      "value": 1.0
    }
  ],
  "Q38_A": [
    {
      "type": "min",
      "trait": "recallPotential",
      "value": 0.8,
      "importance": 1.15,
      "label": "reliable recall matters",
      "category": "training",
      "hard": true
    },
    {
      "type": "max",
      "trait": "roamingRisk",
      "value": 0.35,
      "importance": 0.95,
      "label": "less likely to take off",
      "category": "safety",
      "hard": true
    },
    {
      "type": "context",
      "key": "recallNeed",
      "value": 1.0
    }
  ],
  "Q38_B": [
    {
      "type": "min",
      "trait": "recallPotential",
      "value": 0.56,
      "importance": 0.75,
      "label": "recall is a useful bonus",
      "category": "training"
    },
    {
      "type": "context",
      "key": "recallNeed",
      "value": 0.65
    }
  ],
  "Q38_C": [
    {
      "type": "context",
      "key": "offLeashNeed",
      "value": 0.15
    },
    {
      "type": "context",
      "key": "recallNeed",
      "value": 0.25
    }
  ],
  "Q38_D": [
    {
      "type": "context",
      "key": "offLeashNeed",
      "value": 0.0
    },
    {
      "type": "context",
      "key": "alwaysLeashed",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "recallNeed",
      "value": 0.0
    }
  ],
  "Q39_A": [
    {
      "type": "max",
      "trait": "roamingRisk",
      "value": 0.2,
      "importance": 1.3,
      "label": "must stay close if a gate opens",
      "category": "safety",
      "hard": true,
      "critical": true
    },
    {
      "type": "min",
      "trait": "recallPotential",
      "value": 0.7,
      "importance": 0.8,
      "label": "stays connected to the handler",
      "category": "training",
      "hard": true
    },
    {
      "type": "context",
      "key": "escapeConcern",
      "value": 1.0
    }
  ],
  "Q39_B": [
    {
      "type": "max",
      "trait": "roamingRisk",
      "value": 0.6,
      "importance": 0.55,
      "label": "some wander risk is okay",
      "category": "safety"
    },
    {
      "type": "context",
      "key": "escapeConcern",
      "value": 0.55
    }
  ],
  "Q39_C": [
    {
      "type": "max",
      "trait": "roamingRisk",
      "value": 0.78,
      "importance": 0.35,
      "label": "manageable with training/containment",
      "category": "safety"
    },
    {
      "type": "context",
      "key": "escapeConcern",
      "value": 0.4
    }
  ],
  "Q39_D": [
    {
      "type": "context",
      "key": "escapeConcern",
      "value": 0.1
    },
    {
      "type": "context",
      "key": "escapeConcern",
      "value": 0.1
    }
  ],
  "Q40_A": [
    {
      "type": "min",
      "trait": "strangerFriendliness",
      "value": 0.85,
      "importance": 1.0,
      "label": "very friendly with strangers",
      "category": "social",
      "hard": true
    },
    {
      "type": "context",
      "key": "desiredStrangerFriendliness",
      "value": 1.0
    }
  ],
  "Q40_B": [
    {
      "type": "target",
      "trait": "strangerFriendliness",
      "value": 0.66,
      "importance": 0.9,
      "label": "polite/friendly with strangers",
      "category": "social"
    },
    {
      "type": "context",
      "key": "desiredStrangerFriendliness",
      "value": 0.7
    }
  ],
  "Q40_C": [
    {
      "type": "target",
      "trait": "strangerFriendliness",
      "value": 0.42,
      "importance": 0.75,
      "label": "neutral/reserved with strangers",
      "category": "social"
    },
    {
      "type": "context",
      "key": "desiredStrangerFriendliness",
      "value": 0.45
    }
  ],
  "Q40_D": [
    {
      "type": "max",
      "trait": "strangerFriendliness",
      "value": 0.35,
      "importance": 0.85,
      "label": "aloof/wary with strangers is okay",
      "category": "social"
    },
    {
      "type": "archetype",
      "key": "watchdog",
      "value": 0.2
    },
    {
      "type": "context",
      "key": "desiredStrangerFriendliness",
      "value": 0.15
    }
  ],
  "Q41_A": [
    {
      "type": "max",
      "trait": "alertness",
      "value": 0.18,
      "importance": 1.0,
      "label": "quiet response at home",
      "category": "social",
      "hard": true
    },
    {
      "type": "max",
      "trait": "barkiness",
      "value": 0.18,
      "importance": 1.05,
      "label": "low reactivity to home triggers",
      "category": "living",
      "hard": true
    },
    {
      "type": "max",
      "trait": "protectiveness",
      "value": 0.42,
      "importance": 0.55,
      "label": "low guarding intensity",
      "category": "social"
    },
    {
      "type": "context",
      "key": "desiredAlerting",
      "value": 0.05
    },
    {
      "type": "context",
      "key": "desiredProtection",
      "value": 0.15
    }
  ],
  "Q41_B": [
    {
      "type": "target",
      "trait": "alertness",
      "value": 0.46,
      "importance": 0.9,
      "label": "alerts once then settles",
      "category": "social"
    },
    {
      "type": "max",
      "trait": "barkiness",
      "value": 0.46,
      "importance": 0.6,
      "label": "brief alerting only",
      "category": "living"
    },
    {
      "type": "context",
      "key": "desiredAlerting",
      "value": 0.45
    },
    {
      "type": "context",
      "key": "desiredProtection",
      "value": 0.25
    }
  ],
  "Q41_C": [
    {
      "type": "min",
      "trait": "alertness",
      "value": 0.68,
      "importance": 1.0,
      "label": "clear home alerting",
      "category": "social"
    },
    {
      "type": "min",
      "trait": "barkiness",
      "value": 0.45,
      "importance": 0.45,
      "label": "willing to vocalize when needed",
      "category": "living"
    },
    {
      "type": "archetype",
      "key": "watchdog",
      "value": 0.7
    },
    {
      "type": "context",
      "key": "desiredAlerting",
      "value": 0.75
    },
    {
      "type": "context",
      "key": "desiredProtection",
      "value": 0.45
    }
  ],
  "Q41_D": [
    {
      "type": "min",
      "trait": "alertness",
      "value": 0.84,
      "importance": 1.05,
      "label": "very vigilant at home",
      "category": "social"
    },
    {
      "type": "min",
      "trait": "protectiveness",
      "value": 0.72,
      "importance": 1.0,
      "label": "serious watchful temperament",
      "category": "social"
    },
    {
      "type": "archetype",
      "key": "guardian",
      "value": 0.6
    },
    {
      "type": "context",
      "key": "desiredAlerting",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "desiredProtection",
      "value": 0.8
    }
  ],
  "Q42_A": [
    {
      "type": "target",
      "trait": "protectiveness",
      "value": 0.45,
      "importance": 0.75,
      "label": "light deterrent presence",
      "category": "social"
    },
    {
      "type": "context",
      "key": "desiredProtection",
      "value": 0.45
    }
  ],
  "Q42_B": [
    {
      "type": "target",
      "trait": "protectiveness",
      "value": 0.65,
      "importance": 0.95,
      "label": "moderate guardian feel",
      "category": "social"
    },
    {
      "type": "min",
      "trait": "confidence",
      "value": 0.58,
      "importance": 0.4,
      "label": "confident enough to handle itself",
      "category": "social"
    },
    {
      "type": "context",
      "key": "desiredProtection",
      "value": 0.65
    }
  ],
  "Q42_C": [
    {
      "type": "min",
      "trait": "protectiveness",
      "value": 0.82,
      "importance": 1.15,
      "label": "high protectiveness",
      "category": "social"
    },
    {
      "type": "min",
      "trait": "confidence",
      "value": 0.72,
      "importance": 0.6,
      "label": "serious confidence",
      "category": "social"
    },
    {
      "type": "archetype",
      "key": "guardian",
      "value": 0.75
    },
    {
      "type": "context",
      "key": "desiredProtection",
      "value": 0.85
    }
  ],
  "Q42_D": [
    {
      "type": "min",
      "trait": "protectiveness",
      "value": 0.9,
      "importance": 1.25,
      "label": "working-level protection tendency",
      "category": "social"
    },
    {
      "type": "min",
      "trait": "workingDrive",
      "value": 0.82,
      "importance": 1.0,
      "label": "working-dog mentality",
      "category": "training"
    },
    {
      "type": "min",
      "trait": "trainability",
      "value": 0.74,
      "importance": 0.55,
      "label": "manageable advanced training ceiling",
      "category": "training"
    },
    {
      "type": "min",
      "trait": "confidence",
      "value": 0.8,
      "importance": 0.55,
      "label": "strong, stable confidence",
      "category": "social"
    },
    {
      "type": "archetype",
      "key": "guardian",
      "value": 1.0
    },
    {
      "type": "context",
      "key": "desiredProtection",
      "value": 1.0
    }
  ],
  "Q43_A": [
    {
      "type": "min",
      "trait": "guestEase",
      "value": 0.86,
      "importance": 1.0,
      "label": "happy, easy guest greetings",
      "category": "social",
      "hard": true
    },
    {
      "type": "min",
      "trait": "strangerFriendliness",
      "value": 0.68,
      "importance": 0.55,
      "label": "welcoming temperament",
      "category": "social"
    },
    {
      "type": "context",
      "key": "desiredGuestEase",
      "value": 1.0
    }
  ],
  "Q43_B": [
    {
      "type": "target",
      "trait": "guestEase",
      "value": 0.66,
      "importance": 0.9,
      "label": "calm greeting after a moment",
      "category": "social"
    },
    {
      "type": "context",
      "key": "desiredGuestEase",
      "value": 0.7
    }
  ],
  "Q43_C": [
    {
      "type": "target",
      "trait": "guestEase",
      "value": 0.45,
      "importance": 0.7,
      "label": "reserved but safe with guests",
      "category": "social"
    },
    {
      "type": "context",
      "key": "desiredGuestEase",
      "value": 0.45
    }
  ],
  "Q43_D": [
    {
      "type": "max",
      "trait": "guestEase",
      "value": 0.35,
      "importance": 0.7,
      "label": "strong guest boundaries are acceptable",
      "category": "social"
    },
    {
      "type": "min",
      "trait": "protectiveness",
      "value": 0.58,
      "importance": 0.45,
      "label": "comfortable with a more guarded dog",
      "category": "social"
    },
    {
      "type": "context",
      "key": "desiredGuestEase",
      "value": 0.15
    }
  ],
  "Q44_A": [
    {
      "type": "max",
      "trait": "shedding",
      "value": 0.15,
      "importance": 1.25,
      "label": "minimal shedding",
      "category": "maintenance",
      "hard": true
    },
    {
      "type": "context",
      "key": "tidinessPriority",
      "value": 0.75
    }
  ],
  "Q44_B": [
    {
      "type": "max",
      "trait": "shedding",
      "value": 0.4,
      "importance": 0.9,
      "label": "some shedding is manageable",
      "category": "maintenance"
    }
  ],
  "Q44_C": [
    {
      "type": "max",
      "trait": "shedding",
      "value": 0.7,
      "importance": 0.45,
      "label": "moderate-to-high shedding is okay",
      "category": "maintenance"
    }
  ],
  "Q44_D": [
    {
      "type": "context",
      "key": "shedTolerance",
      "value": 1.0
    }
  ],
  "Q45_A": [
    {
      "type": "max",
      "trait": "groomingNeed",
      "value": 0.2,
      "importance": 1.2,
      "label": "minimal grooming needs",
      "category": "maintenance",
      "hard": true
    },
    {
      "type": "context",
      "key": "tidinessPriority",
      "value": 0.75
    }
  ],
  "Q45_B": [
    {
      "type": "max",
      "trait": "groomingNeed",
      "value": 0.45,
      "importance": 0.95,
      "label": "weekly grooming is fine",
      "category": "maintenance"
    }
  ],
  "Q45_C": [
    {
      "type": "max",
      "trait": "groomingNeed",
      "value": 0.75,
      "importance": 0.45,
      "label": "frequent brushing is okay",
      "category": "maintenance"
    }
  ],
  "Q45_D": [
    {
      "type": "context",
      "key": "groomingBudget",
      "value": 1.0
    }
  ],
  "Q46_A": [
    {
      "type": "max",
      "trait": "drool",
      "value": 0.15,
      "importance": 1.2,
      "label": "very low drool",
      "category": "maintenance",
      "hard": true
    },
    {
      "type": "context",
      "key": "tidinessPriority",
      "value": 0.75
    }
  ],
  "Q46_B": [
    {
      "type": "max",
      "trait": "drool",
      "value": 0.35,
      "importance": 0.95,
      "label": "a little drool is okay",
      "category": "maintenance"
    }
  ],
  "Q46_C": [
    {
      "type": "max",
      "trait": "drool",
      "value": 0.75,
      "importance": 0.45,
      "label": "moderate drool is fine",
      "category": "maintenance"
    }
  ],
  "Q46_D": [
    {
      "type": "context",
      "key": "droolTolerance",
      "value": 1.0
    }
  ],
  "Q47_A": [
    {
      "type": "max",
      "trait": "odor",
      "value": 0.15,
      "importance": 1.15,
      "label": "low dog odor",
      "category": "maintenance",
      "hard": true
    },
    {
      "type": "context",
      "key": "tidinessPriority",
      "value": 0.75
    }
  ],
  "Q47_B": [
    {
      "type": "max",
      "trait": "odor",
      "value": 0.4,
      "importance": 0.9,
      "label": "some dog smell is manageable",
      "category": "maintenance"
    }
  ],
  "Q47_C": [
    {
      "type": "max",
      "trait": "odor",
      "value": 0.75,
      "importance": 0.45,
      "label": "normal dog smell is okay",
      "category": "maintenance"
    }
  ],
  "Q47_D": [
    {
      "type": "context",
      "key": "odorTolerance",
      "value": 1.0
    }
  ],
  "Q48_A": [
    {
      "type": "min",
      "trait": "allergyFriendliness",
      "value": 0.86,
      "importance": 1.45,
      "label": "allergy-friendliness is a must",
      "category": "maintenance",
      "hard": true,
      "critical": true
    }
  ],
  "Q48_B": [
    {
      "type": "min",
      "trait": "allergyFriendliness",
      "value": 0.72,
      "importance": 1.1,
      "label": "strong preference for allergy-friendliness",
      "category": "maintenance",
      "hard": true
    }
  ],
  "Q48_C": [
    {
      "type": "min",
      "trait": "allergyFriendliness",
      "value": 0.48,
      "importance": 0.5,
      "label": "allergy-friendliness is a bonus",
      "category": "maintenance"
    }
  ],
  "Q48_D": [
    {
      "type": "context",
      "key": "allergyConcern",
      "value": 0.0
    }
  ],
  "Q49_A": [
    {
      "type": "min",
      "trait": "healthRobustness",
      "value": 0.82,
      "importance": 1.15,
      "label": "overall hardiness matters a lot",
      "category": "health",
      "hard": true
    }
  ],
  "Q49_B": [
    {
      "type": "min",
      "trait": "healthRobustness",
      "value": 0.66,
      "importance": 0.9,
      "label": "health robustness matters",
      "category": "health"
    }
  ],
  "Q49_C": [
    {
      "type": "min",
      "trait": "healthRobustness",
      "value": 0.48,
      "importance": 0.45,
      "label": "some preference for hardiness",
      "category": "health"
    }
  ],
  "Q49_D": [
    {
      "type": "context",
      "key": "healthPriority",
      "value": 0.15
    }
  ],
  "Q50_A": [
    {
      "type": "archetype",
      "key": "companion",
      "value": 0.75
    },
    {
      "type": "archetype",
      "key": "beginner",
      "value": 0.6
    },
    {
      "type": "archetype",
      "key": "family",
      "value": 0.25
    },
    {
      "type": "context",
      "key": "tieBreak",
      "value": "companion"
    }
  ],
  "Q50_B": [
    {
      "type": "archetype",
      "key": "family",
      "value": 0.9
    },
    {
      "type": "archetype",
      "key": "companion",
      "value": 0.25
    },
    {
      "type": "context",
      "key": "tieBreak",
      "value": "family"
    }
  ],
  "Q50_C": [
    {
      "type": "archetype",
      "key": "adventure",
      "value": 0.85
    },
    {
      "type": "archetype",
      "key": "sport",
      "value": 0.75
    },
    {
      "type": "context",
      "key": "tieBreak",
      "value": "adventure"
    }
  ],
  "Q50_D": [
    {
      "type": "archetype",
      "key": "watchdog",
      "value": 0.8
    },
    {
      "type": "archetype",
      "key": "guardian",
      "value": 0.7
    },
    {
      "type": "context",
      "key": "tieBreak",
      "value": "watchdog"
    }
  ]
};

  function clamp(value, minValue, maxValue) {
    const num = Number(value);
    if (!Number.isFinite(num)) return minValue;
    return Math.min(maxValue, Math.max(minValue, num));
  }

  function clamp01(value) {
    return clamp(value, 0, 1);
  }

  function isPlainObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]';
  }

  function round(value, digits) {
    const factor = Math.pow(10, digits || 0);
    return Math.round(value * factor) / factor;
  }

  function uniqueStrings(values) {
    const seen = new Set();
    const out = [];
    for (const value of values) {
      if (typeof value !== 'string' || !value.trim()) continue;
      const key = value.trim();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(key);
    }
    return out;
  }

  function average(values, fallback) {
    const clean = (values || []).filter((value) => Number.isFinite(value));
    if (!clean.length) return fallback;
    return clean.reduce((sum, value) => sum + value, 0) / clean.length;
  }

  function weightedAverage(pairs, fallback) {
    let totalWeight = 0;
    let totalValue = 0;
    for (const pair of pairs || []) {
      if (!pair || !Number.isFinite(pair.value) || !Number.isFinite(pair.weight) || pair.weight <= 0) continue;
      totalWeight += pair.weight;
      totalValue += pair.value * pair.weight;
    }
    if (!totalWeight) return fallback;
    return totalValue / totalWeight;
  }

  function sum(values) {
    return (values || []).reduce((total, value) => total + (Number.isFinite(value) ? value : 0), 0);
  }

  function copy(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function createBreedTemplate(name) {
    const template = {
      id: name ? String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'example-breed',
      name: name || 'Example Breed'
    };
    for (const trait of CANONICAL_TRAITS) {
      template[trait] = null;
    }
    return template;
  }

  function normalizeOptionModel(optionModel) {
    if (!optionModel) return CURRENT_OPTION_MODEL_DATA;
    if (isPlainObject(optionModel) && optionModel.data && isPlainObject(optionModel.data)) {
      return optionModel.data;
    }
    return optionModel;
  }

  function normalizeAnswers(answerInput) {
    const out = [];

    function pushOption(optionId) {
      if (typeof optionId !== 'string' || !optionId.trim()) return;
      out.push(optionId.trim());
    }

    if (!answerInput) return out;

    if (Array.isArray(answerInput)) {
      for (const item of answerInput) {
        if (typeof item === 'string') {
          pushOption(item);
          continue;
        }
        if (isPlainObject(item)) {
          pushOption(item.optionId || item.option_id || item.selectedOptionId || item.selected_option_id);
        }
      }
      return uniqueStrings(out);
    }

    if (isPlainObject(answerInput)) {
      for (const value of Object.values(answerInput)) {
        if (typeof value === 'string') {
          pushOption(value);
        } else if (Array.isArray(value)) {
          for (const inner of value) {
            if (typeof inner === 'string') {
              pushOption(inner);
            } else if (isPlainObject(inner)) {
              pushOption(inner.optionId || inner.option_id || inner.selectedOptionId || inner.selected_option_id);
            }
          }
        } else if (isPlainObject(value)) {
          pushOption(value.optionId || value.option_id || value.selectedOptionId || value.selected_option_id);
        }
      }
      return uniqueStrings(out);
    }

    return out;
  }

  function createEmptyUserProfile() {
    return {
      answeredOptionIds: [],
      preferences: [],
      contextBuckets: {},
      context: copy(DEFAULT_CONTEXT),
      archetypes: {
        companion: 0,
        family: 0,
        adventure: 0,
        sport: 0,
        watchdog: 0,
        guardian: 0,
        beginner: 0
      },
      notes: [],
      contradictions: [],
      rawImportanceTotal: 0,
      specificity: 0
    };
  }

  function addContextValue(profile, key, value) {
    if (!profile.contextBuckets[key]) {
      profile.contextBuckets[key] = [];
    }
    profile.contextBuckets[key].push(value);
  }

  function addPreference(profile, action, sourceOptionId) {
    const pref = {
      type: action.type,
      trait: action.trait,
      value: action.value,
      min: action.min,
      max: action.max,
      label: action.label || action.trait,
      category: action.category || 'living',
      importance: Number.isFinite(action.importance) ? action.importance : 0.5,
      hard: Boolean(action.hard),
      critical: Boolean(action.critical),
      softness: Number.isFinite(action.softness) ? action.softness : null,
      sourceOptionId: sourceOptionId || null
    };
    profile.preferences.push(pref);
    profile.rawImportanceTotal += pref.importance;
  }

  function applyOptionEffects(profile, optionId, optionModel) {
    const actions = optionModel[optionId];
    if (!actions || !Array.isArray(actions)) return;
    profile.answeredOptionIds.push(optionId);

    for (const action of actions) {
      if (!action || typeof action !== 'object') continue;
      switch (action.type) {
        case 'context':
          addContextValue(profile, action.key, action.value);
          break;
        case 'archetype':
          if (profile.archetypes[action.key] == null) profile.archetypes[action.key] = 0;
          profile.archetypes[action.key] += Number.isFinite(action.value) ? action.value : 0;
          break;
        case 'note':
          profile.notes.push({
            message: action.message,
            level: action.level || 'info',
            sourceOptionId: optionId
          });
          break;
        case 'target':
        case 'min':
        case 'max':
        case 'range':
          addPreference(profile, action, optionId);
          break;
        default:
          break;
      }
    }
  }

  function aggregateContextBuckets(contextBuckets) {
    const context = copy(DEFAULT_CONTEXT);

    for (const [key, values] of Object.entries(contextBuckets || {})) {
      if (!Array.isArray(values) || !values.length) continue;
      const last = values[values.length - 1];
      const numericValues = values.filter((value) => Number.isFinite(value));

      if (typeof last === 'string') {
        context[key] = last;
      } else if (numericValues.length === values.length) {
        context[key] = average(numericValues, context[key]);
      } else {
        context[key] = last;
      }
    }

    return context;
  }

  function appendContradiction(profile, key, message) {
    if (!profile.contradictions.some((item) => item.key === key)) {
      profile.contradictions.push({ key, message });
    }
  }

  function finalizeUserProfile(profile) {
    profile.context = aggregateContextBuckets(profile.contextBuckets);

    const ctx = profile.context;
    ctx.effectiveAbsence = clamp01(ctx.aloneHours * (1 - 0.45 * ctx.daySupport));

    const protectionCeiling = clamp01(
      0.35 +
      (ctx.experience * 0.35) +
      (ctx.behaviorWorkTolerance * 0.25) -
      (ctx.kidsExposure * 0.12)
    );
    ctx.effectiveProtectionCeiling = Math.max(0.3, protectionCeiling);

    const driveCeiling = clamp01(
      (ctx.exerciseBudget * 0.6) +
      (ctx.mentalBudget * 0.4) +
      (ctx.behaviorWorkTolerance * 0.15)
    );
    ctx.effectiveDriveCeiling = Math.max(0.25, driveCeiling);

    if (ctx.quietPriority > 0.72 && (ctx.desiredProtection > 0.62 || ctx.desiredAlerting > 0.72)) {
      appendContradiction(
        profile,
        'quiet-vs-alerting',
        'You asked for a very quiet dog and meaningful watchdog/protection traits, so the engine now favors breeds that alert briefly rather than persistently bark.'
      );

      addPreference(profile, {
        type: 'max',
        trait: 'barkiness',
        value: 0.38,
        importance: 0.9,
        label: 'brief rather than persistent alerting',
        category: 'living',
        hard: true
      }, 'AUTO_QUIET_ALERT_BALANCE');
    }

    if (Math.max(profile.archetypes.adventure || 0, profile.archetypes.sport || 0) > 0.65 && ctx.effectiveDriveCeiling < 0.55) {
      appendContradiction(
        profile,
        'high-drive-vs-time-budget',
        'You leaned toward a high-drive adventure/training dog, but your daily time budget is tighter, so the engine capped the most demanding breeds.'
      );

      addPreference(profile, {
        type: 'max',
        trait: 'exerciseNeed',
        value: Math.max(0.25, ctx.exerciseBudget + 0.1),
        importance: 0.9,
        label: 'realistic daily exercise load',
        category: 'activity',
        hard: true
      }, 'AUTO_ACTIVITY_CAP');

      addPreference(profile, {
        type: 'max',
        trait: 'mentalNeed',
        value: Math.max(0.3, ctx.mentalBudget + 0.1),
        importance: 0.7,
        label: 'realistic enrichment load',
        category: 'training',
        hard: true
      }, 'AUTO_MENTAL_CAP');
    }

    if (ctx.desiredProtection > ctx.effectiveProtectionCeiling + 0.08) {
      appendContradiction(
        profile,
        'protection-softened',
        'The protection request was softened to a household-manageable level based on experience, training bandwidth, and family context.'
      );

      addPreference(profile, {
        type: 'max',
        trait: 'protectiveness',
        value: ctx.effectiveProtectionCeiling,
        importance: 1.1,
        label: 'household-manageable protection level',
        category: 'safety',
        hard: true
      }, 'AUTO_PROTECTION_CAP');
    }

    if (ctx.kidsExposure > 0.7 && ctx.desiredProtection > 0.78) {
      appendContradiction(
        profile,
        'kids-vs-serious-guardian',
        'A heavily child-centered household and a serious guardian temperament often conflict, so the engine prioritizes family-safe stability over extreme guard traits.'
      );

      addPreference(profile, {
        type: 'max',
        trait: 'protectiveness',
        value: Math.min(ctx.effectiveProtectionCeiling, 0.72),
        importance: 0.85,
        label: 'family-manageable protectiveness',
        category: 'safety',
        hard: true
      }, 'AUTO_FAMILY_GUARD_CAP');
    }

    const archetypeValues = Object.values(profile.archetypes);
    const maxArchetype = Math.max.apply(null, archetypeValues.concat([0]));
    if (maxArchetype > 0) {
      for (const key of Object.keys(profile.archetypes)) {
        profile.archetypes[key] = clamp01(profile.archetypes[key] / maxArchetype);
      }
    }

    profile.specificity = clamp01(profile.rawImportanceTotal / 45);

    return profile;
  }

  function buildUserProfile(answerInput, optionModel) {
    const model = normalizeOptionModel(optionModel);
    const answers = normalizeAnswers(answerInput);
    const profile = createEmptyUserProfile();

    for (const optionId of answers) {
      applyOptionEffects(profile, optionId, model);
    }

    return finalizeUserProfile(profile);
  }

  function coerceNormalizedNumber(value, trait) {
    if (value == null || value === '') return null;

    if (typeof value === 'boolean') {
      return value ? 1 : 0;
    }

    if (typeof value === 'string') {
      const cleaned = value.trim().toLowerCase();

      if (ENUM_NORMALIZERS[trait] && cleaned in ENUM_NORMALIZERS[trait]) {
        return ENUM_NORMALIZERS[trait][cleaned];
      }

      if (cleaned === 'yes' || cleaned === 'true') return 1;
      if (cleaned === 'no' || cleaned === 'false') return 0;

      if (cleaned.endsWith('%')) {
        const pct = Number(cleaned.replace('%', ''));
        if (Number.isFinite(pct)) return clamp01(pct / 100);
      }

      const numeric = Number(cleaned);
      if (!Number.isFinite(numeric)) return null;
      value = numeric;
    }

    if (typeof value !== 'number' || !Number.isFinite(value)) return null;

    if (value >= 0 && value <= 1) return value;
    if (value > 1 && value <= 5) return clamp01((value - 1) / 4);
    if (value > 5 && value <= 10) return clamp01(value / 10);
    if (value > 10 && value <= 100) return clamp01(value / 100);
    return clamp01(value);
  }

  function readTrait(rawBreed, trait) {
    if (!rawBreed || typeof rawBreed !== 'object') return null;

    if (rawBreed.traits && rawBreed.traits[trait] != null) {
      return coerceNormalizedNumber(rawBreed.traits[trait], trait);
    }

    const aliases = TRAIT_ALIASES[trait] || [trait];
    for (const key of aliases) {
      if (rawBreed[key] != null) {
        return coerceNormalizedNumber(rawBreed[key], trait);
      }
      if (rawBreed.profile && rawBreed.profile[key] != null) {
        return coerceNormalizedNumber(rawBreed.profile[key], trait);
      }
    }

    return null;
  }

  function deriveBreedFields(breed) {
    if (breed.workingDrive == null) {
      breed.workingDrive = clamp01(
        weightedAverage([
          { value: breed.mentalNeed, weight: 0.35 },
          { value: breed.trainability, weight: 0.3 },
          { value: breed.playfulness, weight: 0.2 },
          { value: breed.handlerFocus, weight: 0.15 }
        ], 0.5)
      );
    }

    if (breed.apartmentFriendly == null) {
      breed.apartmentFriendly = clamp01(
        weightedAverage([
          { value: breed.indoorCalm, weight: 0.35 },
          { value: 1 - breed.barkiness, weight: 0.35 },
          { value: 1 - breed.exerciseNeed, weight: 0.2 },
          { value: 1 - breed.size, weight: 0.1 }
        ], 0.5)
      );
    }

    if (breed.aloneTimeTolerance == null) {
      breed.aloneTimeTolerance = clamp01(
        weightedAverage([
          { value: breed.independence, weight: 0.45 },
          { value: breed.indoorCalm, weight: 0.35 },
          { value: 1 - breed.barkiness, weight: 0.2 }
        ], 0.5)
      );
    }

    if (breed.noviceFriendliness == null) {
      breed.noviceFriendliness = clamp01(
        weightedAverage([
          { value: breed.trainability, weight: 0.22 },
          { value: breed.indoorCalm, weight: 0.14 },
          { value: 1 - breed.power, weight: 0.14 },
          { value: 1 - breed.protectiveness, weight: 0.12 },
          { value: 1 - breed.preyDrive, weight: 0.1 },
          { value: 1 - breed.mentalNeed, weight: 0.14 },
          { value: 1 - breed.exerciseNeed, weight: 0.14 }
        ], 0.5)
      );
    }

    if (breed.guestEase == null) {
      breed.guestEase = clamp01(
        weightedAverage([
          { value: breed.strangerFriendliness, weight: 0.5 },
          { value: 1 - breed.protectiveness, weight: 0.25 },
          { value: breed.confidence, weight: 0.25 }
        ], 0.5)
      );
    }

    if (breed.toddlerTolerance == null) {
      breed.toddlerTolerance = clamp01(
        weightedAverage([
          { value: breed.childFriendliness, weight: 0.4 },
          { value: breed.confidence, weight: 0.25 },
          { value: 1 - breed.mouthiness, weight: 0.2 },
          { value: breed.indoorCalm, weight: 0.15 }
        ], 0.5)
      );
    }

    if (breed.catCompatibility == null) {
      breed.catCompatibility = clamp01(
        weightedAverage([
          { value: 1 - breed.preyDrive, weight: 0.55 },
          { value: breed.confidence, weight: 0.2 },
          { value: 1 - breed.mouthiness, weight: 0.25 }
        ], 0.5)
      );
    }

    if (breed.recallPotential == null) {
      breed.recallPotential = clamp01(
        weightedAverage([
          { value: breed.trainability, weight: 0.35 },
          { value: breed.handlerFocus, weight: 0.35 },
          { value: 1 - breed.roamingRisk, weight: 0.2 },
          { value: 1 - breed.preyDrive, weight: 0.1 }
        ], 0.5)
      );
    }

    return breed;
  }

  function deriveBreedArchetypes(breed) {
    return {
      companion: clamp01(
        (breed.indoorCalm * 0.28) +
        (breed.cuddliness * 0.24) +
        (breed.noviceFriendliness * 0.2) +
        ((1 - breed.barkiness) * 0.12) +
        ((1 - breed.exerciseNeed) * 0.16)
      ),
      family: clamp01(
        (breed.childFriendliness * 0.32) +
        (breed.toddlerTolerance * 0.28) +
        (breed.guestEase * 0.16) +
        (breed.noviceFriendliness * 0.14) +
        ((1 - breed.mouthiness) * 0.1)
      ),
      adventure: clamp01(
        (breed.exerciseNeed * 0.35) +
        (breed.confidence * 0.15) +
        (breed.playfulness * 0.15) +
        (breed.heatTolerance * 0.05) +
        (breed.coldTolerance * 0.05) +
        (breed.workingDrive * 0.25)
      ),
      sport: clamp01(
        (breed.workingDrive * 0.38) +
        (breed.trainability * 0.22) +
        (breed.mentalNeed * 0.2) +
        (breed.handlerFocus * 0.1) +
        (breed.playfulness * 0.1)
      ),
      watchdog: clamp01(
        (breed.alertness * 0.45) +
        (breed.protectiveness * 0.2) +
        ((1 - breed.strangerFriendliness) * 0.15) +
        (breed.confidence * 0.1) +
        (breed.barkiness * 0.1)
      ),
      guardian: clamp01(
        (breed.protectiveness * 0.5) +
        (breed.confidence * 0.18) +
        (breed.workingDrive * 0.12) +
        ((1 - breed.strangerFriendliness) * 0.1) +
        (breed.alertness * 0.1)
      ),
      beginner: clamp01(breed.noviceFriendliness)
    };
  }

  function normalizeBreed(rawBreed, breedAdapter) {
    const source = typeof breedAdapter === 'function' ? breedAdapter(rawBreed) : rawBreed || {};

    const breed = {
      id: String(source.id || source.slug || source.handle || source.name || 'unknown-breed'),
      name: source.name || source.title || source.handle || source.slug || 'Unknown Breed'
    };

    let directTraitCount = 0;

    for (const trait of CANONICAL_TRAITS) {
      const value = readTrait(source, trait);
      if (value != null) directTraitCount += 1;
      breed[trait] = value;
    }

    for (const trait of CANONICAL_TRAITS) {
      if (breed[trait] == null) {
        breed[trait] = 0.5;
      }
    }

    deriveBreedFields(breed);
    breed.dataCompleteness = clamp01(directTraitCount / CANONICAL_TRAITS.length);
    breed.archetypes = deriveBreedArchetypes(breed);

    return breed;
  }

  function getPreferenceSoftness(pref) {
    if (pref.softness != null) return pref.softness;
    if (pref.critical) return 0.12;
    if (pref.hard) return 0.18;
    return 0.24;
  }

  function evaluatePreference(pref, breed) {
    const traitValue = clamp01(breed[pref.trait]);
    const softness = getPreferenceSoftness(pref);
    let fit = 1;

    if (pref.type === 'target') {
      const distance = Math.abs(traitValue - pref.value);
      fit = clamp01(1 - Math.pow(distance / Math.max(softness, 0.001), 1.35));
    } else if (pref.type === 'min') {
      if (traitValue >= pref.value) {
        fit = 1;
      } else {
        const distance = pref.value - traitValue;
        fit = clamp01(1 - Math.pow(distance / Math.max(softness, 0.001), 1.2));
      }
    } else if (pref.type === 'max') {
      if (traitValue <= pref.value) {
        fit = 1;
      } else {
        const distance = traitValue - pref.value;
        fit = clamp01(1 - Math.pow(distance / Math.max(softness, 0.001), 1.2));
      }
    } else if (pref.type === 'range') {
      if (traitValue >= pref.min && traitValue <= pref.max) {
        fit = 1;
      } else {
        const distance = traitValue < pref.min ? pref.min - traitValue : traitValue - pref.max;
        fit = clamp01(1 - Math.pow(distance / Math.max(softness, 0.001), 1.2));
      }
    }

    const positiveWeight = fit * pref.importance;
    const missWeight = (1 - fit) * pref.importance * (pref.hard ? 1.35 : 1) * (pref.critical ? 1.35 : 1);

    return {
      pref,
      traitValue,
      fit,
      positiveWeight,
      missWeight
    };
  }

  function createPenalty(key, points, message, category, critical) {
    return {
      key,
      points: round(points, 2),
      message,
      category: category || 'safety',
      critical: Boolean(critical)
    };
  }

  function evaluateInteractionRules(profile, breed) {
    const ctx = profile.context;
    const penalties = [];

    function addPenalty(key, points, message, category, critical) {
      if (!Number.isFinite(points) || points <= 0.2) return;
      penalties.push(createPenalty(key, points, message, category, critical));
    }

    if (ctx.sharedWalls > 0.4) {
      const over = Math.max(0, breed.barkiness - (0.25 + ((1 - ctx.quietPriority) * 0.35)));
      addPenalty(
        'shared-wall-noise',
        over * (10 + (8 * ctx.sharedWalls)),
        'Too vocal for close neighbors/shared walls.',
        'living',
        true
      );
    }

    const accessPressure = Math.max(0, 0.65 - ctx.outdoorAccess) + Math.max(0, 0.6 - ctx.quickTripEase);
    if (accessPressure > 0.25) {
      const need = Math.max(0, breed.exerciseNeed - 0.45) + Math.max(0, 0.6 - breed.indoorCalm);
      addPenalty(
        'outdoor-access',
        need * (7 + (6 * accessPressure)),
        'Needs more easy outdoor access or faster potty breaks than your setup suggests.',
        'living',
        false
      );
    }

    if ((ctx.freeRoamPotential || 0) > 0.7) {
      const risk = Math.max(0, breed.roamingRisk - 0.35) +
                   Math.max(0, breed.preyDrive - 0.55) +
                   Math.max(0, 0.55 - breed.recallPotential);
      addPenalty(
        'free-roam-risk',
        risk * 8.5,
        'Risky for large-property freedom or open-gate situations.',
        'safety',
        true
      );
    }

    if (ctx.effectiveAbsence > 0.45) {
      const lack = Math.max(0, ctx.effectiveAbsence - breed.aloneTimeTolerance);
      const compound = Math.max(0, breed.barkiness - 0.45) +
                       Math.max(0, 0.55 - breed.indoorCalm) +
                       Math.max(0, 0.5 - breed.independence);
      addPenalty(
        'alone-time',
        (lack * 14) + (compound * 4),
        'Could struggle with your weekday alone-time pattern.',
        'living',
        ctx.effectiveAbsence > 0.8
      );
    }

    const exerciseMismatch = Math.max(0, breed.exerciseNeed - (ctx.exerciseBudget + 0.12));
    addPenalty(
      'exercise-budget',
      exerciseMismatch * 14,
      'Exercise needs may exceed your weekday routine.',
      'activity',
      ctx.exerciseBudget < 0.3
    );

    const mentalMismatch = Math.max(0, breed.mentalNeed - (ctx.mentalBudget + 0.12));
    addPenalty(
      'mental-budget',
      mentalMismatch * 10,
      'Mental-enrichment needs may exceed your routine.',
      'training',
      false
    );

    let householdRisk =
      (Math.max(0, 0.7 - breed.noviceFriendliness) * (1.2 - ctx.experience)) +
      Math.max(0, breed.workingDrive - ctx.projectTolerance) +
      Math.max(0, breed.protectiveness - (0.4 + (ctx.experience * 0.35) + (ctx.behaviorWorkTolerance * 0.25))) +
      Math.max(0, breed.power - (0.35 + (ctx.powerComfort * 0.55)));

    addPenalty(
      'household-capability',
      householdRisk * 7.5,
      'May demand more experience or management than your profile suggests.',
      'training',
      ctx.experience < 0.3
    );

    if (ctx.kidsExposure > 0.3) {
      let kidRisk =
        Math.max(0, 0.72 - breed.childFriendliness) +
        Math.max(0, (ctx.toddlersPresent ? 0.72 : 0) - breed.toddlerTolerance) +
        Math.max(0, breed.mouthiness - (ctx.toddlersPresent ? 0.28 : 0.45)) +
        (Math.max(0, breed.protectiveness - 0.7) * ctx.kidsExposure * 0.5);

      if (ctx.kidChaos > 0.7) {
        kidRisk += Math.max(0, 0.62 - breed.confidence) + Math.max(0, breed.sensitivity - 0.5);
      }

      addPenalty(
        'kids-fit',
        kidRisk * 8.5,
        'Could be a weaker fit for your child-heavy household.',
        'social',
        Boolean(ctx.toddlersPresent)
      );
    }

    if ((ctx.dogHousehold || 0) > 0 || ctx.dogSocialNeed > 0.6) {
      const dogRisk = Math.max(0, (ctx.dogSocialNeed * 0.8) - breed.dogSociability);
      addPenalty(
        'dog-social',
        dogRisk * 10.5,
        'May not be social or compatible enough with other dogs in your lifestyle.',
        'social',
        (ctx.dogHousehold || 0) > 0
      );
    }

    if ((ctx.catHousehold || 0) > 0 || ctx.smallPetSafetyNeed > 0.6) {
      const catRisk =
        Math.max(0, (ctx.smallPetSafetyNeed * 0.85) - breed.catCompatibility) +
        Math.max(0, breed.preyDrive - (0.35 + (ctx.preyTolerance * 0.45)));

      addPenalty(
        'small-pets',
        catRisk * 12,
        'Prey drive or small-pet compatibility is a concern.',
        'safety',
        (ctx.catHousehold || 0) > 0 || ctx.smallPetSafetyNeed > 0.8
      );
    }

    if (ctx.climate === 'hot') {
      addPenalty(
        'climate-hot',
        Math.max(0, 0.72 - breed.heatTolerance) * 13,
        'Heat tolerance looks weak for your climate.',
        'living',
        true
      );
    } else if (ctx.climate === 'cold') {
      addPenalty(
        'climate-cold',
        Math.max(0, 0.72 - breed.coldTolerance) * 13,
        'Cold tolerance looks weak for your climate.',
        'living',
        true
      );
    } else if (ctx.climate === 'mixed') {
      addPenalty(
        'climate-mixed',
        (Math.max(0, 0.48 - breed.heatTolerance) + Math.max(0, 0.48 - breed.coldTolerance)) * 7.5,
        'May not adapt well across hot/cold seasonal swings.',
        'living',
        false
      );
    }

    const physicalMismatch =
      Math.max(0, breed.power - (0.25 + (ctx.powerComfort * 0.65))) +
      Math.max(0, breed.size - (0.2 + (ctx.liftCapacity * 0.75)));

    addPenalty(
      'physical-handling',
      physicalMismatch * 9.5,
      'Physical size or strength may be harder to manage than you want.',
      'safety',
      ctx.powerComfort < 0.35 || ctx.liftCapacity < 0.35
    );

    if (ctx.escapeConcern > 0.6 || ctx.recallNeed > 0.7) {
      const recallRisk =
        Math.max(0, (ctx.recallNeed * 0.85) - breed.recallPotential) +
        Math.max(0, breed.roamingRisk - (0.5 - (ctx.escapeConcern * 0.25)));

      addPenalty(
        'recall-escape',
        recallRisk * 10.5,
        'Recall or roaming tendencies may be a concern.',
        'safety',
        ctx.escapeConcern > 0.85
      );
    }

    if (ctx.desiredProtection > 0.55) {
      addPenalty(
        'protection-low',
        Math.max(0, ctx.desiredProtection - breed.protectiveness) * 8.5,
        'May not feel protective or watchful enough.',
        'social',
        false
      );
    } else if (ctx.desiredProtection < 0.3) {
      addPenalty(
        'protection-high',
        Math.max(0, breed.protectiveness - 0.55) * 7.0,
        'Could feel more guarded or watchful than you want.',
        'social',
        false
      );
    }

    if (ctx.desiredStrangerFriendliness > 0.75) {
      addPenalty(
        'stranger-style',
        Math.max(0, 0.78 - breed.strangerFriendliness) * 7.5,
        'Less social with strangers than you prefer.',
        'social',
        false
      );
    } else if (ctx.desiredStrangerFriendliness < 0.3) {
      addPenalty(
        'stranger-style',
        Math.max(0, breed.strangerFriendliness - 0.5) * 4.5,
        'More socially forward with strangers than you asked for.',
        'social',
        false
      );
    }

    if (ctx.desiredGuestEase > 0.75) {
      addPenalty(
        'guest-style',
        Math.max(0, 0.78 - breed.guestEase) * 7.0,
        'Could need more guest management than you want.',
        'social',
        false
      );
    }

    return penalties.sort((left, right) => right.points - left.points);
  }

  function computeArchetypeFit(profile, breed) {
    const desired = profile.archetypes || {};
    const actual = breed.archetypes || {};

    const pairs = Object.keys(desired)
      .filter((key) => Number.isFinite(desired[key]) && desired[key] > 0)
      .map((key) => {
        return {
          value: 1 - Math.abs((desired[key] || 0) - (actual[key] || 0.5)),
          weight: desired[key]
        };
      });

    return clamp01(weightedAverage(pairs, 0.5));
  }

  function computeTieBreakScore(profile, breed) {
    const tieBreak = profile.context.tieBreak;
    if (!tieBreak) return computeArchetypeFit(profile, breed);
    return clamp01((breed.archetypes && breed.archetypes[tieBreak]) != null ? breed.archetypes[tieBreak] : 0.5);
  }

  function categoryBreakdown(prefResults) {
    const buckets = {};
    for (const category of CATEGORY_DEFAULTS) {
      buckets[category] = { numerator: 0, denominator: 0 };
    }

    for (const result of prefResults) {
      const category = result.pref.category || 'living';
      if (!buckets[category]) {
        buckets[category] = { numerator: 0, denominator: 0 };
      }
      buckets[category].numerator += result.fit * result.pref.importance;
      buckets[category].denominator += result.pref.importance;
    }

    const out = {};
    for (const [category, bucket] of Object.entries(buckets)) {
      out[category] = bucket.denominator > 0 ? round((bucket.numerator / bucket.denominator) * 100, 1) : null;
    }

    return out;
  }

  function collectReasons(prefResults) {
    return uniqueStrings(
      prefResults
        .filter((result) => result.fit >= 0.78)
        .sort((left, right) => right.positiveWeight - left.positiveWeight)
        .map((result) => result.pref.label)
    ).slice(0, 4);
  }

  function collectCautions(prefResults, interactionPenalties) {
    const prefCautions = prefResults
      .filter((result) => result.missWeight > 0.55)
      .sort((left, right) => right.missWeight - left.missWeight)
      .map((result) => result.pref.label);

    const interactionCautions = (interactionPenalties || [])
      .filter((penalty) => penalty.points > 1.0)
      .sort((left, right) => right.points - left.points)
      .map((penalty) => penalty.message);

    return uniqueStrings([].concat(interactionCautions, prefCautions)).slice(0, 5);
  }

  function scoreBreed(breedInput, userProfile, options) {
    const opts = options || {};
    const profile = userProfile && userProfile.preferences ? userProfile : buildUserProfile(userProfile || [], opts.optionModel);
    const breed = normalizeBreed(breedInput, opts.breedAdapter);

    const prefResults = profile.preferences.map((pref) => evaluatePreference(pref, breed));
    const totalImportance = sum(prefResults.map((result) => result.pref.importance)) || 1;
    const weightedFit = sum(prefResults.map((result) => result.fit * result.pref.importance)) / totalImportance;
    const baseScore = round(weightedFit * 100, 2);

    const interactionPenalties = evaluateInteractionRules(profile, breed);
    const interactionPenaltyPoints = round(sum(interactionPenalties.map((penalty) => penalty.points)), 2);

    const archetypeFit = computeArchetypeFit(profile, breed);
    const tieBreakScore = computeTieBreakScore(profile, breed);
    const confidenceScore = round(
      100 * ((profile.specificity * 0.55) + (breed.dataCompleteness * 0.45)),
      1
    );

    const hardMisses = prefResults.filter((result) => result.pref.hard && result.fit < 0.32);
    const criticalMisses = prefResults.filter((result) => result.pref.critical && result.fit < 0.45);
    const severeInteractionConflicts = interactionPenalties.filter((penalty) => penalty.critical && penalty.points >= 10);

    const finalScore = clamp(
      baseScore -
      interactionPenaltyPoints +
      ((archetypeFit - 0.5) * 8) +
      ((breed.dataCompleteness - 0.7) * 4) +
      ((profile.specificity - 0.5) * 2),
      0,
      100
    );

    const excluded = criticalMisses.length > 0 || severeInteractionConflicts.length > 0 || hardMisses.length >= 4;

    return {
      id: breed.id,
      name: breed.name,
      score: round(finalScore, 2),
      baseScore,
      interactionPenaltyPoints,
      archetypeFit: round(archetypeFit, 3),
      tieBreakScore: round(tieBreakScore, 3),
      confidenceScore,
      excluded,
      breakdown: categoryBreakdown(prefResults),
      reasons: collectReasons(prefResults),
      cautions: collectCautions(prefResults, interactionPenalties),
      hardConflicts: uniqueStrings(hardMisses.map((result) => result.pref.label)),
      criticalConflicts: uniqueStrings(criticalMisses.map((result) => result.pref.label).concat(
        severeInteractionConflicts.map((penalty) => penalty.message)
      )),
      interactionPenalties,
      normalizedBreed: breed
    };
  }

  function resultScore(result) {
    return result && Number.isFinite(result.score) ? result.score : -Infinity;
  }

  function resultNameKey(result) {
    return String((result && (result.name || result.id)) || '');
  }

  function compareRankedResults(left, right) {
    // Primary rule: strict score order. Tie-breakers only apply when the
    // rounded score is effectively identical, so a lower-scoring breed can
    // never outrank a higher-scoring breed.
    const scoreGap = resultScore(right) - resultScore(left);
    if (Math.abs(scoreGap) > RANKING_SCORE_EPSILON) {
      return scoreGap;
    }

    if (left.excluded !== right.excluded) {
      return left.excluded ? 1 : -1;
    }

    const tieGap = right.tieBreakScore - left.tieBreakScore;
    if (Math.abs(tieGap) > 0.01) {
      return tieGap;
    }

    const archetypeGap = right.archetypeFit - left.archetypeFit;
    if (Math.abs(archetypeGap) > 0.01) {
      return archetypeGap;
    }

    const confidenceGap = right.confidenceScore - left.confidenceScore;
    if (Math.abs(confidenceGap) > 0.01) {
      return confidenceGap;
    }

    return resultNameKey(left).localeCompare(resultNameKey(right));
  }

  function selectTopBreedResults(scored, limit, includeExcluded) {
    const topLimit = Math.min(TOP_RESULTS_COUNT, Math.max(1, Math.floor(limit || TOP_RESULTS_COUNT)));

    if (includeExcluded) {
      return scored.slice(0, topLimit);
    }

    const selected = scored.filter((result) => !result.excluded).slice(0, topLimit);

    // The quiz must always show five ranked breeds when enough breed data exists.
    // If hard constraints leave fewer than five eligible breeds, backfill with the
    // next-best scored breeds rather than returning a short result set.
    if (selected.length < topLimit) {
      const selectedIds = new Set(selected.map((result) => result.id));
      for (const result of scored) {
        if (selected.length >= topLimit) break;
        if (selectedIds.has(result.id)) continue;
        selected.push(result);
        selectedIds.add(result.id);
      }
      selected.sort(compareRankedResults);
    }

    return selected;
  }

  function rankBreeds(arg1, arg2, arg3) {
    let answers;
    let breeds;
    let options;

    if (arguments.length === 1 && isPlainObject(arg1) && ('answers' in arg1 || 'breeds' in arg1)) {
      answers = arg1.answers;
      breeds = Array.isArray(arg1.breeds) ? arg1.breeds : [];
      options = {
        limit: arg1.limit,
        includeExcluded: arg1.includeExcluded,
        optionModel: arg1.optionModel,
        breedAdapter: arg1.breedAdapter
      };
    } else {
      answers = arg1;
      breeds = Array.isArray(arg2) ? arg2 : [];
      options = isPlainObject(arg3) ? arg3 : {};
    }

    const limit = TOP_RESULTS_COUNT;
    const includeExcluded = Boolean(options.includeExcluded);
    const profile = buildUserProfile(answers, options.optionModel);
    const scored = breeds.map((breed) => scoreBreed(breed, profile, options));

    scored.sort(compareRankedResults);

    return {
      version: VERSION,
      optionModelId: OPTION_MODEL_ID,
      profile,
      results: selectTopBreedResults(scored, limit, includeExcluded),
      allResults: scored,
      summary: {
        answeredCount: profile.answeredOptionIds.length,
        totalBreeds: breeds.length,
        returnedCount: Math.min(TOP_RESULTS_COUNT, scored.length),
        excludedCount: scored.filter((result) => result.excluded).length,
        contradictions: profile.contradictions.slice()
      }
    };
  }

  function auditQuestionFlow(schema) {
    if (!schema || !Array.isArray(schema.questions)) {
      return {
        ok: false,
        error: 'Expected a question schema object with a questions array.'
      };
    }

    const nodeMap = new Map();
    const missingTargets = [];
    const duplicateNodeIds = [];

    for (const question of schema.questions) {
      const nodeId = question && question.node_id;
      if (nodeMap.has(nodeId)) {
        duplicateNodeIds.push(nodeId);
      }
      nodeMap.set(nodeId, question);
    }

    const reachableNodes = new Set();
    const queue = [schema.start_node_id];

    while (queue.length) {
      const nodeId = queue.shift();
      if (reachableNodes.has(nodeId)) continue;
      reachableNodes.add(nodeId);

      const question = nodeMap.get(nodeId);
      if (!question || !Array.isArray(question.options)) continue;

      for (const option of question.options) {
        const nextValue = option.next;
        if (typeof nextValue === 'number') {
          if (!nodeMap.has(nextValue)) {
            missingTargets.push({
              fromNodeId: nodeId,
              optionId: option.option_id,
              missingNodeId: nextValue
            });
          } else {
            queue.push(nextValue);
          }
        }
      }
    }

    const unreachableQuestions = schema.questions
      .filter((question) => !reachableNodes.has(question.node_id))
      .map((question) => question.question_id);

    const deadEnds = schema.questions
      .filter((question) => Array.isArray(question.options) && question.options.every((option) => option.next === 'RESULT'))
      .map((question) => question.question_id);

    return {
      ok: duplicateNodeIds.length === 0 && missingTargets.length === 0,
      startNodeId: schema.start_node_id,
      reachableNodeIds: Array.from(reachableNodes).sort((a, b) => a - b),
      unreachableQuestionIds: unreachableQuestions,
      duplicateNodeIds,
      missingTargets,
      terminalQuestionIds: deadEnds
    };
  }

  return {
    version: VERSION,
    optionModelId: OPTION_MODEL_ID,
    currentOptionModel: CURRENT_OPTION_MODEL_DATA,
    canonicalTraits: CANONICAL_TRAITS.slice(),
    defaultContext: copy(DEFAULT_CONTEXT),
    createBreedTemplate,
    buildUserProfile,
    normalizeBreed,
    scoreBreed,
    rankBreeds,
    auditQuestionFlow
  };
}));

(function(root) {
  'use strict';

  root.DOG_BREED_QUIZ_COPY_QUESTIONS_V2 = {
  "schema": "dog_quiz_copy_questions",
  "schema_version": 1,
  "id": "dog-breed-quiz-copy-questions-v2",
  "language": "en",
  "variant_styles": [
    {
      "index": 0,
      "id": "clear",
      "name": "Clear",
      "description": "Direct, plain-language wording."
    },
    {
      "index": 1,
      "id": "friendly",
      "name": "Friendly",
      "description": "Warm, conversational wording."
    },
    {
      "index": 2,
      "id": "scenario",
      "name": "Scenario",
      "description": "A quick real-life scenario to help beginners answer."
    },
    {
      "index": 3,
      "id": "playful",
      "name": "Playful",
      "description": "Light, fun phrasing that still stays clear."
    }
  ],
  "start_node_id": 1,
  "result_token": "RESULT",
  "questions": [
    {
      "question_id": "Q01",
      "node_id": 1,
      "order": 1,
      "variants": [
        "Which home setup best matches where your dog would live most of the time?",
        "What kind of home would your dog be living in day to day?",
        "Picture your dog's normal home base. Which setup is closest?",
        "What does your dog's everyday home situation look like?"
      ],
      "options": [
        {
          "option_id": "Q01_A",
          "label": "A",
          "next": 2
        },
        {
          "option_id": "Q01_B",
          "label": "B",
          "next": 5
        },
        {
          "option_id": "Q01_C",
          "label": "C",
          "next": 7
        },
        {
          "option_id": "Q01_D",
          "label": "D",
          "next": 7
        }
      ]
    },
    {
      "question_id": "Q02",
      "node_id": 2,
      "order": 2,
      "variants": [
        "If your dog barked for 30–60 seconds at hallway or neighbor noise, how much of a problem would that be?",
        "If hallway sounds triggered about a minute of barking, how workable would that be where you live?",
        "Imagine footsteps outside your door set off a short barking burst. What would that cause for you?",
        "Neighbor-noise reality check: one minute of barking would be…?"
      ],
      "options": [
        {
          "option_id": "Q02_A",
          "label": "A",
          "next": 3
        },
        {
          "option_id": "Q02_B",
          "label": "B",
          "next": 3
        },
        {
          "option_id": "Q02_C",
          "label": "C",
          "next": 3
        },
        {
          "option_id": "Q02_D",
          "label": "D",
          "next": 3
        }
      ]
    },
    {
      "question_id": "Q03",
      "node_id": 3,
      "order": 3,
      "variants": [
        "On a normal day, how easy is it for you to get a dog outside for quick potty breaks or a short walk?",
        "How practical is it for you to take a dog out quickly during the day?",
        "If your dog needs a fast trip outside, how easy is that in your routine?",
        "Quick-break logistics: easy, manageable, annoying, or genuinely hard?"
      ],
      "options": [
        {
          "option_id": "Q03_A",
          "label": "A",
          "next": 4
        },
        {
          "option_id": "Q03_B",
          "label": "B",
          "next": 4
        },
        {
          "option_id": "Q03_C",
          "label": "C",
          "next": 4
        },
        {
          "option_id": "Q03_D",
          "label": "D",
          "next": 4
        }
      ]
    },
    {
      "question_id": "Q04",
      "node_id": 4,
      "order": 4,
      "variants": [
        "What kind of indoor energy level would feel best to live with?",
        "Inside the house, what pace do you want your dog to have?",
        "Think about a normal evening indoors. How active can your dog be and still feel like a good fit?",
        "Indoor vibe: mostly calm, balanced, playful, or busy?"
      ],
      "options": [
        {
          "option_id": "Q04_A",
          "label": "A",
          "next": 9
        },
        {
          "option_id": "Q04_B",
          "label": "B",
          "next": 9
        },
        {
          "option_id": "Q04_C",
          "label": "C",
          "next": 9
        },
        {
          "option_id": "Q04_D",
          "label": "D",
          "next": 9
        }
      ]
    },
    {
      "question_id": "Q05",
      "node_id": 5,
      "order": 5,
      "variants": [
        "With neighbors close by, how would you feel about a dog that barks when someone passes your home?",
        "If your dog ‘announced’ every passerby, how would you feel?",
        "Picture people walking by your place—barking good or bad?",
        "Passerby patrol: would barking drive you nuts or feel reassuring?"
      ],
      "options": [
        {
          "option_id": "Q05_A",
          "label": "A",
          "next": 6
        },
        {
          "option_id": "Q05_B",
          "label": "B",
          "next": 6
        },
        {
          "option_id": "Q05_C",
          "label": "C",
          "next": 6
        },
        {
          "option_id": "Q05_D",
          "label": "D",
          "next": 6
        }
      ]
    },
    {
      "question_id": "Q06",
      "node_id": 6,
      "order": 6,
      "variants": [
        "Right at home, what outdoor access do you have for the dog?",
        "What outdoor setup do you have right at home for your dog?",
        "When your dog needs fresh air, what’s easiest at your place?",
        "Home turf: how does your dog get outside?"
      ],
      "options": [
        {
          "option_id": "Q06_A",
          "label": "A",
          "next": 9
        },
        {
          "option_id": "Q06_B",
          "label": "B",
          "next": 9
        },
        {
          "option_id": "Q06_C",
          "label": "C",
          "next": 9
        },
        {
          "option_id": "Q06_D",
          "label": "D",
          "next": 9
        }
      ]
    },
    {
      "question_id": "Q07",
      "node_id": 7,
      "order": 7,
      "variants": [
        "How secure is your outdoor setup (fence/containment) most of the time?",
        "How reliable is your fence/containment for keeping a dog safely in?",
        "If your dog spots something exciting outside, how likely is it they could get out?",
        "Is your yard Fort Knox… or more ‘oops, he’s gone’?"
      ],
      "options": [
        {
          "option_id": "Q07_A",
          "label": "A",
          "next": 8
        },
        {
          "option_id": "Q07_B",
          "label": "B",
          "next": 8
        },
        {
          "option_id": "Q07_C",
          "label": "C",
          "next": 8
        },
        {
          "option_id": "Q07_D",
          "label": "D",
          "next": 8
        }
      ]
    },
    {
      "question_id": "Q08",
      "node_id": 8,
      "order": 8,
      "variants": [
        "Are there animals/things at home that would be a big problem if a dog chased them?",
        "If your dog chased animals on your property, how big of a deal would that be?",
        "Imagine your dog bolts after wildlife—problem or manageable?",
        "Chase drama: would it be dangerous, annoying, or totally fine?"
      ],
      "options": [
        {
          "option_id": "Q08_A",
          "label": "A",
          "next": 9
        },
        {
          "option_id": "Q08_B",
          "label": "B",
          "next": 9
        },
        {
          "option_id": "Q08_C",
          "label": "C",
          "next": 9
        },
        {
          "option_id": "Q08_D",
          "label": "D",
          "next": 9
        }
      ]
    },
    {
      "question_id": "Q09",
      "node_id": 9,
      "order": 9,
      "variants": [
        "What role do you most want this dog to play in your life?",
        "What is the main job or vibe you want from your dog?",
        "If one goal mattered most, what would you want this dog to be for you?",
        "Big-picture fit: what kind of dog are you really looking for?"
      ],
      "options": [
        {
          "option_id": "Q09_A",
          "label": "A",
          "next": 10
        },
        {
          "option_id": "Q09_B",
          "label": "B",
          "next": 10
        },
        {
          "option_id": "Q09_C",
          "label": "C",
          "next": 10
        },
        {
          "option_id": "Q09_D",
          "label": "D",
          "next": 10
        }
      ]
    },
    {
      "question_id": "Q10",
      "node_id": 10,
      "order": 10,
      "variants": [
        "What climate will your dog mostly live in?",
        "What kind of weather does your dog need to handle for most of the year?",
        "Think about your usual year. What climate is most accurate?",
        "Weather profile: hot, cold, mixed, or mild?"
      ],
      "options": [
        {
          "option_id": "Q10_A",
          "label": "A",
          "next": 11
        },
        {
          "option_id": "Q10_B",
          "label": "B",
          "next": 11
        },
        {
          "option_id": "Q10_C",
          "label": "C",
          "next": 11
        },
        {
          "option_id": "Q10_D",
          "label": "D",
          "next": 11
        }
      ]
    },
    {
      "question_id": "Q11",
      "node_id": 11,
      "order": 11,
      "variants": [
        "What size dog feels realistic for your lifestyle and home?",
        "What dog size feels like the best fit for you?",
        "When you picture your ideal dog, what size comes to mind?",
        "Size preference: small, medium, large, or surprise me?"
      ],
      "options": [
        {
          "option_id": "Q11_A",
          "label": "A",
          "next": 12
        },
        {
          "option_id": "Q11_B",
          "label": "B",
          "next": 12
        },
        {
          "option_id": "Q11_C",
          "label": "C",
          "next": 12
        },
        {
          "option_id": "Q11_D",
          "label": "D",
          "next": 12
        }
      ]
    },
    {
      "question_id": "Q12",
      "node_id": 12,
      "order": 12,
      "variants": [
        "On leash, if a dog suddenly pulls toward a squirrel/dog, how comfortable are you handling that?",
        "If your dog yanked toward something exciting, how okay are you with that?",
        "On leash, can you manage a sudden pull?",
        "Leash moment: squirrel sprint—how confident are you?"
      ],
      "options": [
        {
          "option_id": "Q12_A",
          "label": "A",
          "next": 13
        },
        {
          "option_id": "Q12_B",
          "label": "B",
          "next": 13
        },
        {
          "option_id": "Q12_C",
          "label": "C",
          "next": 13
        },
        {
          "option_id": "Q12_D",
          "label": "D",
          "next": 13
        }
      ]
    },
    {
      "question_id": "Q13",
      "node_id": 13,
      "order": 13,
      "variants": [
        "If your dog got injured, how realistic is it for you to lift/carry them (stairs, car, vet, etc.)?",
        "If you had to lift your dog into a car or up stairs, what's realistic?",
        "Emergency moment: can you carry your dog if needed?",
        "Real talk—could you lift your dog in a pinch?"
      ],
      "options": [
        {
          "option_id": "Q13_A",
          "label": "A",
          "next": 14
        },
        {
          "option_id": "Q13_B",
          "label": "B",
          "next": 14
        },
        {
          "option_id": "Q13_C",
          "label": "C",
          "next": 14
        },
        {
          "option_id": "Q13_D",
          "label": "D",
          "next": 14
        }
      ]
    },
    {
      "question_id": "Q14",
      "node_id": 14,
      "order": 14,
      "variants": [
        "On a typical weekday, what is the longest stretch your dog would be home alone with no people around?",
        "How long would your dog usually be alone in one block on weekdays?",
        "Think about a normal work or school day. What is the longest solo stretch?",
        "Home-alone reality: what's the longest stretch?"
      ],
      "options": [
        {
          "option_id": "Q14_A",
          "label": "A",
          "next": 15
        },
        {
          "option_id": "Q14_B",
          "label": "B",
          "next": 15
        },
        {
          "option_id": "Q14_C",
          "label": "C",
          "next": 15
        },
        {
          "option_id": "Q14_D",
          "label": "D",
          "next": 15
        }
      ]
    },
    {
      "question_id": "Q15",
      "node_id": 15,
      "order": 15,
      "variants": [
        "When you’re away, what support can you reliably provide most weeks?",
        "When you're out, what backup plan can you truly count on most weeks?",
        "Picture your busiest week—what help would your dog actually get while you’re away?",
        "Away-time reality check: what’s realistic?"
      ],
      "options": [
        {
          "option_id": "Q15_A",
          "label": "A",
          "next": 16
        },
        {
          "option_id": "Q15_B",
          "label": "B",
          "next": 16
        },
        {
          "option_id": "Q15_C",
          "label": "C",
          "next": 16
        },
        {
          "option_id": "Q15_D",
          "label": "D",
          "next": 16
        }
      ]
    },
    {
      "question_id": "Q16",
      "node_id": 16,
      "order": 16,
      "variants": [
        "On most weekdays, how much time can you realistically spend on dog exercise beyond potty trips?",
        "How much weekday time do you honestly have for walks and active play?",
        "In a normal weekday routine, how much exercise time can you reliably give a dog?",
        "Exercise budget: what is realistic on most weekdays?"
      ],
      "options": [
        {
          "option_id": "Q16_A",
          "label": "A",
          "next": 17
        },
        {
          "option_id": "Q16_B",
          "label": "B",
          "next": 17
        },
        {
          "option_id": "Q16_C",
          "label": "C",
          "next": 17
        },
        {
          "option_id": "Q16_D",
          "label": "D",
          "next": 17
        }
      ]
    },
    {
      "question_id": "Q17",
      "node_id": 17,
      "order": 17,
      "variants": [
        "What kind of activity do you actually enjoy doing with a dog?",
        "What kind of ‘fun’ do you want to do with your dog?",
        "When you picture time with your dog, what does it look like?",
        "Pick your dog-date activity:"
      ],
      "options": [
        {
          "option_id": "Q17_A",
          "label": "A",
          "next": 18
        },
        {
          "option_id": "Q17_B",
          "label": "B",
          "next": 18
        },
        {
          "option_id": "Q17_C",
          "label": "C",
          "next": 18
        },
        {
          "option_id": "Q17_D",
          "label": "D",
          "next": 18
        }
      ]
    },
    {
      "question_id": "Q18",
      "node_id": 18,
      "order": 18,
      "variants": [
        "How often would you realistically do ‘brain games’ with your dog (training, puzzles, sniff games)?",
        "How often will you do training/puzzles/sniff games—realistically?",
        "On most weeks, how much ‘mental workout’ will your dog get?",
        "Brain time: how much do you want to do?"
      ],
      "options": [
        {
          "option_id": "Q18_A",
          "label": "A",
          "next": 19
        },
        {
          "option_id": "Q18_B",
          "label": "B",
          "next": 19
        },
        {
          "option_id": "Q18_C",
          "label": "C",
          "next": 19
        },
        {
          "option_id": "Q18_D",
          "label": "D",
          "next": 19
        }
      ]
    },
    {
      "question_id": "Q19",
      "node_id": 19,
      "order": 19,
      "variants": [
        "How much hands-on experience do you have as the person mainly responsible for a dog's care and training?",
        "What is your real experience level as the primary dog person?",
        "How experienced are you with living with, managing, and training dogs yourself?",
        "Owner experience check: first-timer, some experience, solid experience, or advanced?"
      ],
      "options": [
        {
          "option_id": "Q19_A",
          "label": "A",
          "next": 20
        },
        {
          "option_id": "Q19_B",
          "label": "B",
          "next": 20
        },
        {
          "option_id": "Q19_C",
          "label": "C",
          "next": 20
        },
        {
          "option_id": "Q19_D",
          "label": "D",
          "next": 20
        }
      ]
    },
    {
      "question_id": "Q20",
      "node_id": 20,
      "order": 20,
      "variants": [
        "In the first year, how much training and behavior management are you willing to take on?",
        "How much year-one work are you willing to do on things like chewing, jumping, barking, and manners?",
        "Be realistic: how much dog-chaos coaching can you handle in year one?",
        "Year-one difficulty setting: how much work are you actually up for?"
      ],
      "options": [
        {
          "option_id": "Q20_A",
          "label": "A",
          "next": 21
        },
        {
          "option_id": "Q20_B",
          "label": "B",
          "next": 21
        },
        {
          "option_id": "Q20_C",
          "label": "C",
          "next": 21
        },
        {
          "option_id": "Q20_D",
          "label": "D",
          "next": 21
        }
      ]
    },
    {
      "question_id": "Q21",
      "node_id": 21,
      "order": 21,
      "variants": [
        "How important is it that your dog is generally easy to train for basics (sit, leash walking, manners)?",
        "How important is it that training the basics feels pretty smooth?",
        "Do you need an easy-to-train dog for everyday manners?",
        "Basics like sit/loose leash—how big a deal is ‘easy to train’?"
      ],
      "options": [
        {
          "option_id": "Q21_A",
          "label": "A",
          "next": 22
        },
        {
          "option_id": "Q21_B",
          "label": "B",
          "next": 22
        },
        {
          "option_id": "Q21_C",
          "label": "C",
          "next": 22
        },
        {
          "option_id": "Q21_D",
          "label": "D",
          "next": 22
        }
      ]
    },
    {
      "question_id": "Q22",
      "node_id": 22,
      "order": 22,
      "variants": [
        "When you call your dog or give a cue, what do you expect most of the time?",
        "When you ask your dog to do something, how ‘snappy’ should the response be?",
        "If you call your dog, do you expect an instant response or is ‘eventually’ okay?",
        "Cue response: do you want ‘right now’ or ‘when they feel like it’?"
      ],
      "options": [
        {
          "option_id": "Q22_A",
          "label": "A",
          "next": 23
        },
        {
          "option_id": "Q22_B",
          "label": "B",
          "next": 23
        },
        {
          "option_id": "Q22_C",
          "label": "C",
          "next": 23
        },
        {
          "option_id": "Q22_D",
          "label": "D",
          "next": 23
        }
      ]
    },
    {
      "question_id": "Q23",
      "node_id": 23,
      "order": 23,
      "variants": [
        "Picture a normal evening at home—what do you want your dog to be doing?",
        "After dinner, what would your ideal dog be doing?",
        "In the evenings, do you want a lounger or a playmate?",
        "Nighttime at home: what’s your dog’s vibe?"
      ],
      "options": [
        {
          "option_id": "Q23_A",
          "label": "A",
          "next": 24
        },
        {
          "option_id": "Q23_B",
          "label": "B",
          "next": 24
        },
        {
          "option_id": "Q23_C",
          "label": "C",
          "next": 24
        },
        {
          "option_id": "Q23_D",
          "label": "D",
          "next": 24
        }
      ]
    },
    {
      "question_id": "Q24",
      "node_id": 24,
      "order": 24,
      "variants": [
        "If your dog barked when the doorbell rings or someone knocks, how would you feel?",
        "If the doorbell sets off barking, how would you feel?",
        "Door knocks and deliveries—barking welcome or not?",
        "Delivery time: does barking annoy you or feel helpful?"
      ],
      "options": [
        {
          "option_id": "Q24_A",
          "label": "A",
          "next": 25
        },
        {
          "option_id": "Q24_B",
          "label": "B",
          "next": 25
        },
        {
          "option_id": "Q24_C",
          "label": "C",
          "next": 25
        },
        {
          "option_id": "Q24_D",
          "label": "D",
          "next": 25
        }
      ]
    },
    {
      "question_id": "Q25",
      "node_id": 25,
      "order": 25,
      "variants": [
        "Imagine the dog chews something valuable once (shoe/couch corner). What’s your reaction?",
        "If your dog wrecked one thing once, how would you react?",
        "Oops moment: chewed shoe/couch corner—what happens next?",
        "Chew test: one expensive mistake—dealbreaker or teachable moment?"
      ],
      "options": [
        {
          "option_id": "Q25_A",
          "label": "A",
          "next": 26
        },
        {
          "option_id": "Q25_B",
          "label": "B",
          "next": 26
        },
        {
          "option_id": "Q25_C",
          "label": "C",
          "next": 26
        },
        {
          "option_id": "Q25_D",
          "label": "D",
          "next": 26
        }
      ]
    },
    {
      "question_id": "Q26",
      "node_id": 26,
      "order": 26,
      "variants": [
        "How would a dog that startles easily (noise-sensitive, sensitive to correction) fit your home?",
        "How well would a sensitive/easily startled dog fit your home?",
        "Some dogs are sensitive to noise and surprises—does that work for you?",
        "Sensitivity check: does your home suit a ‘delicate flower’ or a ‘tough cookie’?"
      ],
      "options": [
        {
          "option_id": "Q26_A",
          "label": "A",
          "next": 27
        },
        {
          "option_id": "Q26_B",
          "label": "B",
          "next": 27
        },
        {
          "option_id": "Q26_C",
          "label": "C",
          "next": 27
        },
        {
          "option_id": "Q26_D",
          "label": "D",
          "next": 27
        }
      ]
    },
    {
      "question_id": "Q27",
      "node_id": 27,
      "order": 27,
      "variants": [
        "How ‘cuddly’ do you want your dog to be with you/your household?",
        "How much personal space do you want your dog to respect?",
        "Are you looking for a cuddle bug or a more independent roommate?",
        "Cuddle level: Velcro dog or independent pal?"
      ],
      "options": [
        {
          "option_id": "Q27_A",
          "label": "A",
          "next": 28
        },
        {
          "option_id": "Q27_B",
          "label": "B",
          "next": 28
        },
        {
          "option_id": "Q27_C",
          "label": "C",
          "next": 28
        },
        {
          "option_id": "Q27_D",
          "label": "D",
          "next": 28
        }
      ]
    },
    {
      "question_id": "Q28",
      "node_id": 28,
      "order": 28,
      "variants": [
        "What kind of play would you enjoy most with your dog?",
        "What kind of play sounds most fun for you?",
        "When it’s playtime, what do you want from your dog?",
        "Pick your play vibe: chill, moderate, fetch, or turbo?"
      ],
      "options": [
        {
          "option_id": "Q28_A",
          "label": "A",
          "next": 29
        },
        {
          "option_id": "Q28_B",
          "label": "B",
          "next": 29
        },
        {
          "option_id": "Q28_C",
          "label": "C",
          "next": 29
        },
        {
          "option_id": "Q28_D",
          "label": "D",
          "next": 29
        }
      ]
    },
    {
      "question_id": "Q29",
      "node_id": 29,
      "order": 29,
      "variants": [
        "How often will this dog be around children in daily life or frequent visits?",
        "How kid-heavy is your dog's world likely to be?",
        "Thinking about your real life, how often will children be part of the dog's routine?",
        "Kids in the picture: almost never, sometimes, often, or every day?"
      ],
      "options": [
        {
          "option_id": "Q29_A",
          "label": "A",
          "next": 33
        },
        {
          "option_id": "Q29_B",
          "label": "B",
          "next": 30
        },
        {
          "option_id": "Q29_C",
          "label": "C",
          "next": 30
        },
        {
          "option_id": "Q29_D",
          "label": "D",
          "next": 30
        }
      ]
    },
    {
      "question_id": "Q30",
      "node_id": 30,
      "order": 30,
      "variants": [
        "If kids are around, what does your household feel like most days?",
        "If kids are around, what’s the ‘volume level’ of your home most days?",
        "With kids in the picture, how calm or chaotic is daily life?",
        "Kid-house vibe: calm, normal busy, or wild?"
      ],
      "options": [
        {
          "option_id": "Q30_A",
          "label": "A",
          "next": 31
        },
        {
          "option_id": "Q30_B",
          "label": "B",
          "next": 31
        },
        {
          "option_id": "Q30_C",
          "label": "C",
          "next": 31
        },
        {
          "option_id": "Q30_D",
          "label": "D",
          "next": 31
        }
      ]
    },
    {
      "question_id": "Q31",
      "node_id": 31,
      "order": 31,
      "variants": [
        "Many young dogs mouth hands/clothes while learning (not aggression). How okay are you with that phase?",
        "Puppy mouthing happens while they learn—how would that fit for you?",
        "Imagine a young dog grabs your sleeve or hand during play sometimes—what’s your reaction?",
        "Puppy shark phase: are you ready for it?"
      ],
      "options": [
        {
          "option_id": "Q31_A",
          "label": "A",
          "next": 32
        },
        {
          "option_id": "Q31_B",
          "label": "B",
          "next": 32
        },
        {
          "option_id": "Q31_C",
          "label": "C",
          "next": 32
        },
        {
          "option_id": "Q31_D",
          "label": "D",
          "next": 32
        }
      ]
    },
    {
      "question_id": "Q32",
      "node_id": 32,
      "order": 32,
      "variants": [
        "With kids around, how strict should we be about kid-friendliness?",
        "If kids are part of your world, how much should we prioritize a kid-friendly temperament?",
        "Imagine a normal day with kids—how picky do we need to be about patience and gentleness?",
        "Kids + dog: how high’s the bar?"
      ],
      "options": [
        {
          "option_id": "Q32_A",
          "label": "A",
          "next": 33
        },
        {
          "option_id": "Q32_B",
          "label": "B",
          "next": 33
        },
        {
          "option_id": "Q32_C",
          "label": "C",
          "next": 33
        },
        {
          "option_id": "Q32_D",
          "label": "D",
          "next": 33
        }
      ]
    },
    {
      "question_id": "Q33",
      "node_id": 33,
      "order": 33,
      "variants": [
        "What other animals, if any, would this dog live with?",
        "Who else lives in the home that this dog would need to coexist with?",
        "What pets would this dog be sharing space with day to day?",
        "Resident-animal check: who else is in the household?"
      ],
      "options": [
        {
          "option_id": "Q33_A",
          "label": "A",
          "next": 34
        },
        {
          "option_id": "Q33_B",
          "label": "B",
          "next": 34
        },
        {
          "option_id": "Q33_C",
          "label": "C",
          "next": 35
        },
        {
          "option_id": "Q33_D",
          "label": "D",
          "next": 36
        }
      ]
    },
    {
      "question_id": "Q34",
      "node_id": 34,
      "order": 34,
      "variants": [
        "How much will your dog be around OTHER DOGS (friends’ dogs, parks, walks)?",
        "How often will your dog interact with other dogs (parks, friends, walks)?",
        "Dog social life: how much time around other dogs will happen?",
        "Other-dog exposure: a lot or hardly ever?"
      ],
      "options": [
        {
          "option_id": "Q34_A",
          "label": "A",
          "next": 37
        },
        {
          "option_id": "Q34_B",
          "label": "B",
          "next": 37
        },
        {
          "option_id": "Q34_C",
          "label": "C",
          "next": 37
        },
        {
          "option_id": "Q34_D",
          "label": "D",
          "next": 37
        }
      ]
    },
    {
      "question_id": "Q35",
      "node_id": 35,
      "order": 35,
      "variants": [
        "Cats/small pets at home: how strict should we be about calm, safe compatibility?",
        "If you have cats or small pets, how much should we prioritize ‘no chasing’ and good manners?",
        "Imagine your dog sharing space with a cat—how important is peaceful coexistence?",
        "Cat test: how important is chill coexistence?"
      ],
      "options": [
        {
          "option_id": "Q35_A",
          "label": "A",
          "next": 37
        },
        {
          "option_id": "Q35_B",
          "label": "B",
          "next": 37
        },
        {
          "option_id": "Q35_C",
          "label": "C",
          "next": 37
        },
        {
          "option_id": "Q35_D",
          "label": "D",
          "next": 37
        }
      ]
    },
    {
      "question_id": "Q36",
      "node_id": 36,
      "order": 36,
      "variants": [
        "If you have both dogs AND cats/small pets, what’s the #1 priority?",
        "In a mixed-pet home, what matters most to you?",
        "If you have both dogs and cats/small pets, what’s the #1 safety/fit priority?",
        "Mixed-pet juggling: what’s your top priority?"
      ],
      "options": [
        {
          "option_id": "Q36_A",
          "label": "A",
          "next": 37
        },
        {
          "option_id": "Q36_B",
          "label": "B",
          "next": 37
        },
        {
          "option_id": "Q36_C",
          "label": "C",
          "next": 37
        },
        {
          "option_id": "Q36_D",
          "label": "D",
          "next": 37
        }
      ]
    },
    {
      "question_id": "Q37",
      "node_id": 37,
      "order": 37,
      "variants": [
        "How would you feel about a dog that REALLY wants to chase squirrels/rabbits on walks?",
        "On walks, if your dog locks onto squirrels like it’s a full-time job, how do you feel?",
        "Imagine constant ‘chase mode’ on walks—love it or hate it?",
        "Squirrel radar: would that be annoying or awesome?"
      ],
      "options": [
        {
          "option_id": "Q37_A",
          "label": "A",
          "next": 38
        },
        {
          "option_id": "Q37_B",
          "label": "B",
          "next": 38
        },
        {
          "option_id": "Q37_C",
          "label": "C",
          "next": 38
        },
        {
          "option_id": "Q37_D",
          "label": "D",
          "next": 38
        }
      ]
    },
    {
      "question_id": "Q38",
      "node_id": 38,
      "order": 38,
      "variants": [
        "In your lifestyle, how important is it that your dog comes back reliably when called (in safe areas)?",
        "How important is it that your dog comes back when called (in safe spots)?",
        "In safe areas, do you want off-leash reliability or will you keep a leash on?",
        "Recall question: ‘Come!’—how much do you need that to work?"
      ],
      "options": [
        {
          "option_id": "Q38_A",
          "label": "A",
          "next": 39
        },
        {
          "option_id": "Q38_B",
          "label": "B",
          "next": 39
        },
        {
          "option_id": "Q38_C",
          "label": "C",
          "next": 39
        },
        {
          "option_id": "Q38_D",
          "label": "D",
          "next": 39
        }
      ]
    },
    {
      "question_id": "Q39",
      "node_id": 39,
      "order": 39,
      "variants": [
        "If a door/gate was accidentally left open, how serious would it be if your dog took off exploring?",
        "If a door accidentally opens, how risky is it if your dog wanders off?",
        "Oops—gate left open. How important is it that the dog stays close anyway?",
        "Escape test: gate open—panic or shrug?"
      ],
      "options": [
        {
          "option_id": "Q39_A",
          "label": "A",
          "next": 40
        },
        {
          "option_id": "Q39_B",
          "label": "B",
          "next": 40
        },
        {
          "option_id": "Q39_C",
          "label": "C",
          "next": 40
        },
        {
          "option_id": "Q39_D",
          "label": "D",
          "next": 40
        }
      ]
    },
    {
      "question_id": "Q40",
      "node_id": 40,
      "order": 40,
      "variants": [
        "In public, what vibe do you want your dog to have with strangers?",
        "When you’re out and about, do you want your dog to love strangers or keep to itself?",
        "Public outings: friendly greeter or polite observer?",
        "Stranger style: social butterfly or introvert?"
      ],
      "options": [
        {
          "option_id": "Q40_A",
          "label": "A",
          "next": 41
        },
        {
          "option_id": "Q40_B",
          "label": "B",
          "next": 41
        },
        {
          "option_id": "Q40_C",
          "label": "C",
          "next": 41
        },
        {
          "option_id": "Q40_D",
          "label": "D",
          "next": 41
        }
      ]
    },
    {
      "question_id": "Q41",
      "node_id": 41,
      "order": 41,
      "variants": [
        "At home, when something unusual happens outside, what do you want your dog to do?",
        "When there is a delivery, noise, or someone outside, what reaction do you want from your dog?",
        "If your dog notices something unexpected around the home, what is the ideal response?",
        "Home-alert preference: stay quiet, alert once, clearly alert, or be very watchful?"
      ],
      "options": [
        {
          "option_id": "Q41_A",
          "label": "A",
          "next": 43
        },
        {
          "option_id": "Q41_B",
          "label": "B",
          "next": 43
        },
        {
          "option_id": "Q41_C",
          "label": "C",
          "next": 42
        },
        {
          "option_id": "Q41_D",
          "label": "D",
          "next": 42
        }
      ]
    },
    {
      "question_id": "Q42",
      "node_id": 42,
      "order": 42,
      "variants": [
        "If you want a protective dog, what level feels appropriate for your household and experience?",
        "If you want protectiveness, what level fits your situation?",
        "How ‘serious’ should your dog be about guarding?",
        "Protection dial: light, medium, high, or pro-level?"
      ],
      "options": [
        {
          "option_id": "Q42_A",
          "label": "A",
          "next": 43
        },
        {
          "option_id": "Q42_B",
          "label": "B",
          "next": 43
        },
        {
          "option_id": "Q42_C",
          "label": "C",
          "next": 43
        },
        {
          "option_id": "Q42_D",
          "label": "D",
          "next": 43
        }
      ]
    },
    {
      "question_id": "Q43",
      "node_id": 43,
      "order": 43,
      "variants": [
        "When approved guests come over, what should your dog ideally do?",
        "When friends come over (that you approve), how should your dog behave?",
        "Guests arrive: immediate bestie or slow warm-up?",
        "Party manners: what should your dog do when visitors come in?"
      ],
      "options": [
        {
          "option_id": "Q43_A",
          "label": "A",
          "next": 44
        },
        {
          "option_id": "Q43_B",
          "label": "B",
          "next": 44
        },
        {
          "option_id": "Q43_C",
          "label": "C",
          "next": 44
        },
        {
          "option_id": "Q43_D",
          "label": "D",
          "next": 44
        }
      ]
    },
    {
      "question_id": "Q44",
      "node_id": 44,
      "order": 44,
      "variants": [
        "Dog hair reality: which statement is closest to you?",
        "How do you feel about dog hair on clothes and furniture?",
        "Be honest: are you okay with shedding?",
        "Lint roller test: how much hair can you tolerate?"
      ],
      "options": [
        {
          "option_id": "Q44_A",
          "label": "A",
          "next": 45
        },
        {
          "option_id": "Q44_B",
          "label": "B",
          "next": 45
        },
        {
          "option_id": "Q44_C",
          "label": "C",
          "next": 45
        },
        {
          "option_id": "Q44_D",
          "label": "D",
          "next": 45
        }
      ]
    },
    {
      "question_id": "Q45",
      "node_id": 45,
      "order": 45,
      "variants": [
        "Grooming reality: what’s your comfort level with coat care?",
        "How much brushing/grooming are you willing to do (or pay for)?",
        "Coat care: how much effort are you okay with?",
        "Grooming level: low, weekly, frequent, or pro salons?"
      ],
      "options": [
        {
          "option_id": "Q45_A",
          "label": "A",
          "next": 46
        },
        {
          "option_id": "Q45_B",
          "label": "B",
          "next": 46
        },
        {
          "option_id": "Q45_C",
          "label": "C",
          "next": 46
        },
        {
          "option_id": "Q45_D",
          "label": "D",
          "next": 46
        }
      ]
    },
    {
      "question_id": "Q46",
      "node_id": 46,
      "order": 46,
      "variants": [
        "Drool/slobber: how would that fit your home and preferences?",
        "How do you feel about slobber (water drips, drooly kisses)?",
        "Drool level: dealbreaker or no big deal?",
        "Slobber factor: how much can your home handle?"
      ],
      "options": [
        {
          "option_id": "Q46_A",
          "label": "A",
          "next": 47
        },
        {
          "option_id": "Q46_B",
          "label": "B",
          "next": 47
        },
        {
          "option_id": "Q46_C",
          "label": "C",
          "next": 47
        },
        {
          "option_id": "Q46_D",
          "label": "D",
          "next": 47
        }
      ]
    },
    {
      "question_id": "Q47",
      "node_id": 47,
      "order": 47,
      "variants": [
        "Dog smell & bathing: which is most true for you?",
        "How sensitive are you to ‘dog smell’ and bathing needs?",
        "Some dogs need more bathing/odor management—how do you feel about that?",
        "Odor check: are you a ‘fresh sheets’ person or pretty relaxed?"
      ],
      "options": [
        {
          "option_id": "Q47_A",
          "label": "A",
          "next": 48
        },
        {
          "option_id": "Q47_B",
          "label": "B",
          "next": 48
        },
        {
          "option_id": "Q47_C",
          "label": "C",
          "next": 48
        },
        {
          "option_id": "Q47_D",
          "label": "D",
          "next": 48
        }
      ]
    },
    {
      "question_id": "Q48",
      "node_id": 48,
      "order": 48,
      "variants": [
        "Allergies: do you need us to prioritize more allergy-friendly breeds? (No breed is 100% hypoallergenic.)",
        "Any allergy concerns we should plan around? (We’ll lean toward more allergy-friendly breeds.)",
        "If someone is sensitive to dander, how important is choosing a more allergy-friendly breed?",
        "Allergy question: how important is ‘low-allergen’?"
      ],
      "options": [
        {
          "option_id": "Q48_A",
          "label": "A",
          "next": 49
        },
        {
          "option_id": "Q48_B",
          "label": "B",
          "next": 49
        },
        {
          "option_id": "Q48_C",
          "label": "C",
          "next": 49
        },
        {
          "option_id": "Q48_D",
          "label": "D",
          "next": 49
        }
      ]
    },
    {
      "question_id": "Q49",
      "node_id": 49,
      "order": 49,
      "variants": [
        "How much do you want to prioritize a breed that tends to be more robust/healthy overall?",
        "How much do you want to prioritize a breed that’s generally hardy/low-issue?",
        "How important is ‘overall robustness’ (fewer health headaches) to you?",
        "Health priority: is ‘hardy’ a must or just a bonus?"
      ],
      "options": [
        {
          "option_id": "Q49_A",
          "label": "A",
          "next": 50
        },
        {
          "option_id": "Q49_B",
          "label": "B",
          "next": 50
        },
        {
          "option_id": "Q49_C",
          "label": "C",
          "next": 50
        },
        {
          "option_id": "Q49_D",
          "label": "D",
          "next": 50
        }
      ]
    },
    {
      "question_id": "Q50",
      "node_id": 50,
      "order": 50,
      "variants": [
        "If two breeds score similarly, which direction should break the tie?",
        "If we end up with two strong matches, what type of dog should we lean toward?",
        "As a final tie-breaker, which overall fit matters most to you?",
        "Final nudge: if it's close, which direction should win?"
      ],
      "options": [
        {
          "option_id": "Q50_A",
          "label": "A",
          "next": "RESULT"
        },
        {
          "option_id": "Q50_B",
          "label": "B",
          "next": "RESULT"
        },
        {
          "option_id": "Q50_C",
          "label": "C",
          "next": "RESULT"
        },
        {
          "option_id": "Q50_D",
          "label": "D",
          "next": "RESULT"
        }
      ]
    }
  ]
};
  root.DOG_BREED_QUIZ_COPY_ANSWERS_V2 = {
  "schema": "dog_quiz_copy_answers",
  "schema_version": 1,
  "id": "dog-breed-quiz-copy-answers-v2",
  "language": "en",
  "variant_styles": [
    {
      "index": 0,
      "id": "clear",
      "name": "Clear",
      "description": "Direct, plain-language wording."
    },
    {
      "index": 1,
      "id": "friendly",
      "name": "Friendly",
      "description": "Warm, conversational wording."
    },
    {
      "index": 2,
      "id": "scenario",
      "name": "Scenario",
      "description": "A quick real-life scenario to help beginners answer."
    },
    {
      "index": 3,
      "id": "playful",
      "name": "Playful",
      "description": "Light, fun phrasing that still stays clear."
    }
  ],
  "answers_by_option_id": {
    "Q01_A": [
      "Apartment or condo with shared walls",
      "Apartment or condo living, with neighbors close by",
      "Shared-wall home where noise and indoor manners matter",
      "Apartment life: tight space and nearby neighbors"
    ],
    "Q01_B": [
      "Townhouse, duplex, or close-neighbor setup",
      "Townhouse or duplex living, with people nearby",
      "Home with a bit more room, but neighbors are still close",
      "Close-neighbor setup with some extra breathing room"
    ],
    "Q01_C": [
      "House with a yard or more private space",
      "House living with a yard or more room overall",
      "More space at home, with easier outdoor access",
      "House setup with room to spread out"
    ],
    "Q01_D": [
      "Rural home or large property",
      "Rural setup or plenty of land",
      "Big outdoor space where a dog has room to move",
      "Country setup with lots of room"
    ],
    "Q02_A": [
      "Serious problem (complaints/rules/stress)",
      "That would cause issues for me (rules, neighbors, or my sanity)",
      "That would be a dealbreaker—I’d worry about complaints",
      "Uh-oh. A minute of barking would be a big problem here"
    ],
    "Q02_B": [
      "Occasional only—not often",
      "I can tolerate it sometimes, but not regularly",
      "Once in a while is okay; frequent barking isn’t",
      "A little is fine… just not all the time"
    ],
    "Q02_C": [
      "Some barking is fine",
      "I’m not too worried about a bit of barking",
      "That wouldn’t really bother me",
      "Woof happens—I can live with it"
    ],
    "Q02_D": [
      "Totally fine / no one would care",
      "That’s totally okay where I live",
      "No consequences—I’ve got plenty of tolerance for it",
      "Let ’em announce themselves—no one’s judging"
    ],
    "Q03_A": [
      "Very easy (quick trips are simple)",
      "Super easy—I’m close to an outdoor spot",
      "Quick out-and-back is no problem",
      "Easy mode: I can pop outside in a flash"
    ],
    "Q03_B": [
      "Moderate (stairs/elevator, manageable)",
      "Doable but takes a few minutes (stairs/elevator)",
      "It’s a bit of a trek, but I can manage it",
      "Not instant, but totally doable"
    ],
    "Q03_C": [
      "Hard (time-consuming to get outside)",
      "It’s annoying/time-consuming to get outside",
      "Quick breaks are tough—getting outside takes effort",
      "Hard mode: leaving the building takes time"
    ],
    "Q03_D": [
      "Very hard (needs great indoor manners)",
      "Very tough—I’d need a dog that’s content indoors",
      "Potty trips are a hassle; indoor calm is a must",
      "Very tough—this dog needs excellent indoor manners"
    ],
    "Q04_A": [
      "Mostly calm and relaxed indoors",
      "I want a chill dog at home",
      "Indoors should be mostly quiet and relaxed",
      "Couch mode, please—calm indoors"
    ],
    "Q04_B": [
      "Balanced: chill indoors after exercise",
      "Calm at home as long as we get exercise",
      "Exercise first, then relax indoors",
      "Work hard, nap hard—balanced energy"
    ],
    "Q04_C": [
      "Some indoor play and zoomies are okay",
      "A little indoor chaos is fine",
      "Some playing indoors is totally okay",
      "Zoomies happen—I can roll with it"
    ],
    "Q04_D": [
      "Busy/energetic indoors is okay",
      "High energy indoors is okay with me",
      "Even at home, I’m fine with an active dog",
      "Bring the pep—busy indoor dog is fine"
    ],
    "Q05_A": [
      "Quiet is important—I’d dislike that",
      "That would annoy me; I want a quiet dog",
      "I’d rather avoid that kind of barking",
      "Nope—passerby barking would stress me out"
    ],
    "Q05_B": [
      "Occasional is okay if it settles",
      "A little is fine, but it should stop quickly",
      "Okay sometimes—just not nonstop",
      "One or two barks? Fine. A concert? No."
    ],
    "Q05_C": [
      "I like some alerting (watchdog vibe)",
      "I don’t mind a little ‘heads up’ barking",
      "A bit of alerting is a plus for me",
      "A little doorbell security system sounds good"
    ],
    "Q05_D": [
      "Barking doesn’t bother me",
      "It wouldn’t bother me much",
      "I’m okay with a barky dog",
      "Bark away—doesn’t phase me"
    ],
    "Q06_A": [
      "Secure fenced yard",
      "I have a fenced yard—easy outdoor breaks",
      "Secure yard access—dog can go out safely",
      "Fenced yard life: outside is easy"
    ],
    "Q06_B": [
      "Some outdoor space (not fully secure)",
      "I’ve got a patio/partial fence—supervised outdoor time",
      "Outdoor space exists, but it’s not escape-proof",
      "Some outdoor space, but I’ll need to watch them"
    ],
    "Q06_C": [
      "No private yard; walks/parks",
      "No yard—walks and parks are the plan",
      "We’ll do outside time via walks",
      "City-style: no yard, just leashes and parks"
    ],
    "Q06_D": [
      "Limited outdoor access",
      "Outdoor time is limited; indoor calm matters",
      "Getting outside isn’t easy—dog should be content indoors",
      "Not much outside access—indoor champion needed"
    ],
    "Q07_A": [
      "Very secure fence/containment",
      "Rock-solid fence/containment",
      "Escape-proof setup (very secure)",
      "Fort Knox fence—very secure"
    ],
    "Q07_B": [
      "Somewhat secure (needs supervision)",
      "Mostly secure but I’d supervise",
      "Not perfect—dog needs watching",
      "Semi-secure: I’ll keep an eye out"
    ],
    "Q07_C": [
      "Mostly leash-only outdoors",
      "We’ll rely on leash walks, not the yard",
      "Containment isn’t the main tool—leash is",
      "Leash life: outside time is on-leash"
    ],
    "Q07_D": [
      "Large property / more freedom",
      "Plenty of land; dog may roam a bit",
      "Big space—more freedom outdoors",
      "Lots of acreage—more ‘free range’ time"
    ],
    "Q08_A": [
      "Yes—safety is critical around animals",
      "Yes—there are animals here; chasing would be dangerous",
      "Must be low-chase—safety around animals matters",
      "Big deal: we’ve got critters to protect"
    ],
    "Q08_B": [
      "Prefer a dog that isn’t chasey",
      "Some wildlife—I'd rather have low chasing",
      "I’d prefer minimal chasing behavior",
      "Wildlife exists, but I’d like a calmer chaser"
    ],
    "Q08_C": [
      "Chasing is manageable",
      "Manageable with leash/training",
      "Not ideal, but I can handle it",
      "Chasing happens—I’ll manage it"
    ],
    "Q08_D": [
      "Chasing sometimes is fine",
      "I expect some chasing—no big deal",
      "It’s okay if the dog likes to chase",
      "A little chase instinct is part of the fun"
    ],
    "Q09_A": [
      "Easygoing companion",
      "A loving companion and day-to-day sidekick",
      "Mostly a best-friend dog: close, easy to live with, and companionable",
      "Companion mode: everyday best friend"
    ],
    "Q09_B": [
      "Watchdog who alerts me",
      "A dog that notices things and lets me know",
      "Mainly an alert dog that gives me a heads-up when something is off",
      "Alert system mode: let me know what's going on"
    ],
    "Q09_C": [
      "Protective presence",
      "A dog that feels more guardian-like than purely social",
      "A dog whose presence feels protective, not just friendly",
      "Guardian vibe: protective presence"
    ],
    "Q09_D": [
      "Adventure or training partner",
      "An active dog for hikes, training, and doing things together",
      "A dog built for shared activities, outings, and purposeful work",
      "Adventure mode: train, explore, repeat"
    ],
    "Q10_A": [
      "Mostly hot/humid",
      "Mostly warm—heat is the big factor",
      "Hot summers / humid climate",
      "Heat-heavy climate (hot & humid)"
    ],
    "Q10_B": [
      "Mostly cold/snowy",
      "Cold winters—snow and chill are common",
      "We get lots of cold weather",
      "Cold-weather home (snowy seasons)"
    ],
    "Q10_C": [
      "Four seasons / big swings",
      "A bit of everything—temps change a lot",
      "Big seasonal changes (hot and cold)",
      "Weather rollercoaster: four seasons"
    ],
    "Q10_D": [
      "Mostly mild / indoors climate-controlled",
      "Pretty mild—or dog is indoors most of the time",
      "Moderate climate or climate-controlled living",
      "Mild vibes: not too hot, not too cold"
    ],
    "Q11_A": [
      "Small",
      "Small dog—easy to carry if needed",
      "I’m leaning small",
      "Small and portable (easy lift/carry)"
    ],
    "Q11_B": [
      "Medium",
      "Medium—solid but not huge",
      "Mid-size feels right",
      "Goldilocks size: medium"
    ],
    "Q11_C": [
      "Large",
      "Large dog—bring on the big pup",
      "I want a big dog",
      "Go big: large dog"
    ],
    "Q11_D": [
      "No preference",
      "Size isn’t a big deal; lifestyle matters",
      "I’ll take the best fit, any size",
      "Surprise me—pick based on fit"
    ],
    "Q12_A": [
      "Not comfortable—must be easy to manage",
      "I need a dog that’s easy to hold onto",
      "Sudden pulling would be hard for me",
      "Please keep it easy to handle"
    ],
    "Q12_B": [
      "Some pulling is okay if it improves",
      "A little pulling is fine as long as it gets better",
      "I can manage some pulling with work",
      "Okay with some tugging—training will be key"
    ],
    "Q12_C": [
      "Can manage strong pulling (gear + training)",
      "Strong pullers are okay—I’ll use gear and training",
      "I can handle a strong dog on leash",
      "Prepared for a strong pull (harness, training)"
    ],
    "Q12_D": [
      "Very comfortable with a strong dog",
      "I’m confident with a powerful dog on leash",
      "Pulling doesn’t scare me",
      "Bring it on—I can manage a strong dog"
    ],
    "Q13_A": [
      "Prefer a dog I can carry fairly easily",
      "I’d like a dog I can pick up if needed",
      "I need a ‘carryable’ size",
      "Easy-lift dog, please"
    ],
    "Q13_B": [
      "Can carry a medium dog",
      "I can manage lifting a medium-size dog",
      "Medium-lift is doable",
      "I can carry a mid-size pup if I have to"
    ],
    "Q13_C": [
      "Can lift a large dog if necessary",
      "I can lift a large dog in an emergency",
      "I’m okay with a heavy lift if needed",
      "I can handle big-dog lifting if it comes to that"
    ],
    "Q13_D": [
      "Not a concern",
      "Not worried—I’ve got help or easy access",
      "I’ve got support/equipment, so lifting isn’t an issue",
      "No worries—teamwork/ramps/zero stairs"
    ],
    "Q14_A": [
      "0–2 hours",
      "Hardly alone (0–2 hrs)",
      "Short stretches only (0–2)",
      "Almost never solo (0–2)"
    ],
    "Q14_B": [
      "2–4 hours",
      "A few hours (2–4)",
      "Moderate alone time (2–4)",
      "Some solo time (2–4)"
    ],
    "Q14_C": [
      "4–8 hours",
      "Workday-ish (4–8)",
      "Half to full workday (4–8)",
      "Long stretches (4–8)"
    ],
    "Q14_D": [
      "8+ hours",
      "Usually a full workday or longer",
      "A very long solo stretch, often eight hours or more",
      "Long-haul alone time: 8+ hours"
    ],
    "Q15_A": [
      "Home a lot / work from home",
      "I’m around most days (WFH/home a lot)",
      "Minimal alone time—I’m usually home",
      "Home-base human: I’m home often"
    ],
    "Q15_B": [
      "Reliable midday break",
      "I can consistently pop home midday",
      "Midday break is realistic most days",
      "Lunch break check-ins are doable"
    ],
    "Q15_C": [
      "Walker/daycare some days",
      "I can hire a walker or do daycare sometimes",
      "Some weekday help (walker/daycare)",
      "Backup squad: walker/daycare on some days"
    ],
    "Q15_D": [
      "No breaks (dog must be okay alone)",
      "No midday help; dog needs to handle alone time",
      "No support during the day",
      "No breaks—this dog needs solo skills"
    ],
    "Q16_A": [
      "Under 30 minutes",
      "Usually under half an hour",
      "Realistically, only short exercise sessions most weekdays",
      "Short exercise budget: under 30 minutes"
    ],
    "Q16_B": [
      "30–60 minutes",
      "About 30–60 min",
      "A solid walk/play block (30–60)",
      "Medium dose—30 to 60 minutes"
    ],
    "Q16_C": [
      "60–120 minutes",
      "1–2 hours",
      "An hour or two (60–120)",
      "Big block—60 to 120 minutes"
    ],
    "Q16_D": [
      "2+ hours",
      "Two hours or more most days",
      "A large daily exercise budget—two hours or more",
      "High-output lifestyle: 2+ hours"
    ],
    "Q17_A": [
      "Relaxed walks/sniffing",
      "Easy strolls and sniffing around",
      "Slow walks with lots of sniff breaks",
      "Sniffari walks—slow and relaxed"
    ],
    "Q17_B": [
      "Brisk walks/jogs",
      "Brisk walks or jogging together",
      "Steady-paced walks/runs",
      "Let’s get moving—brisk walks/jogs"
    ],
    "Q17_C": [
      "Hikes/adventures",
      "Hikes and longer adventures",
      "Trail time and big outings",
      "Adventure mode—hikes and exploring"
    ],
    "Q17_D": [
      "Games/training (fetch, agility-style)",
      "Games like fetch + training sessions",
      "Structured play/training (fetch, agility-style)",
      "Brain + body: games and training"
    ],
    "Q18_A": [
      "Rarely",
      "Not often—just occasionally",
      "Maybe sometimes, not regularly",
      "Low brain-game budget: rarely"
    ],
    "Q18_B": [
      "A few minutes most days",
      "Quick daily mini-sessions",
      "Short training/games most days",
      "Daily bite-size brain games"
    ],
    "Q18_C": [
      "Dedicated sessions most days",
      "Real training/enrichment most days",
      "Consistent longer sessions",
      "Dedicated brain time most days"
    ],
    "Q18_D": [
      "Advanced training/sports",
      "I love training—sports/advanced work",
      "High-level training is part of the plan",
      "Bring on the advanced stuff"
    ],
    "Q19_A": [
      "First-time primary owner",
      "This would be my first time being the main dog person",
      "New to being fully responsible for a dog's care and training",
      "First-timer as the primary dog human"
    ],
    "Q19_B": [
      "Basic experience",
      "I’ve had a dog before",
      "Some experience, nothing fancy",
      "Been there once or twice"
    ],
    "Q19_C": [
      "Very experienced",
      "I’ve had multiple dogs and trained them",
      "Comfortable owner with solid experience",
      "Experienced: multiple dogs"
    ],
    "Q19_D": [
      "Advanced or working-dog experience",
      "I have serious experience with training, sports, or working dogs",
      "High-level dog experience beyond casual ownership",
      "Advanced handler mode"
    ],
    "Q20_A": [
      "Minimal year-one behavior work",
      "I want the easiest path I can realistically get",
      "Low tolerance for chewing, jumping, barking, and management-heavy phases",
      "Easy setting: minimal behavior work"
    ],
    "Q20_B": [
      "Some work if I see progress",
      "I can do some training if it’s improving",
      "Moderate work is okay",
      "I’ll put in work—as long as it’s paying off"
    ],
    "Q20_C": [
      "Enjoy challenges",
      "I like training and don’t mind challenges",
      "Bring on the training—I'm into it",
      "I’m game for a challenge"
    ],
    "Q20_D": [
      "High-drive project dog is fine",
      "I am intentionally open to a more demanding dog",
      "I can take on a dog that needs serious training, structure, and management",
      "Hard mode: I'm okay with a project dog"
    ],
    "Q21_A": [
      "Very important—easy to train basics",
      "Very important: I want training to feel easy",
      "I need a dog that picks up basics quickly",
      "Fast learner for the basics"
    ],
    "Q21_B": [
      "Important with effort",
      "Important—I’ll train regularly",
      "I can work at it; doesn’t have to be effortless",
      "I’ll put in time for manners"
    ],
    "Q21_C": [
      "Not that important",
      "I’m okay with ‘close enough’ manners",
      "Perfection isn’t required",
      "Good enough works for me"
    ],
    "Q21_D": [
      "Advanced potential",
      "I want a dog with high training potential",
      "I’m aiming beyond the basics",
      "I want a dog that can learn the fancy stuff"
    ],
    "Q22_A": [
      "Quick response",
      "I want a quick, reliable response",
      "Ideally responds right away",
      "‘Yes, human!’ quick response"
    ],
    "Q22_B": [
      "Usually responds (maybe a reminder)",
      "Mostly responds, sometimes needs a reminder",
      "Pretty responsive, not perfect",
      "Usually listens—maybe one extra ‘hey!’"
    ],
    "Q22_C": [
      "Independent; repeating is fine",
      "I can repeat myself; independence is okay",
      "Doesn’t have to be super responsive",
      "Fine if I have to ask twice"
    ],
    "Q22_D": [
      "Prefer independent thinkers",
      "I prefer an independent dog",
      "I don’t need a super handler-focused dog",
      "A little free spirit is my style"
    ],
    "Q23_A": [
      "Lounging/cuddling",
      "Mostly cuddling on the couch",
      "Chill and snuggly",
      "Couch buddy—lounging and cuddles"
    ],
    "Q23_B": [
      "Calm with short play bursts",
      "Mostly calm, with a little play",
      "Chill, quick game, chill again",
      "Balanced: calm + mini play"
    ],
    "Q23_C": [
      "Playful indoors for a while",
      "Some indoor playtime most nights",
      "Wants to play indoors fairly often",
      "Playtime inside is part of the routine"
    ],
    "Q23_D": [
      "Very playful/active indoors",
      "High-energy evenings indoors are fine",
      "Lots of indoor play is okay",
      "Zoomies at 9pm? Bring it."
    ],
    "Q24_A": [
      "Minimal barking at the door",
      "I want a quiet doorbell moment",
      "I’d prefer little to no barking",
      "No bark concert at the door"
    ],
    "Q24_B": [
      "A little barking if it stops",
      "A couple barks is fine if it settles",
      "Okay as long as it quiets down fast",
      "Two barks max, then chill"
    ],
    "Q24_C": [
      "Barking is fine",
      "I’m okay with normal barking at the door",
      "That doesn’t bother me",
      "Doorbell barking? That’s just dog stuff"
    ],
    "Q24_D": [
      "Prefer alert barking",
      "I want a dog that lets me know someone’s there",
      "Alert barking is a plus",
      "I like a dog that announces visitors"
    ],
    "Q25_A": [
      "Dealbreaker—very low chewing risk",
      "I can’t handle destructive chewing",
      "I need minimal chewing issues",
      "Hard no on chewing damage"
    ],
    "Q25_B": [
      "Upset but I can manage",
      "I’d be upset, but I’ll manage with structure",
      "I can work through it with training/tools",
      "Not happy, but manageable with crates/training"
    ],
    "Q25_C": [
      "Annoying but manageable",
      "It would annoy me, but I can live with it",
      "Manageable if it’s occasional",
      "Annoying… but we’ll get through it"
    ],
    "Q25_D": [
      "Not a big deal",
      "I’m patient and prepared for that",
      "I can roll with it",
      "Teachable moment—no big deal"
    ],
    "Q26_A": [
      "Calm/quiet home—sensitive dogs could fit",
      "My home is pretty calm, so a sensitive dog could work",
      "Quiet vibe—sensitivity is okay",
      "We run a calm ship—sensitive dogs welcome"
    ],
    "Q26_B": [
      "Normal household noise—either is fine",
      "Average noise levels—either temperament could work",
      "Normal home sounds; I’m flexible",
      "Middle-of-the-road noise—either is fine"
    ],
    "Q26_C": [
      "Busy/loud home—need a confident dog",
      "We’re loud/busy; dog should be sturdy and confident",
      "Lots going on—sensitive dogs may struggle",
      "Chaos-friendly dog needed"
    ],
    "Q26_D": [
      "Not sure / can adapt",
      "I’m not sure; I can adapt",
      "Open to either",
      "Not picky—I’ll adjust to the dog"
    ],
    "Q27_A": [
      "Very cuddly",
      "Total cuddle bug",
      "I want a Velcro dog",
      "Attached at the hip—in a good way"
    ],
    "Q27_B": [
      "Affectionate, not clingy",
      "Sweet and affectionate, but not clingy",
      "Cuddles yes, constant contact no",
      "Affection with boundaries"
    ],
    "Q27_C": [
      "More independent",
      "More independent personality",
      "I like a dog that does its own thing",
      "Independent pal (cuddles are a bonus)"
    ],
    "Q27_D": [
      "No preference",
      "Either is fine",
      "I’m flexible",
      "Surprise me—any cuddle level"
    ],
    "Q28_A": [
      "Low-play (chill)",
      "Chill dog—low play needs",
      "Not super playful",
      "Team Chill: low-play dog"
    ],
    "Q28_B": [
      "Moderately playful",
      "Plays sometimes, not nonstop",
      "Moderate play is perfect",
      "Playful on request"
    ],
    "Q28_C": [
      "Fetch/retrieve is a plus",
      "I’d love a fetch fan",
      "Fetch and retrieve games sound great",
      "Throw ball, dog returns—yes please"
    ],
    "Q28_D": [
      "Very playful/high-drive",
      "Bring the energy—high-drive play buddy",
      "Lots of play and drive",
      "Turbo play partner—high drive"
    ],
    "Q29_A": [
      "Rarely or never around kids",
      "Kids are not really part of my day-to-day life",
      "Children would be rare or mostly absent from the dog's routine",
      "Low-kid lifestyle"
    ],
    "Q29_B": [
      "Occasional visits",
      "Kids visit sometimes",
      "A few kid visits here and there",
      "Occasional kid time"
    ],
    "Q29_C": [
      "Kids at home (school-age)",
      "Kids live with me (mostly 6+)",
      "Regular kid contact—school-age",
      "Full-time kids (school-age)"
    ],
    "Q29_D": [
      "Kids at home (toddlers)",
      "Little kids at home",
      "Toddlers/preschoolers around a lot",
      "Tiny humans included"
    ],
    "Q30_A": [
      "Calm + lots of supervision",
      "Calm household with close supervision",
      "Pretty calm and structured",
      "Calm and watchful"
    ],
    "Q30_B": [
      "Typical busy family home",
      "Normal family busy-ness",
      "Average kid energy",
      "Busy, but nothing extreme"
    ],
    "Q30_C": [
      "High-energy/chaotic",
      "High-energy house—lots of noise and motion",
      "Kids are loud/active; lots going on",
      "Chaos mode: loud and active"
    ],
    "Q30_D": [
      "Unpredictable toddler chaos",
      "Toddler chaos—need a very patient dog",
      "Unpredictable little-kid energy",
      "Toddler tornadoes—extra gentle required"
    ],
    "Q31_A": [
      "Need very low mouthiness",
      "Mouthing would stress me out—I need low",
      "I want minimal nipping",
      "Low-mouthy only, please"
    ],
    "Q31_B": [
      "Some is okay if it improves quickly",
      "A little is fine if it gets better fast",
      "Okay with some, as long as training works",
      "Some nibbling is okay—must improve quickly"
    ],
    "Q31_C": [
      "Normal puppy mouthiness is fine",
      "Typical puppy mouthing is okay",
      "I can train through normal nibbling",
      "Normal puppy phase is fine"
    ],
    "Q31_D": [
      "Totally fine—I’m experienced",
      "I’m comfortable managing it",
      "No worries—I know how to handle mouthing",
      "Bring on the puppy teeth—I can handle it"
    ],
    "Q32_A": [
      "Top-tier kid-friendly (non-negotiable)",
      "Must be excellent with kids",
      "Kid-friendly is a must-have",
      "Only the most kid-safe temperament"
    ],
    "Q32_B": [
      "Important, but not the only factor",
      "Important, but not the only thing",
      "Kid-friendly is a big factor",
      "Kid-friendly yes, but not the only checkbox"
    ],
    "Q32_C": [
      "Moderate is okay (we’ll manage)",
      "Moderate is fine; we’ll supervise/manage",
      "We can handle some training/management",
      "We’ll manage interactions—moderate okay"
    ],
    "Q32_D": [
      "Kid contact will be limited",
      "Kids and dog will be kept mostly separate",
      "Limited kid interaction",
      "Kid time is limited—so moderate is okay"
    ],
    "Q33_A": [
      "No other pets",
      "No other animals at home",
      "It’ll be the only pet",
      "Solo-pet household"
    ],
    "Q33_B": [
      "Other dog(s)",
      "Yes—there are dog(s) at home",
      "Dog siblings in the house",
      "Already have one or more dogs"
    ],
    "Q33_C": [
      "Cat(s) and/or small pets",
      "Yes—cats or small pets",
      "Cats/small pets live here",
      "We’ve got cats and/or small critters"
    ],
    "Q33_D": [
      "Dogs and cats/small pets",
      "Yes—dogs and cats/small pets",
      "Mixed-pet home (dogs + cats/small)",
      "Both types: dogs and cats/small pets"
    ],
    "Q34_A": [
      "A lot—very dog-social",
      "A lot (dog parks/group play)",
      "Frequent dog hangouts—needs to be social",
      "Big dog-social scene"
    ],
    "Q34_B": [
      "Regularly—friendly with most",
      "Regular contact—should be friendly",
      "Often around dogs—generally friendly",
      "Regular dog encounters—friendly with most"
    ],
    "Q34_C": [
      "Occasionally—neutral/selective ok",
      "Sometimes—neutral/selective is fine",
      "Occasional dog interactions",
      "Some dog contact—neutral is okay"
    ],
    "Q34_D": [
      "Rarely—prefer ignores dogs",
      "Rarely around dogs; prefers to ignore them",
      "Not much dog social life",
      "Low dog interaction—ignore mode"
    ],
    "Q35_A": [
      "Must be very compatible—no chasing",
      "Must be safe with cats/small pets",
      "No chasing allowed—high compatibility needed",
      "Absolute must: safe with small pets"
    ],
    "Q35_B": [
      "Compatible with management/training",
      "Should be okay with training and management",
      "Reasonably compatible; we can work on it",
      "Compatible enough—with training and boundaries"
    ],
    "Q35_C": [
      "We can manage/separate",
      "We can manage separation if needed",
      "Doesn’t have to be perfect—we’ll manage",
      "Not perfect is fine—we can set up gates/rooms"
    ],
    "Q35_D": [
      "Not important (fully separate)",
      "Not important—we’ll keep them separated",
      "They won’t really interact",
      "Full separation is fine"
    ],
    "Q36_A": [
      "Cat/small-pet safety first",
      "Small-pet/cat safety is top priority",
      "Must prioritize cat/small pet safety",
      "Protect the small pets first"
    ],
    "Q36_B": [
      "Dog-to-dog friendliness first",
      "Dog-dog harmony matters most",
      "Primary goal: friendly with other dogs",
      "Dog sibling compatibility comes first"
    ],
    "Q36_C": [
      "Both equally important",
      "Both matter equally",
      "Need balance: good with dogs and cats",
      "Equal priority: dog + cat compatibility"
    ],
    "Q36_D": [
      "I can manage separation/training",
      "I can manage with gates/training",
      "I’m okay handling separation if needed",
      "I’ll manage the setup—separation/training is fine"
    ],
    "Q37_A": [
      "Hate it—low chase preferred",
      "I want minimal chasing on walks",
      "That would frustrate me; low chase please",
      "No thanks—keep the chase instinct low"
    ],
    "Q37_B": [
      "Some chasing is okay (manageable)",
      "Okay with some chasing if controllable",
      "A little is fine; I can manage it",
      "Some chase is okay—as long as I can handle it"
    ],
    "Q37_C": [
      "Fine with strong chase instinct",
      "I can manage a strong chase instinct",
      "Chasey is okay; I’ll handle it",
      "Bring on the chase—I can manage"
    ],
    "Q37_D": [
      "I like high drive/chase",
      "I actually want high drive for sports/activity",
      "High chase drive is a plus for me",
      "Yes please—high drive is fun"
    ],
    "Q38_A": [
      "Very important—reliable recall",
      "Must come back reliably",
      "Recall is a big priority",
      "High priority: comes when called"
    ],
    "Q38_B": [
      "Nice to have (use long lines)",
      "Nice, but I can use a long line",
      "Helpful but not required",
      "Great, but I’ll manage with a long lead"
    ],
    "Q38_C": [
      "Not important—mostly leashed",
      "Mostly leashed; recall isn’t critical",
      "Not a priority",
      "Leash-first lifestyle—recall not crucial"
    ],
    "Q38_D": [
      "Never off-leash",
      "Never off-leash; always contained",
      "Off-leash isn’t part of my plan",
      "No off-leash—ever"
    ],
    "Q39_A": [
      "Very serious—must stay close",
      "Very serious; I need a dog that sticks nearby",
      "I can’t risk a runner—staying close matters",
      "Panic level high—dog must stay close"
    ],
    "Q39_B": [
      "Some risk is okay",
      "Some risk is okay",
      "Not ideal, but I can manage some wander",
      "I can handle a bit of risk"
    ],
    "Q39_C": [
      "Manageable with training/containment",
      "Not ideal, but I can manage it",
      "I’ll use training/tools to prevent it",
      "I can manage with training and good gates"
    ],
    "Q39_D": [
      "Not a concern",
      "Not really a concern for me",
      "My setup makes it not a big worry",
      "Shrug—my setup is secure"
    ],
    "Q40_A": [
      "Super friendly with strangers",
      "Loves people and wants to say hi",
      "Very social with strangers",
      "Social butterfly—loves meeting people"
    ],
    "Q40_B": [
      "Polite/friendly, not overly social",
      "Friendly but not everyone’s best friend",
      "Polite greetings, then moves on",
      "Friendly, but not clingy with strangers"
    ],
    "Q40_C": [
      "Neutral/reserved is fine",
      "Neutral—doesn’t need to greet everyone",
      "Reserved but calm",
      "More of a ‘mind my business’ vibe"
    ],
    "Q40_D": [
      "Aloof/wary is okay",
      "I’m okay with a more aloof dog",
      "Not super social; cautious is fine",
      "Introvert dog—aloof/wary is okay"
    ],
    "Q41_A": [
      "Stay quiet and relaxed",
      "I prefer a dog that notices things without making much of it",
      "The ideal response is calm, low-reactive, and quiet",
      "Quiet-home mode"
    ],
    "Q41_B": [
      "Alert once, then settle",
      "One quick alert, then calm down",
      "Notice it but settle fast",
      "One ‘hey!’ then back to chill"
    ],
    "Q41_C": [
      "Clearly alert me (watchdog)",
      "I want clear alerting when something’s off",
      "Should bark/alert so I know",
      "Let me know—watchdog mode"
    ],
    "Q41_D": [
      "Very vigilant and serious",
      "I am comfortable with a highly watchful dog",
      "A more intense, security-minded response is acceptable to me",
      "Serious guard-dog energy"
    ],
    "Q42_A": [
      "Light deterrent",
      "Light protection—mainly presence and alerting",
      "Just a little deterrent",
      "Light ‘don’t mess with us’ vibe"
    ],
    "Q42_B": [
      "Moderate guardian",
      "Moderate—protective but still manageable",
      "Guardian-ish, but not extreme",
      "Middle setting: protective but controllable"
    ],
    "Q42_C": [
      "High protectiveness",
      "High protectiveness—serious guardian",
      "More intense protection drive",
      "Serious guardian level"
    ],
    "Q42_D": [
      "Working-level protection",
      "Working-level protection (I’m experienced)",
      "Very high—pro/working style",
      "Pro setting: working-level protection"
    ],
    "Q43_A": [
      "Greet happily right away",
      "Friendly greeter right away",
      "Happy hello and social",
      "Instant friend-maker"
    ],
    "Q43_B": [
      "Greet calmly after a moment",
      "Calm greeting after a brief warm-up",
      "Takes a moment, then says hi",
      "Slow hello, calm vibe"
    ],
    "Q43_C": [
      "Neutral/reserved but safe",
      "Reserved is fine as long as it’s safe",
      "Polite distance okay",
      "Keeps to itself, but safe"
    ],
    "Q43_D": [
      "Strong boundaries (I’ll manage)",
      "I’m okay managing/containing at first",
      "Strong boundaries—guest management needed",
      "Crate/behind-gate routine when guests arrive"
    ],
    "Q44_A": [
      "Minimal shedding",
      "I really want low shedding",
      "Hair everywhere would drive me nuts",
      "Minimal shedding, please"
    ],
    "Q44_B": [
      "Some shedding is manageable",
      "A bit of shedding is fine",
      "Some hair is okay with regular cleaning",
      "Manageable shedding is okay"
    ],
    "Q44_C": [
      "Shedding doesn’t bother me much",
      "Shedding doesn’t really bother me",
      "I’m pretty relaxed about dog hair",
      "Hair? Meh—I can handle it"
    ],
    "Q44_D": [
      "Doesn’t matter",
      "Shedding is no issue for me",
      "Bring on the fur",
      "I accept the fur as part of life"
    ],
    "Q45_A": [
      "Minimal grooming",
      "Low-maintenance coat only",
      "I want easy coat care",
      "Minimal brushing please"
    ],
    "Q45_B": [
      "Weekly brushing is fine",
      "Once-a-week brushing works",
      "Weekly grooming is doable",
      "Weekly brush routine is okay"
    ],
    "Q45_C": [
      "Frequent brushing is fine",
      "I can brush often",
      "Frequent brushing doesn’t bother me",
      "Brush a lot? Fine by me"
    ],
    "Q45_D": [
      "Professional grooming is fine",
      "I’m okay paying for pro grooming",
      "Haircuts/salon visits are okay",
      "Groomer on speed dial—pro grooming is fine"
    ],
    "Q46_A": [
      "Want very little drool",
      "Drool bothers me—keep it minimal",
      "Low drool is important",
      "No slobber, please"
    ],
    "Q46_B": [
      "A little drool is okay",
      "Some drool is fine",
      "A bit is okay",
      "A little slobber won’t hurt"
    ],
    "Q46_C": [
      "Drool doesn’t bother me",
      "Drool is not a problem",
      "I’m fine with drooly dogs",
      "Slobber? Doesn’t phase me"
    ],
    "Q46_D": [
      "Don’t mind lots of drool",
      "I don’t mind lots of drool",
      "Drooly kisses are okay",
      "Bring on the slobber—I'm fine with it"
    ],
    "Q47_A": [
      "Sensitive to odor",
      "I’m sensitive—want low odor",
      "Prefer minimal dog smell and less bathing",
      "Low-odor lifestyle, please"
    ],
    "Q47_B": [
      "Some odor is okay",
      "Some smell is okay; I can manage",
      "Okay with moderate bathing needs",
      "I can handle some dog smell"
    ],
    "Q47_C": [
      "Normal dog smell is fine",
      "Not very sensitive—normal is okay",
      "Regular dog smell doesn’t bother me",
      "Dog smell is fine—I’m chill"
    ],
    "Q47_D": [
      "Doesn’t matter",
      "Odor doesn’t bother me",
      "Not concerned at all",
      "Smell? Not on my worry list"
    ],
    "Q48_A": [
      "Must-have allergy-friendly",
      "Yes—this is non-negotiable",
      "Allergy-friendly is required",
      "Must-have: allergy-friendly"
    ],
    "Q48_B": [
      "Strongly prefer",
      "Strong preference for allergy-friendly",
      "I’d really like an allergy-friendly option",
      "Prefer it a lot"
    ],
    "Q48_C": [
      "Nice to have",
      "Nice bonus, not required",
      "Would be helpful but not mandatory",
      "Optional: nice-to-have"
    ],
    "Q48_D": [
      "No allergy concerns",
      "No allergies here",
      "Not an issue for us",
      "No need to prioritize allergies"
    ],
    "Q49_A": [
      "Extremely important",
      "Top priority: robust/healthy",
      "I want the hardiest option possible",
      "Health first—robust is a must"
    ],
    "Q49_B": [
      "Important, but not the only factor",
      "Important, but balance with other traits",
      "I care a lot, but it’s not everything",
      "Important—just not the only thing"
    ],
    "Q49_C": [
      "Somewhat important",
      "Somewhat—nice to have",
      "It matters a little",
      "Somewhat important, but flexible"
    ],
    "Q49_D": [
      "Not a priority",
      "Not a big priority for me",
      "I can manage higher needs if needed",
      "Not a deciding factor"
    ],
    "Q50_A": [
      "Easygoing companion / beginner-friendly",
      "Lean toward the easygoing, easier-to-live-with option",
      "If it's close, favor the companion who fits smoothly into everyday life",
      "Tie goes to the chill companion"
    ],
    "Q50_B": [
      "Family and kids first",
      "Lean toward the better family-life fit",
      "If it's close, favor the breed that is strongest around family and kids",
      "Tie goes to the family pick"
    ],
    "Q50_C": [
      "Adventure / training partner",
      "Lean toward the more active, trainable partner",
      "If it's close, favor the dog built for activities, outings, and training",
      "Tie goes to the adventure buddy"
    ],
    "Q50_D": [
      "Watchdog / protective presence",
      "Lean toward the more watchful or protective option",
      "If it's close, favor the dog with more alerting or guardian qualities",
      "Tie goes to the watchful one"
    ]
  }
};

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      questions: root.DOG_BREED_QUIZ_COPY_QUESTIONS_V2,
      answers: root.DOG_BREED_QUIZ_COPY_ANSWERS_V2
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);


/*
  ML-9665 Dog Breed Quiz (Spec v4 + runtimePatch4)
  - Renders the routed 50-node dog-breed quiz (questions/answers stored in a metaobject JSON field).
  - Scores dog_breed metaobjects using the v4 progression + scoring spec.

  Notes:
  - The quiz routing comes from the questions JSON (option.next).
  - The scoring weights/prefs are embedded below as DBQ_SPEC_V4 (generated from the provided v4 spec).
  - This file is safe to load multiple times; it installs once.
*/

(() => {
  'use strict';

  const GLOBAL_FLAG = 'ML9665DogBreedQuizLoaded';
  if (window[GLOBAL_FLAG]) return;
  window[GLOBAL_FLAG] = true;

  const ROOT_SELECTOR = '[data-ml-9665-dbq-root]';

  const VARIANT_TO_INDEX = {
    clear: 0,
    friendly: 1,
    scenario: 2,
    playful: 3,
    random: -1
  };

  // Quiz flow
  // - short: curated, max-questions flow to prevent burnout (default)
  // - full: follow the `option.next` graph from the questions JSON
  const DEFAULT_FLOW_MODE = 'short';
  const DEFAULT_MAX_QUESTIONS = 16;
  // Target run length (the router aims for this when it can, but never exceeds maxQuestions).
  const DEFAULT_TARGET_QUESTIONS = 14;
  // UX spec: always show the top 5 matches (also used for scoring top-k selection).
  const DEFAULT_RESULTS_COUNT = 5;

  // -----------------------------
  // Spec (v4)
  // -----------------------------
  // Minified JSON; valid JS object literal.
  const DBQ_SPEC_V4 = {"id":"dog_breed_quiz_tree_50_no_finance_weighted_v4_constraint_patch3","schema":"3.1.3.1.0","start_node_id":1,"result_token":"RESULT","scoring_engine":{"tie_break":{"epsilon":0.5,"default_metric":"computed.overall_rankings.overall_companion"}},"nodes":[{"id":1,"key":"Q01","title":"What kind of home will your dog live in most of the time?","weight":0.8,"section":{"id":"home_environment","name":"Home & environment"},"scoring_traits":[{"field":"computed.grades.apartment_dog.grade_0_100","scale":"grade_0_100","importance":0.461538,"prefs":{"Q01_A":{"type":"target","target":90},"Q01_B":{"type":"target","target":70},"Q01_C":null,"Q01_D":null}},{"field":"ratings_1_to_10.apartment_adaptability","scale":"rating_1_10","importance":0.307692,"prefs":{"Q01_A":{"type":"target","target":8},"Q01_B":{"type":"target","target":7},"Q01_C":null,"Q01_D":null}},{"field":"computed.midpoints.weight_lbs_mid","scale":"lbs","importance":0.23077,"prefs":{"Q01_A":{"type":"range","min":5,"max":55,"pad":30},"Q01_B":{"type":"range","min":10,"max":80,"pad":40},"Q01_C":null,"Q01_D":null}}]},{"id":2,"key":"Q02","title":"If your dog barked for 30–60 seconds when it heard hallway/neighbor noise, what would happen?","weight":2.5,"section":{"id":"home_environment","name":"Home & environment"},"scoring_traits":[{"field":"ratings_1_to_10.barking_vocalness","scale":"rating_1_10","importance":0.7,"prefs":{"Q02_A":{"type":"range","min":0,"max":3,"pad":3},"Q02_B":{"type":"range","min":0,"max":5,"pad":3},"Q02_C":{"type":"range","min":0,"max":7,"pad":3},"Q02_D":null}},{"field":"ratings_1_to_10.watchdog_alertness","scale":"rating_1_10","importance":0.3,"prefs":{"Q02_A":{"type":"range","min":0,"max":3,"pad":3},"Q02_B":{"type":"range","min":0,"max":5,"pad":3},"Q02_C":{"type":"range","min":0,"max":7,"pad":3},"Q02_D":null}},{"field":"ratings_1_to_10.barking_vocalness","scale":"rating_1_10","importance":0.0,"prefs":{"Q02_A":{"type":"filter_max","max":4,"penalty":300},"Q02_B":null,"Q02_C":null,"Q02_D":null}}]},{"id":3,"key":"Q03","title":"On a typical day, how easy is it for you to take a dog out for quick potty breaks and short walks?","weight":1.5,"section":{"id":"home_environment","name":"Home & environment"},"scoring_traits":[{"field":"ratings_1_to_10.tolerance_for_being_alone","scale":"rating_1_10","importance":0.45,"prefs":{"Q03_A":null,"Q03_B":{"type":"range","min":5,"max":10,"pad":4},"Q03_C":{"type":"range","min":6,"max":10,"pad":4},"Q03_D":{"type":"range","min":7,"max":10,"pad":4}}},{"field":"computed.calmness.grade_0_100","scale":"grade_0_100","importance":0.35,"prefs":{"Q03_A":null,"Q03_B":{"type":"range","min":55,"max":100,"pad":30},"Q03_C":{"type":"range","min":65,"max":100,"pad":30},"Q03_D":{"type":"range","min":75,"max":100,"pad":30}}},{"field":"ratings_1_to_10.daily_exercise_needs","scale":"rating_1_10","importance":0.2,"prefs":{"Q03_A":null,"Q03_B":{"type":"range","min":0,"max":6,"pad":3},"Q03_C":{"type":"range","min":0,"max":5,"pad":3},"Q03_D":{"type":"range","min":0,"max":4,"pad":3}}}]},{"id":4,"key":"Q04","title":"Inside your home, what kind of dog ‘energy’ would feel best to live with?","weight":2.0,"section":{"id":"home_environment","name":"Home & environment"},"scoring_traits":[{"field":"computed.calmness.grade_0_100","scale":"grade_0_100","importance":0.5,"prefs":{"Q04_A":{"type":"target","target":85},"Q04_B":{"type":"target","target":70},"Q04_C":{"type":"target","target":50},"Q04_D":{"type":"target","target":35}}},{"field":"ratings_1_to_10.energy_level","scale":"rating_1_10","importance":0.3,"prefs":{"Q04_A":{"type":"target","target":3},"Q04_B":{"type":"target","target":5},"Q04_C":{"type":"target","target":7},"Q04_D":{"type":"target","target":9}}},{"field":"ratings_1_to_10.playfulness","scale":"rating_1_10","importance":0.2,"prefs":{"Q04_A":{"type":"target","target":3},"Q04_B":{"type":"target","target":5},"Q04_C":{"type":"target","target":7},"Q04_D":{"type":"target","target":9}}}]},{"id":5,"key":"Q05","title":"With neighbors close by, how would you feel about a dog that barks when someone passes your home?","weight":2.0,"section":{"id":"home_environment","name":"Home & environment"},"scoring_traits":[{"field":"ratings_1_to_10.barking_vocalness","scale":"rating_1_10","importance":0.7,"prefs":{"Q05_A":{"type":"range","min":0,"max":3,"pad":3},"Q05_B":{"type":"range","min":0,"max":5,"pad":3},"Q05_C":{"type":"target","target":6},"Q05_D":null}},{"field":"ratings_1_to_10.watchdog_alertness","scale":"rating_1_10","importance":0.3,"prefs":{"Q05_A":{"type":"range","min":0,"max":3,"pad":3},"Q05_B":{"type":"range","min":0,"max":5,"pad":3},"Q05_C":{"type":"target","target":6},"Q05_D":null}},{"field":"ratings_1_to_10.barking_vocalness","scale":"rating_1_10","importance":0.0,"prefs":{"Q05_A":{"type":"filter_max","max":5,"penalty":250},"Q05_B":null,"Q05_C":null,"Q05_D":null}}]},{"id":6,"key":"Q06","title":"Right at home, what outdoor access do you have for the dog?","weight":1.0,"section":{"id":"home_environment","name":"Home & environment"},"scoring_traits":[{"field":"ratings_1_to_10.chase_wander_tendency","scale":"rating_1_10","importance":0.545455,"prefs":{"Q06_A":null,"Q06_B":{"type":"target","target":3},"Q06_C":{"type":"target","target":4},"Q06_D":{"type":"target","target":2}}},{"field":"ratings_1_to_10.off_leash_reliability","scale":"rating_1_10","importance":0.272727,"prefs":{"Q06_A":null,"Q06_B":{"type":"target","target":6},"Q06_C":{"type":"target","target":5},"Q06_D":{"type":"target","target":6}}},{"field":"ratings_1_to_10.daily_exercise_needs","scale":"rating_1_10","importance":0.181818,"prefs":{"Q06_A":null,"Q06_B":null,"Q06_C":{"type":"target","target":6},"Q06_D":{"type":"target","target":4}}}]},{"id":7,"key":"Q07","title":"How secure is your outdoor setup (fence/containment) most of the time?","weight":1.2,"section":{"id":"home_environment","name":"Home & environment"},"scoring_traits":[{"field":"ratings_1_to_10.chase_wander_tendency","scale":"rating_1_10","importance":0.7,"prefs":{"Q07_A":null,"Q07_B":{"type":"range","min":0,"max":5,"pad":3},"Q07_C":{"type":"range","min":0,"max":4,"pad":3},"Q07_D":{"type":"range","min":0,"max":6,"pad":3}}},{"field":"ratings_1_to_10.off_leash_reliability","scale":"rating_1_10","importance":0.3,"prefs":{"Q07_A":null,"Q07_B":{"type":"range","min":6,"max":10,"pad":3},"Q07_C":null,"Q07_D":{"type":"range","min":6,"max":10,"pad":3}}}]},{"id":8,"key":"Q08","title":"Are there animals/things at home that would be a big problem if a dog chased them?","weight":1.8,"section":{"id":"home_environment","name":"Home & environment"},"scoring_traits":[{"field":"ratings_1_to_10.prey_drive","scale":"rating_1_10","importance":0.5,"prefs":{"Q08_A":{"type":"range","min":0,"max":3,"pad":3},"Q08_B":{"type":"range","min":0,"max":5,"pad":3},"Q08_C":{"type":"range","min":0,"max":7,"pad":3},"Q08_D":null}},{"field":"ratings_1_to_10.chase_wander_tendency","scale":"rating_1_10","importance":0.3,"prefs":{"Q08_A":{"type":"range","min":0,"max":3,"pad":3},"Q08_B":{"type":"range","min":0,"max":5,"pad":3},"Q08_C":{"type":"range","min":0,"max":7,"pad":3},"Q08_D":null}},{"field":"ratings_1_to_10.cat_small_pet_compatibility","scale":"rating_1_10","importance":0.2,"prefs":{"Q08_A":{"type":"target","target":8},"Q08_B":{"type":"target","target":6},"Q08_C":null,"Q08_D":null}},{"field":"ratings_1_to_10.prey_drive","scale":"rating_1_10","importance":0.0,"prefs":{"Q08_A":{"type":"filter_max","max":6,"penalty":250},"Q08_B":null,"Q08_C":null,"Q08_D":null}}]},{"id":9,"key":"Q09","title":"What are you mainly hoping your dog will be ‘for’ you?","weight":1.5,"section":{"id":"home_environment","name":"Home & environment"},"scoring_traits":[{"field":"computed.overall_rankings.overall_companion","scale":"overall_0_100ish","importance":0.194444,"prefs":{"Q09_A":{"type":"target","target":75},"Q09_B":null,"Q09_C":null,"Q09_D":null}},{"field":"computed.grades.guard_watch.grade_0_100","scale":"grade_0_100","importance":0.138889,"prefs":{"Q09_A":null,"Q09_B":{"type":"target","target":75},"Q09_C":{"type":"target","target":75},"Q09_D":null}},{"field":"ratings_1_to_10.watchdog_alertness","scale":"rating_1_10","importance":0.111111,"prefs":{"Q09_A":null,"Q09_B":{"type":"target","target":7},"Q09_C":{"type":"target","target":7},"Q09_D":null}},{"field":"ratings_1_to_10.guarding_protectiveness","scale":"rating_1_10","importance":0.138889,"prefs":{"Q09_A":null,"Q09_B":null,"Q09_C":{"type":"target","target":8},"Q09_D":null}},{"field":"computed.overall_rankings.overall_adventure","scale":"overall_0_100ish","importance":0.194444,"prefs":{"Q09_A":null,"Q09_B":null,"Q09_C":null,"Q09_D":{"type":"target","target":75}}},{"field":"computed.grades.energy_activity.grade_0_100","scale":"grade_0_100","importance":0.138889,"prefs":{"Q09_A":null,"Q09_B":null,"Q09_C":null,"Q09_D":{"type":"target","target":80}}},{"field":"computed.grades.training.grade_0_100","scale":"grade_0_100","importance":0.083334,"prefs":{"Q09_A":null,"Q09_B":null,"Q09_C":null,"Q09_D":{"type":"target","target":70}}}]},{"id":10,"key":"Q10","title":"What’s the typical climate where you live most of the year?","weight":1.5,"section":{"id":"climate_size","name":"Climate & size"},"scoring_traits":[{"field":"ratings_1_to_10.heat_tolerance","scale":"rating_1_10","importance":0.5,"prefs":{"Q10_A":{"type":"target","target":8},"Q10_B":null,"Q10_C":{"type":"target","target":6},"Q10_D":null}},{"field":"ratings_1_to_10.cold_tolerance","scale":"rating_1_10","importance":0.5,"prefs":{"Q10_A":null,"Q10_B":{"type":"target","target":8},"Q10_C":{"type":"target","target":6},"Q10_D":null}}]},{"id":11,"key":"Q11","title":"What size dog feels realistic for your lifestyle and home?","weight":1.8,"section":{"id":"climate_size","name":"Climate & size"},"scoring_traits":[{"field":"computed.midpoints.weight_lbs_mid","scale":"lbs","importance":1.0,"prefs":{"Q11_A":{"type":"range","min":5,"max":25,"pad":20},"Q11_B":{"type":"range","min":25,"max":55,"pad":25},"Q11_C":{"type":"range","min":55,"max":120,"pad":40},"Q11_D":null}}]},{"id":12,"key":"Q12","title":"On leash, if a dog suddenly pulls toward a squirrel/dog, how comfortable are you handling that?","weight":2.0,"section":{"id":"climate_size","name":"Climate & size"},"scoring_traits":[{"field":"ratings_1_to_10.strength_pulling_power","scale":"rating_1_10","importance":0.7,"prefs":{"Q12_A":{"type":"range","min":0,"max":3,"pad":3},"Q12_B":{"type":"range","min":0,"max":5,"pad":3},"Q12_C":{"type":"range","min":0,"max":7,"pad":3},"Q12_D":null}},{"field":"ratings_1_to_10.trainability","scale":"rating_1_10","importance":0.2,"prefs":{"Q12_A":{"type":"range","min":7,"max":10,"pad":3},"Q12_B":{"type":"range","min":6,"max":10,"pad":3},"Q12_C":{"type":"range","min":5,"max":10,"pad":3},"Q12_D":null}},{"field":"ratings_1_to_10.responsiveness_to_training","scale":"rating_1_10","importance":0.1,"prefs":{"Q12_A":{"type":"range","min":7,"max":10,"pad":3},"Q12_B":{"type":"range","min":6,"max":10,"pad":3},"Q12_C":{"type":"range","min":5,"max":10,"pad":3},"Q12_D":null}}]},{"id":13,"key":"Q13","title":"If your dog got injured, how realistic is it for you to lift/carry them (stairs, car, vet, etc.)?","weight":1.6,"section":{"id":"climate_size","name":"Climate & size"},"scoring_traits":[{"field":"computed.midpoints.weight_lbs_mid","scale":"lbs","importance":1.0,"prefs":{"Q13_A":{"type":"range","min":5,"max":30,"pad":30},"Q13_B":{"type":"range","min":10,"max":60,"pad":40},"Q13_C":{"type":"range","min":20,"max":100,"pad":60},"Q13_D":null}}]},{"id":14,"key":"Q14","title":"On a typical weekday, how long is your home empty (no people) at a stretch?","weight":2.5,"section":{"id":"schedule_activity","name":"Schedule & activity"},"scoring_traits":[{"field":"ratings_1_to_10.tolerance_for_being_alone","scale":"rating_1_10","importance":0.7,"prefs":{"Q14_A":null,"Q14_B":{"type":"range","min":5,"max":10,"pad":4},"Q14_C":{"type":"range","min":6,"max":10,"pad":4},"Q14_D":{"type":"range","min":8,"max":10,"pad":4}}},{"field":"ratings_1_to_10.destructiveness_risk","scale":"rating_1_10","importance":0.3,"prefs":{"Q14_A":null,"Q14_B":{"type":"range","min":0,"max":6,"pad":3},"Q14_C":{"type":"range","min":0,"max":5,"pad":3},"Q14_D":{"type":"range","min":0,"max":4,"pad":3}}}]},{"id":15,"key":"Q15","title":"When you’re away, what kind of support can you realistically provide most weeks?","weight":1.2,"section":{"id":"schedule_activity","name":"Schedule & activity"},"scoring_traits":[{"field":"ratings_1_to_10.tolerance_for_being_alone","scale":"rating_1_10","importance":0.7,"prefs":{"Q15_A":null,"Q15_B":{"type":"range","min":5,"max":10,"pad":4},"Q15_C":{"type":"range","min":6,"max":10,"pad":4},"Q15_D":{"type":"range","min":8,"max":10,"pad":4}}},{"field":"ratings_1_to_10.destructiveness_risk","scale":"rating_1_10","importance":0.3,"prefs":{"Q15_A":null,"Q15_B":{"type":"range","min":0,"max":6,"pad":3},"Q15_C":{"type":"range","min":0,"max":6,"pad":3},"Q15_D":{"type":"range","min":0,"max":4,"pad":3}}}]},{"id":16,"key":"Q16","title":"Most weekdays, how much time can you truly commit to walks/play (not counting quick potty trips)?","weight":2.8,"section":{"id":"schedule_activity","name":"Schedule & activity"},"scoring_traits":[{"field":"computed.grades.energy_activity.grade_0_100","scale":"grade_0_100","importance":0.6,"prefs":{"Q16_A":{"type":"range","min":0,"max":45,"pad":25},"Q16_B":{"type":"range","min":0,"max":65,"pad":25},"Q16_C":{"type":"range","min":0,"max":80,"pad":30},"Q16_D":null}},{"field":"ratings_1_to_10.daily_exercise_needs","scale":"rating_1_10","importance":0.25,"prefs":{"Q16_A":{"type":"range","min":0,"max":4,"pad":3},"Q16_B":{"type":"range","min":0,"max":6,"pad":3},"Q16_C":{"type":"range","min":0,"max":8,"pad":3},"Q16_D":null}},{"field":"ratings_1_to_10.energy_level","scale":"rating_1_10","importance":0.15,"prefs":{"Q16_A":{"type":"range","min":0,"max":5,"pad":3},"Q16_B":{"type":"range","min":0,"max":7,"pad":3},"Q16_C":{"type":"range","min":0,"max":9,"pad":3},"Q16_D":null}}]},{"id":17,"key":"Q17","title":"What kind of activity do you actually enjoy doing with a dog?","weight":1.6,"section":{"id":"schedule_activity","name":"Schedule & activity"},"scoring_traits":[{"field":"ratings_1_to_10.energy_level","scale":"rating_1_10","importance":0.35,"prefs":{"Q17_A":{"type":"target","target":4},"Q17_B":{"type":"target","target":6},"Q17_C":{"type":"target","target":8},"Q17_D":{"type":"target","target":8}}},{"field":"computed.calmness.grade_0_100","scale":"grade_0_100","importance":0.25,"prefs":{"Q17_A":{"type":"target","target":75},"Q17_B":{"type":"target","target":60},"Q17_C":{"type":"target","target":50},"Q17_D":{"type":"target","target":50}}},{"field":"computed.grades.energy_activity.grade_0_100","scale":"grade_0_100","importance":0.25,"prefs":{"Q17_A":{"type":"target","target":40},"Q17_B":{"type":"target","target":60},"Q17_C":{"type":"target","target":80},"Q17_D":{"type":"target","target":75}}},{"field":"ratings_1_to_10.fetch_retrieve_drive","scale":"rating_1_10","importance":0.15,"prefs":{"Q17_A":null,"Q17_B":null,"Q17_C":null,"Q17_D":{"type":"target","target":8}}}]},{"id":18,"key":"Q18","title":"How often would you realistically do ‘brain games’ with your dog (training, puzzles, sniff games)?","weight":2.0,"section":{"id":"schedule_activity","name":"Schedule & activity"},"scoring_traits":[{"field":"computed.grades.smart.grade_0_100","scale":"grade_0_100","importance":0.45,"prefs":{"Q18_A":{"type":"target","target":45},"Q18_B":{"type":"target","target":55},"Q18_C":{"type":"target","target":70},"Q18_D":{"type":"target","target":85}}},{"field":"ratings_1_to_10.intelligence_problem_solving","scale":"rating_1_10","importance":0.25,"prefs":{"Q18_A":{"type":"target","target":4},"Q18_B":{"type":"target","target":5},"Q18_C":{"type":"target","target":7},"Q18_D":{"type":"target","target":9}}},{"field":"ratings_1_to_10.destructiveness_risk","scale":"rating_1_10","importance":0.15,"prefs":{"Q18_A":{"type":"target","target":2},"Q18_B":{"type":"target","target":3},"Q18_C":{"type":"target","target":4},"Q18_D":{"type":"target","target":5}}},{"field":"computed.grades.training.grade_0_100","scale":"grade_0_100","importance":0.15,"prefs":{"Q18_A":{"type":"target","target":65},"Q18_B":{"type":"target","target":65},"Q18_C":{"type":"target","target":65},"Q18_D":{"type":"target","target":75}}}]},{"id":19,"key":"Q19","title":"What’s your experience living with dogs (as the person responsible for care/training)?","weight":1.8,"section":{"id":"training_temperament","name":"Training & temperament"},"scoring_traits":[{"field":"ratings_1_to_10.ease_for_first_time_owners","scale":"rating_1_10","importance":0.8,"prefs":{"Q19_A":{"type":"target","target":9},"Q19_B":{"type":"target","target":7},"Q19_C":null,"Q19_D":null}},{"field":"computed.grades.training.grade_0_100","scale":"grade_0_100","importance":0.2,"prefs":{"Q19_A":{"type":"target","target":75},"Q19_B":{"type":"target","target":65},"Q19_C":null,"Q19_D":null}}]},{"id":20,"key":"Q20","title":"How much ‘behavior work’ are you willing to take on in the first year?","weight":1.5,"section":{"id":"training_temperament","name":"Training & temperament"},"scoring_traits":[{"field":"ratings_1_to_10.ease_for_first_time_owners","scale":"rating_1_10","importance":0.45,"prefs":{"Q20_A":{"type":"range","min":7,"max":10,"pad":3},"Q20_B":{"type":"range","min":6,"max":10,"pad":3},"Q20_C":{"type":"range","min":4,"max":10,"pad":4},"Q20_D":null}},{"field":"ratings_1_to_10.destructiveness_risk","scale":"rating_1_10","importance":0.3,"prefs":{"Q20_A":{"type":"range","min":0,"max":3,"pad":3},"Q20_B":{"type":"range","min":0,"max":4,"pad":3},"Q20_C":{"type":"range","min":0,"max":5,"pad":3},"Q20_D":null}},{"field":"ratings_1_to_10.intelligence_problem_solving","scale":"rating_1_10","importance":0.25,"prefs":{"Q20_A":{"type":"target","target":4},"Q20_B":{"type":"target","target":5},"Q20_C":{"type":"target","target":6},"Q20_D":{"type":"target","target":8}}}]},{"id":21,"key":"Q21","title":"How important is it that your dog is generally easy to train for basics (sit, leash walking, manners)?","weight":2.0,"section":{"id":"training_temperament","name":"Training & temperament"},"scoring_traits":[{"field":"computed.grades.training.grade_0_100","scale":"grade_0_100","importance":0.5,"prefs":{"Q21_A":{"type":"range","min":75,"max":100,"pad":25},"Q21_B":{"type":"range","min":65,"max":100,"pad":25},"Q21_C":null,"Q21_D":{"type":"range","min":80,"max":100,"pad":25}}},{"field":"ratings_1_to_10.trainability","scale":"rating_1_10","importance":0.25,"prefs":{"Q21_A":{"type":"range","min":7,"max":10,"pad":3},"Q21_B":{"type":"range","min":6,"max":10,"pad":3},"Q21_C":null,"Q21_D":{"type":"range","min":8,"max":10,"pad":3}}},{"field":"ratings_1_to_10.responsiveness_to_training","scale":"rating_1_10","importance":0.25,"prefs":{"Q21_A":{"type":"range","min":7,"max":10,"pad":3},"Q21_B":{"type":"range","min":6,"max":10,"pad":3},"Q21_C":null,"Q21_D":{"type":"range","min":8,"max":10,"pad":3}}}]},{"id":22,"key":"Q22","title":"When you call your dog or give a cue, what do you expect most of the time?","weight":1.5,"section":{"id":"training_temperament","name":"Training & temperament"},"scoring_traits":[{"field":"ratings_1_to_10.responsiveness_to_training","scale":"rating_1_10","importance":0.6,"prefs":{"Q22_A":{"type":"target","target":9},"Q22_B":{"type":"target","target":7},"Q22_C":{"type":"target","target":5},"Q22_D":{"type":"target","target":3}}},{"field":"ratings_1_to_10.trainability","scale":"rating_1_10","importance":0.4,"prefs":{"Q22_A":{"type":"target","target":8},"Q22_B":{"type":"target","target":7},"Q22_C":{"type":"target","target":6},"Q22_D":{"type":"target","target":5}}}]},{"id":23,"key":"Q23","title":"Picture a normal evening at home—what do you want your dog to be doing?","weight":1.8,"section":{"id":"training_temperament","name":"Training & temperament"},"scoring_traits":[{"field":"computed.calmness.grade_0_100","scale":"grade_0_100","importance":0.45,"prefs":{"Q23_A":{"type":"target","target":85},"Q23_B":{"type":"target","target":70},"Q23_C":{"type":"target","target":55},"Q23_D":{"type":"target","target":40}}},{"field":"ratings_1_to_10.energy_level","scale":"rating_1_10","importance":0.25,"prefs":{"Q23_A":{"type":"target","target":3},"Q23_B":{"type":"target","target":5},"Q23_C":{"type":"target","target":7},"Q23_D":{"type":"target","target":9}}},{"field":"ratings_1_to_10.playfulness","scale":"rating_1_10","importance":0.2,"prefs":{"Q23_A":{"type":"target","target":3},"Q23_B":{"type":"target","target":5},"Q23_C":{"type":"target","target":7},"Q23_D":{"type":"target","target":9}}},{"field":"ratings_1_to_10.family_affection","scale":"rating_1_10","importance":0.1,"prefs":{"Q23_A":{"type":"target","target":8},"Q23_B":{"type":"target","target":7},"Q23_C":{"type":"target","target":6},"Q23_D":{"type":"target","target":6}}}]},{"id":24,"key":"Q24","title":"If your dog barked when the doorbell rings or someone knocks, how would you feel?","weight":1.7,"section":{"id":"training_temperament","name":"Training & temperament"},"scoring_traits":[{"field":"ratings_1_to_10.barking_vocalness","scale":"rating_1_10","importance":0.65,"prefs":{"Q24_A":{"type":"target","target":2},"Q24_B":{"type":"target","target":4},"Q24_C":{"type":"target","target":6},"Q24_D":{"type":"target","target":7}}},{"field":"ratings_1_to_10.watchdog_alertness","scale":"rating_1_10","importance":0.35,"prefs":{"Q24_A":{"type":"target","target":2},"Q24_B":{"type":"target","target":4},"Q24_C":{"type":"target","target":5},"Q24_D":{"type":"target","target":7}}}]},{"id":25,"key":"Q25","title":"Imagine the dog chews something valuable once (shoe/couch corner). What’s your reaction?","weight":1.8,"section":{"id":"training_temperament","name":"Training & temperament"},"scoring_traits":[{"field":"ratings_1_to_10.destructiveness_risk","scale":"rating_1_10","importance":0.8,"prefs":{"Q25_A":{"type":"range","min":0,"max":3,"pad":3},"Q25_B":{"type":"range","min":0,"max":5,"pad":3},"Q25_C":{"type":"range","min":0,"max":6.5,"pad":3},"Q25_D":null}},{"field":"ratings_1_to_10.trainability","scale":"rating_1_10","importance":0.2,"prefs":{"Q25_A":{"type":"range","min":7,"max":10,"pad":3},"Q25_B":{"type":"range","min":6,"max":10,"pad":3},"Q25_C":{"type":"range","min":5,"max":10,"pad":3},"Q25_D":null}}]},{"id":26,"key":"Q26","title":"How would a dog that startles easily (noise-sensitive, sensitive to correction) fit your home?","weight":1.2,"section":{"id":"training_temperament","name":"Training & temperament"},"scoring_traits":[{"field":"ratings_1_to_10.sensitivity","scale":"rating_1_10","importance":1.0,"prefs":{"Q26_A":{"type":"target","target":8},"Q26_B":{"type":"target","target":5},"Q26_C":{"type":"target","target":2},"Q26_D":null}}]},{"id":27,"key":"Q27","title":"How ‘cuddly’ do you want your dog to be with you/your household?","weight":1.0,"section":{"id":"training_temperament","name":"Training & temperament"},"scoring_traits":[{"field":"ratings_1_to_10.family_affection","scale":"rating_1_10","importance":1.0,"prefs":{"Q27_A":{"type":"target","target":9},"Q27_B":{"type":"target","target":7},"Q27_C":{"type":"target","target":4},"Q27_D":null}}]},{"id":28,"key":"Q28","title":"What kind of play would you enjoy most with your dog?","weight":1.2,"section":{"id":"training_temperament","name":"Training & temperament"},"scoring_traits":[{"field":"ratings_1_to_10.playfulness","scale":"rating_1_10","importance":0.6,"prefs":{"Q28_A":{"type":"target","target":3},"Q28_B":{"type":"target","target":5},"Q28_C":{"type":"target","target":6},"Q28_D":{"type":"target","target":9}}},{"field":"ratings_1_to_10.fetch_retrieve_drive","scale":"rating_1_10","importance":0.4,"prefs":{"Q28_A":{"type":"target","target":3},"Q28_B":{"type":"target","target":5},"Q28_C":{"type":"target","target":8},"Q28_D":{"type":"target","target":8}}}]},{"id":29,"key":"Q29","title":"How often will your dog be around children (in your home or frequent visitors)?","weight":1.5,"section":{"id":"kids","name":"Kids"},"scoring_traits":[{"field":"computed.grades.children.grade_0_100","scale":"grade_0_100","importance":0.55,"prefs":{"Q29_A":null,"Q29_B":{"type":"target","target":65},"Q29_C":{"type":"target","target":80},"Q29_D":{"type":"target","target":90}}},{"field":"ratings_1_to_10.kid_friendliness","scale":"rating_1_10","importance":0.3,"prefs":{"Q29_A":null,"Q29_B":{"type":"target","target":7},"Q29_C":{"type":"target","target":8},"Q29_D":{"type":"target","target":9}}},{"field":"computed.calmness.grade_0_100","scale":"grade_0_100","importance":0.15,"prefs":{"Q29_A":null,"Q29_B":{"type":"target","target":60},"Q29_C":{"type":"target","target":70},"Q29_D":{"type":"target","target":80}}}]},{"id":30,"key":"Q30","title":"If kids are around, what does your household feel like most days?","weight":1.5,"section":{"id":"kids","name":"Kids"},"scoring_traits":[{"field":"computed.calmness.grade_0_100","scale":"grade_0_100","importance":0.45,"prefs":{"Q30_A":{"type":"target","target":65},"Q30_B":{"type":"target","target":70},"Q30_C":{"type":"target","target":75},"Q30_D":{"type":"target","target":85}}},{"field":"ratings_1_to_10.kid_friendliness","scale":"rating_1_10","importance":0.35,"prefs":{"Q30_A":{"type":"target","target":7},"Q30_B":{"type":"target","target":8},"Q30_C":{"type":"target","target":8},"Q30_D":{"type":"target","target":9}}},{"field":"ratings_1_to_10.mouthiness_nipping_tendency","scale":"rating_1_10","importance":0.2,"prefs":{"Q30_A":{"type":"target","target":5},"Q30_B":{"type":"target","target":5},"Q30_C":{"type":"target","target":5},"Q30_D":{"type":"target","target":3}}}]},{"id":31,"key":"Q31","title":"Young dogs often mouth hands/clothes while learning (not aggression). How would you handle that phase?","weight":1.8,"section":{"id":"kids","name":"Kids"},"scoring_traits":[{"field":"ratings_1_to_10.mouthiness_nipping_tendency","scale":"rating_1_10","importance":0.7,"prefs":{"Q31_A":{"type":"range","min":0,"max":3,"pad":3},"Q31_B":{"type":"range","min":0,"max":5,"pad":3},"Q31_C":{"type":"range","min":0,"max":7,"pad":3},"Q31_D":null}},{"field":"ratings_1_to_10.trainability","scale":"rating_1_10","importance":0.3,"prefs":{"Q31_A":{"type":"range","min":7,"max":10,"pad":3},"Q31_B":{"type":"range","min":6,"max":10,"pad":3},"Q31_C":{"type":"range","min":5,"max":10,"pad":3},"Q31_D":null}}]},{"id":32,"key":"Q32","title":"With kids in the picture, what’s your minimum requirement?","weight":2.0,"section":{"id":"kids","name":"Kids"},"scoring_traits":[{"field":"computed.grades.children.grade_0_100","scale":"grade_0_100","importance":0.5,"prefs":{"Q32_A":{"type":"range","min":80,"max":100,"pad":25},"Q32_B":{"type":"range","min":70,"max":100,"pad":25},"Q32_C":{"type":"range","min":60,"max":100,"pad":30},"Q32_D":{"type":"range","min":50,"max":100,"pad":30}}},{"field":"ratings_1_to_10.kid_friendliness","scale":"rating_1_10","importance":0.35,"prefs":{"Q32_A":{"type":"range","min":8,"max":10,"pad":3},"Q32_B":{"type":"range","min":7,"max":10,"pad":3},"Q32_C":{"type":"range","min":6,"max":10,"pad":3},"Q32_D":{"type":"range","min":5,"max":10,"pad":3}}},{"field":"computed.calmness.grade_0_100","scale":"grade_0_100","importance":0.15,"prefs":{"Q32_A":{"type":"range","min":60,"max":100,"pad":30},"Q32_B":{"type":"range","min":55,"max":100,"pad":30},"Q32_C":{"type":"range","min":50,"max":100,"pad":30},"Q32_D":{"type":"range","min":45,"max":100,"pad":30}}},{"field":"ratings_1_to_10.kid_friendliness","scale":"rating_1_10","importance":0.0,"prefs":{"Q32_A":{"type":"filter_min","min":7,"penalty":400},"Q32_B":null,"Q32_C":null,"Q32_D":null}}]},{"id":33,"key":"Q33","title":"Do you have other animals that the dog must live with day-to-day?","weight":0.8,"section":{"id":"other_pets","name":"Other pets"},"scoring_traits":[{"field":"computed.grades.multi_pet.grade_0_100","scale":"grade_0_100","importance":1.0,"prefs":{"Q33_A":null,"Q33_B":{"type":"target","target":65},"Q33_C":{"type":"target","target":65},"Q33_D":{"type":"target","target":75}}}]},{"id":34,"key":"Q34","title":"How much will your dog be around OTHER DOGS (friends’ dogs, parks, walks)?","weight":1.5,"section":{"id":"other_pets","name":"Other pets"},"scoring_traits":[{"field":"ratings_1_to_10.dog_friendliness","scale":"rating_1_10","importance":1.0,"prefs":{"Q34_A":{"type":"target","target":9},"Q34_B":{"type":"target","target":7},"Q34_C":null,"Q34_D":null}}]},{"id":35,"key":"Q35","title":"If you have cats/small pets, how strict is ‘safe + compatible’ for you?","weight":2.5,"section":{"id":"other_pets","name":"Other pets"},"scoring_traits":[{"field":"ratings_1_to_10.cat_small_pet_compatibility","scale":"rating_1_10","importance":0.55,"prefs":{"Q35_A":{"type":"target","target":9},"Q35_B":{"type":"target","target":7},"Q35_C":{"type":"target","target":5},"Q35_D":null}},{"field":"ratings_1_to_10.prey_drive","scale":"rating_1_10","importance":0.25,"prefs":{"Q35_A":{"type":"target","target":2},"Q35_B":{"type":"target","target":4},"Q35_C":{"type":"target","target":6},"Q35_D":null}},{"field":"computed.grades.multi_pet.grade_0_100","scale":"grade_0_100","importance":0.2,"prefs":{"Q35_A":{"type":"target","target":75},"Q35_B":{"type":"target","target":65},"Q35_C":{"type":"target","target":55},"Q35_D":null}},{"field":"ratings_1_to_10.cat_small_pet_compatibility","scale":"rating_1_10","importance":0.0,"prefs":{"Q35_A":{"type":"filter_min","min":6,"penalty":450},"Q35_B":null,"Q35_C":null,"Q35_D":null}},{"field":"ratings_1_to_10.prey_drive","scale":"rating_1_10","importance":0.0,"prefs":{"Q35_A":{"type":"filter_max","max":6,"penalty":250},"Q35_B":null,"Q35_C":null,"Q35_D":null}}]},{"id":36,"key":"Q36","title":"If you have both dogs AND cats/small pets, what’s the #1 priority?","weight":2.0,"section":{"id":"other_pets","name":"Other pets"},"scoring_traits":[{"field":"ratings_1_to_10.cat_small_pet_compatibility","scale":"rating_1_10","importance":0.35,"prefs":{"Q36_A":{"type":"target","target":9},"Q36_B":null,"Q36_C":{"type":"target","target":8},"Q36_D":{"type":"target","target":6}}},{"field":"ratings_1_to_10.dog_friendliness","scale":"rating_1_10","importance":0.25,"prefs":{"Q36_A":null,"Q36_B":{"type":"target","target":9},"Q36_C":{"type":"target","target":8},"Q36_D":{"type":"target","target":6}}},{"field":"computed.grades.multi_pet.grade_0_100","scale":"grade_0_100","importance":0.25,"prefs":{"Q36_A":{"type":"target","target":70},"Q36_B":{"type":"target","target":70},"Q36_C":{"type":"target","target":80},"Q36_D":{"type":"target","target":65}}},{"field":"ratings_1_to_10.prey_drive","scale":"rating_1_10","importance":0.15,"prefs":{"Q36_A":{"type":"target","target":2},"Q36_B":{"type":"target","target":5},"Q36_C":{"type":"target","target":3},"Q36_D":{"type":"target","target":6}}}]},{"id":37,"key":"Q37","title":"How would you feel about a dog that REALLY wants to chase squirrels/rabbits on walks?","weight":1.8,"section":{"id":"outdoors_recall","name":"Outdoors & recall"},"scoring_traits":[{"field":"ratings_1_to_10.prey_drive","scale":"rating_1_10","importance":0.6,"prefs":{"Q37_A":{"type":"target","target":2},"Q37_B":{"type":"target","target":4},"Q37_C":{"type":"target","target":6},"Q37_D":{"type":"target","target":9}}},{"field":"ratings_1_to_10.chase_wander_tendency","scale":"rating_1_10","importance":0.4,"prefs":{"Q37_A":{"type":"target","target":3},"Q37_B":{"type":"target","target":4},"Q37_C":{"type":"target","target":6},"Q37_D":{"type":"target","target":8}}}]},{"id":38,"key":"Q38","title":"In your lifestyle, how important is it that your dog comes back reliably when called (in safe areas)?","weight":1.7,"section":{"id":"outdoors_recall","name":"Outdoors & recall"},"scoring_traits":[{"field":"ratings_1_to_10.off_leash_reliability","scale":"rating_1_10","importance":0.8,"prefs":{"Q38_A":{"type":"target","target":9},"Q38_B":{"type":"target","target":7},"Q38_C":null,"Q38_D":null}},{"field":"ratings_1_to_10.responsiveness_to_training","scale":"rating_1_10","importance":0.2,"prefs":{"Q38_A":{"type":"target","target":8},"Q38_B":{"type":"target","target":7},"Q38_C":null,"Q38_D":null}}]},{"id":39,"key":"Q39","title":"If a door/gate was accidentally left open, how serious would it be if your dog took off exploring?","weight":2.0,"section":{"id":"outdoors_recall","name":"Outdoors & recall"},"scoring_traits":[{"field":"ratings_1_to_10.chase_wander_tendency","scale":"rating_1_10","importance":0.6,"prefs":{"Q39_A":{"type":"target","target":2},"Q39_B":{"type":"target","target":4},"Q39_C":{"type":"target","target":5},"Q39_D":null}},{"field":"ratings_1_to_10.off_leash_reliability","scale":"rating_1_10","importance":0.4,"prefs":{"Q39_A":{"type":"target","target":8},"Q39_B":{"type":"target","target":6},"Q39_C":{"type":"target","target":5},"Q39_D":null}}]},{"id":40,"key":"Q40","title":"In public, what vibe do you want your dog to have with strangers?","weight":1.8,"section":{"id":"strangers_protection","name":"Strangers & protection"},"scoring_traits":[{"field":"ratings_1_to_10.stranger_friendliness","scale":"rating_1_10","importance":0.7,"prefs":{"Q40_A":{"type":"target","target":9},"Q40_B":{"type":"target","target":7},"Q40_C":{"type":"target","target":4},"Q40_D":{"type":"target","target":2}}},{"field":"ratings_1_to_10.guarding_protectiveness","scale":"rating_1_10","importance":0.3,"prefs":{"Q40_A":{"type":"target","target":2},"Q40_B":{"type":"target","target":4},"Q40_C":{"type":"target","target":6},"Q40_D":{"type":"target","target":8}}}]},{"id":41,"key":"Q41","title":"At home, when something unusual happens (delivery, noise, someone outside), what should the dog do?","weight":2.0,"section":{"id":"strangers_protection","name":"Strangers & protection"},"scoring_traits":[{"field":"ratings_1_to_10.watchdog_alertness","scale":"rating_1_10","importance":0.35,"prefs":{"Q41_A":{"type":"target","target":2},"Q41_B":{"type":"target","target":5},"Q41_C":{"type":"target","target":8},"Q41_D":{"type":"target","target":9}}},{"field":"ratings_1_to_10.barking_vocalness","scale":"rating_1_10","importance":0.25,"prefs":{"Q41_A":{"type":"target","target":2},"Q41_B":{"type":"target","target":4},"Q41_C":{"type":"target","target":6},"Q41_D":{"type":"target","target":7}}},{"field":"computed.grades.guard_watch.grade_0_100","scale":"grade_0_100","importance":0.25,"prefs":{"Q41_A":{"type":"target","target":40},"Q41_B":{"type":"target","target":55},"Q41_C":{"type":"target","target":75},"Q41_D":{"type":"target","target":85}}},{"field":"computed.calmness.grade_0_100","scale":"grade_0_100","importance":0.15,"prefs":{"Q41_A":{"type":"target","target":80},"Q41_B":{"type":"target","target":75},"Q41_C":{"type":"target","target":55},"Q41_D":{"type":"target","target":45}}}]},{"id":42,"key":"Q42","title":"If you want a protective dog, what level feels appropriate for your household and experience?","weight":2.2,"section":{"id":"strangers_protection","name":"Strangers & protection"},"scoring_traits":[{"field":"ratings_1_to_10.guarding_protectiveness","scale":"rating_1_10","importance":0.45,"prefs":{"Q42_A":{"type":"target","target":5},"Q42_B":{"type":"target","target":7},"Q42_C":{"type":"target","target":9},"Q42_D":{"type":"target","target":10}}},{"field":"computed.overall_rankings.overall_protection","scale":"overall_0_100ish","importance":0.25,"prefs":{"Q42_A":{"type":"target","target":60},"Q42_B":{"type":"target","target":70},"Q42_C":{"type":"target","target":80},"Q42_D":{"type":"target","target":85}}},{"field":"computed.grades.guard_watch.grade_0_100","scale":"grade_0_100","importance":0.2,"prefs":{"Q42_A":{"type":"target","target":60},"Q42_B":{"type":"target","target":70},"Q42_C":{"type":"target","target":80},"Q42_D":{"type":"target","target":85}}},{"field":"ratings_1_to_10.trainability","scale":"rating_1_10","importance":0.1,"prefs":{"Q42_A":{"type":"target","target":6},"Q42_B":{"type":"target","target":6},"Q42_C":{"type":"target","target":6},"Q42_D":{"type":"target","target":7}}}]},{"id":43,"key":"Q43","title":"When approved guests come over, what should your dog ideally do?","weight":1.5,"section":{"id":"strangers_protection","name":"Strangers & protection"},"scoring_traits":[{"field":"ratings_1_to_10.stranger_friendliness","scale":"rating_1_10","importance":0.45,"prefs":{"Q43_A":{"type":"target","target":9},"Q43_B":{"type":"target","target":7},"Q43_C":{"type":"target","target":5},"Q43_D":{"type":"target","target":3}}},{"field":"computed.calmness.grade_0_100","scale":"grade_0_100","importance":0.35,"prefs":{"Q43_A":{"type":"target","target":55},"Q43_B":{"type":"target","target":75},"Q43_C":{"type":"target","target":75},"Q43_D":{"type":"target","target":65}}},{"field":"ratings_1_to_10.watchdog_alertness","scale":"rating_1_10","importance":0.2,"prefs":{"Q43_A":{"type":"target","target":3},"Q43_B":{"type":"target","target":4},"Q43_C":{"type":"target","target":5},"Q43_D":{"type":"target","target":7}}}]},{"id":44,"key":"Q44","title":"Dog hair reality: which statement is closest to you?","weight":2.0,"section":{"id":"grooming_allergies","name":"Grooming & allergies"},"scoring_traits":[{"field":"ratings_1_to_10.shedding","scale":"rating_1_10","importance":0.7,"prefs":{"Q44_A":{"type":"range","min":0,"max":3,"pad":3},"Q44_B":{"type":"range","min":0,"max":5,"pad":3},"Q44_C":{"type":"range","min":0,"max":8,"pad":3},"Q44_D":null}},{"field":"computed.grades.grooming_clean_home.grade_0_100","scale":"grade_0_100","importance":0.3,"prefs":{"Q44_A":{"type":"range","min":70,"max":100,"pad":30},"Q44_B":{"type":"range","min":55,"max":100,"pad":35},"Q44_C":null,"Q44_D":null}},{"field":"ratings_1_to_10.shedding","scale":"rating_1_10","importance":0.0,"prefs":{"Q44_A":{"type":"filter_max","max":5,"penalty":250},"Q44_B":null,"Q44_C":null,"Q44_D":null}}]},{"id":45,"key":"Q45","title":"Grooming reality: what’s your comfort level with coat care?","weight":1.8,"section":{"id":"grooming_allergies","name":"Grooming & allergies"},"scoring_traits":[{"field":"ratings_1_to_10.coat_maintenance_grooming_needs","scale":"rating_1_10","importance":1.0,"prefs":{"Q45_A":{"type":"range","min":0,"max":3,"pad":3},"Q45_B":{"type":"range","min":0,"max":5,"pad":3},"Q45_C":{"type":"range","min":0,"max":8,"pad":3},"Q45_D":null}}]},{"id":46,"key":"Q46","title":"Drool/slobber: how would that fit your home and preferences?","weight":1.6,"section":{"id":"grooming_allergies","name":"Grooming & allergies"},"scoring_traits":[{"field":"ratings_1_to_10.drooling","scale":"rating_1_10","importance":1.0,"prefs":{"Q46_A":{"type":"range","min":0,"max":3,"pad":3},"Q46_B":{"type":"range","min":0,"max":5,"pad":3},"Q46_C":null,"Q46_D":null}},{"field":"ratings_1_to_10.drooling","scale":"rating_1_10","importance":0.0,"prefs":{"Q46_A":{"type":"filter_max","max":5,"penalty":200},"Q46_B":null,"Q46_C":null,"Q46_D":null}}]},{"id":47,"key":"Q47","title":"Dog smell & bathing: which is most true for you?","weight":1.4,"section":{"id":"grooming_allergies","name":"Grooming & allergies"},"scoring_traits":[{"field":"ratings_1_to_10.bathing_odor_management_needs","scale":"rating_1_10","importance":1.0,"prefs":{"Q47_A":{"type":"range","min":0,"max":3,"pad":3},"Q47_B":{"type":"range","min":0,"max":5,"pad":3},"Q47_C":{"type":"range","min":0,"max":7,"pad":3},"Q47_D":null}}]},{"id":48,"key":"Q48","title":"Allergies: do you need to prioritize breeds that tend to be more allergy-friendly?","weight":2.5,"section":{"id":"grooming_allergies","name":"Grooming & allergies"},"scoring_traits":[{"field":"ratings_1_to_10.hypoallergenic_tendency","scale":"rating_1_10","importance":0.75,"prefs":{"Q48_A":{"type":"target","target":9},"Q48_B":{"type":"target","target":8},"Q48_C":{"type":"target","target":6},"Q48_D":null}},{"field":"ratings_1_to_10.shedding","scale":"rating_1_10","importance":0.25,"prefs":{"Q48_A":{"type":"target","target":2},"Q48_B":{"type":"target","target":3},"Q48_C":{"type":"target","target":4},"Q48_D":null}},{"field":"ratings_1_to_10.hypoallergenic_tendency","scale":"rating_1_10","importance":0.0,"prefs":{"Q48_A":{"type":"filter_min","min":7,"penalty":800,"hard":true},"Q48_B":null,"Q48_C":null,"Q48_D":null}}]},{"id":49,"key":"Q49","title":"How much do you want to prioritize a breed that tends to be more robust/healthy overall?","weight":1.8,"section":{"id":"health","name":"Health"},"scoring_traits":[{"field":"ratings_1_to_10.health_robustness","scale":"rating_1_10","importance":0.75,"prefs":{"Q49_A":{"type":"range","min":8,"max":10,"pad":3},"Q49_B":{"type":"range","min":7,"max":10,"pad":3},"Q49_C":{"type":"range","min":6,"max":10,"pad":3},"Q49_D":null}},{"field":"computed.midpoints.lifespan_years_mid","scale":"years","importance":0.25,"prefs":{"Q49_A":{"type":"range","min":13,"max":25,"pad":5},"Q49_B":{"type":"range","min":12,"max":25,"pad":5},"Q49_C":{"type":"range","min":11,"max":25,"pad":6},"Q49_D":null}}]},{"id":50,"key":"Q50","title":"If two breeds score similarly, which ‘direction’ should we lean as the tie-breaker?","weight":0.0,"section":{"id":"tie_break","name":"Tie-break"},"scoring_traits":[],"tie_break_map_by_option_id":{"Q50_A":"computed.overall_rankings.overall_first_time","Q50_B":"computed.overall_rankings.overall_family_kids","Q50_C":"computed.overall_rankings.overall_adventure","Q50_D":"computed.overall_rankings.overall_protection"}}]};

  // -----------------------------
  // Small utilities
  // -----------------------------

  /**
   * Stable, fast, non-cryptographic hash for deterministic randomness.
   * Returns an unsigned 32-bit integer.
   */
  function fnv1a32(str) {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
  }

  
  /**
   * Returns the best available Web Storage (prefers localStorage, falls back to sessionStorage).
   * Returns null if storage is unavailable or blocked.
   */
  function getBestAvailableStorage() {
    try {
      const testKey = '__ml9665_dbq_test__';
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      return window.localStorage;
    } catch (_) {
      // ignore
    }

    try {
      const testKey = '__ml9665_dbq_test__';
      window.sessionStorage.setItem(testKey, '1');
      window.sessionStorage.removeItem(testKey);
      return window.sessionStorage;
    } catch (_) {
      // ignore
    }

    return null;
  }


  /**
   * Sanitizes any string into a safe HTML id/name token.
   * - Ensures no whitespace (ids must not contain whitespace).
   * - Keeps it stable (deterministic) for the same input.
   */
  function sanitizeDomToken(input) {
    const s = String(input == null ? '' : input).trim();
    if (!s) return 'inst';
    // Replace whitespace first to avoid collapsing into empty.
    const cleaned = s
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9_-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^[-_]+|[-_]+$/g, '');
    return cleaned || 'inst';
  }

  /**
   * Escapes a value for safe use inside a double-quoted CSS attribute selector.
   * Example: `[name="..."]`
   *
   * NOTE: This is intentionally minimal (we only use it for internal fallbacks).
   */

  function escapeCssAttrValue(value) {
    return String(value == null ? '' : value)
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');
  }

  function safeJsonParse(raw, label) {
    if (raw == null) return { ok: false, error: new Error(`Missing ${label}`) };
    const text = String(raw).trim();
    if (!text) return { ok: false, error: new Error(`Empty ${label}`) };
    try {
      return { ok: true, value: JSON.parse(text) };
    } catch (err) {
      return { ok: false, error: new Error(`Invalid JSON for ${label}: ${err && err.message ? err.message : String(err)}`) };
    }
  }

  function clamp01(x) {
    // Guard against NaN / +/-Infinity. Returning 0 is safer than allowing
    // NaN to leak into UI (progress/score) where it can render as "NaN".
    if (!Number.isFinite(x)) return 0;
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    return x;
  }

  function isFiniteNumber(n) {
    return typeof n === 'number' && Number.isFinite(n);
  }

  function toNumber(v) {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') {
      const t = v.trim();
      if (!t) return null;
      const n = Number(t);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }


  function cleanShareText(value) {
    return String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim();
  }

  function toHttpShareUrl(candidateUrl) {
    const raw = String(candidateUrl == null ? '' : candidateUrl).trim();
    const fallbackBase = (window.location && window.location.href) ? window.location.href : 'https://selecturns.com/';

    try {
      const url = new URL(raw || fallbackBase, fallbackBase);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
      // Do not include in-page state/fragments in social URLs.
      url.hash = '';
      return url.href;
    } catch (_) {
      return '';
    }
  }

  function currentQuizShareUrl() {
    return toHttpShareUrl(window.location && window.location.href ? window.location.href : '');
  }

  function resultBreedNameForSharing(breedView) {
    const name = cleanShareText(breedView && (breedView.breed_display_name || breedView.breed_name));
    return name || 'Dog Breed';
  }

  function resultShareUrl(breedView) {
    const articleUrl = breedView && breedView.article && breedView.article.url ? breedView.article.url : '';
    return toHttpShareUrl(articleUrl) || currentQuizShareUrl();
  }

  function buildDogResultShareData(resultEntry) {
    const breedView = resultEntry && resultEntry.breed ? resultEntry.breed : null;
    const breedName = resultBreedNameForSharing(breedView);
    const text = `The Best Dog Breed For Me Is ${breedName}!`;
    const url = resultShareUrl(breedView);
    return { breedName, text, url };
  }

  function buildDogResultShareTargets(shareData) {
    const text = cleanShareText(shareData && shareData.text);
    const url = toHttpShareUrl(shareData && shareData.url);
    if (!text && !url) return [];

    const xParams = new URLSearchParams();
    if (text) xParams.set('text', text);
    if (url) xParams.set('url', url);

    const threadsParams = new URLSearchParams();
    threadsParams.set('text', [text, url].filter(Boolean).join('\n\n'));

    const facebookParams = new URLSearchParams();
    if (url) facebookParams.set('u', url);

    const targets = [];
    if (url) {
      targets.push({
        id: 'facebook',
        label: 'Facebook',
        icon: 'f',
        href: `https://www.facebook.com/sharer/sharer.php?${facebookParams.toString()}`
      });
    }
    targets.push({
      id: 'x',
      label: 'X',
      icon: '𝕏',
      href: `https://x.com/intent/tweet?${xParams.toString()}`
    });
    targets.push({
      id: 'threads',
      label: 'Threads',
      icon: '@',
      href: `https://www.threads.com/intent/post?${threadsParams.toString()}`
    });

    return targets;
  }

  function fallbackCopyText(text) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const copied = document.execCommand('copy');
      textarea.remove();
      return Promise.resolve(!!copied);
    } catch (_) {
      return Promise.resolve(false);
    }
  }

  function copyShareText(text) {
    const value = String(text == null ? '' : text);
    if (!value) return Promise.resolve(false);

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      return navigator.clipboard.writeText(value).then(() => true).catch(() => fallbackCopyText(value));
    }

    return fallbackCopyText(value);
  }

  function createDogResultSharePanel(resultEntry) {
    const shareData = buildDogResultShareData(resultEntry);
    const targets = buildDogResultShareTargets(shareData);
    if (!targets.length) return null;

    const panel = document.createElement('section');
    panel.className = 'ml-9665-dbq-share';
    panel.setAttribute('aria-label', 'Share your top dog breed result');

    const copy = shareData.text;
    const copyWithUrl = shareData.url ? `${shareData.text}\n${shareData.url}` : shareData.text;

    const eyebrow = document.createElement('p');
    eyebrow.className = 'ml-9665-dbq-share__eyebrow';
    eyebrow.textContent = 'Share your top match';

    const headline = document.createElement('p');
    headline.className = 'ml-9665-dbq-share__text';
    headline.textContent = copy;

    const actions = document.createElement('div');
    actions.className = 'ml-9665-dbq-share__actions';

    for (const target of targets) {
      const link = document.createElement('a');
      link.className = `ml-9665-dbq-share__button ml-9665-dbq-share__button--${target.id}`;
      link.href = target.href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      const mark = document.createElement('span');
      mark.className = 'ml-9665-dbq-share__button-mark';
      mark.setAttribute('aria-hidden', 'true');
      mark.textContent = target.icon || target.label.charAt(0);

      const label = document.createElement('span');
      label.className = 'ml-9665-dbq-share__button-label';
      label.textContent = target.label;

      link.setAttribute('aria-label', `Share your top dog breed result on ${target.label}`);
      link.appendChild(mark);
      link.appendChild(label);
      actions.appendChild(link);
    }

    const copyButton = document.createElement('button');
    copyButton.className = 'ml-9665-dbq-share__button ml-9665-dbq-share__button--copy';
    copyButton.type = 'button';

    const copyMark = document.createElement('span');
    copyMark.className = 'ml-9665-dbq-share__button-mark';
    copyMark.setAttribute('aria-hidden', 'true');
    copyMark.textContent = '⧉';

    const copyLabel = document.createElement('span');
    copyLabel.className = 'ml-9665-dbq-share__button-label';
    copyLabel.textContent = 'Copy text';

    copyButton.appendChild(copyMark);
    copyButton.appendChild(copyLabel);

    const status = document.createElement('span');
    status.className = 'ml-9665-dbq-share__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    copyButton.addEventListener('click', () => {
      copyShareText(copyWithUrl).then((ok) => {
        status.textContent = ok ? 'Copied.' : 'Copy failed.';
      });
    });

    actions.appendChild(copyButton);

    panel.appendChild(eyebrow);
    panel.appendChild(headline);
    panel.appendChild(actions);
    panel.appendChild(status);

    return panel;
  }

  function roundPct(n) {
    return Math.round(n);
  }

  /**
   * Cross-browser child replacement.
   * - Uses replaceChildren() when available (fast, no HTML parsing).
   * - Falls back to manual removal.
   */
  function replaceChildrenSafe(el, ...kids) {
    if (!el) return;
    if (typeof el.replaceChildren === 'function') {
      // eslint-disable-next-line no-restricted-syntax
      el.replaceChildren(...kids.filter(Boolean));
      return;
    }
    while (el.firstChild) el.removeChild(el.firstChild);
    for (let i = 0; i < kids.length; i++) {
      const k = kids[i];
      if (k) el.appendChild(k);
    }
  }

  // -----------------------------
  // Spec helpers (getter + matches)
  // -----------------------------

  /** Safe dot-path getter. Returns null if any segment is missing. */
  function getValue(obj, path) {
    const parts = String(path || '').split('.');
    let cur = obj;
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      if (cur == null || typeof cur !== 'object' || !(p in cur)) return null;
      cur = cur[p];
    }
    return cur;
  }

  /**
   * Faster getter that uses pre-split path segments (avoids repeated String.split in hot loops).
   * Returns null if any segment is missing.
   */
  function getValueByParts(obj, parts) {
    let cur = obj;
    if (!parts || !parts.length) return null;
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      if (cur == null || typeof cur !== 'object') return null;
      // Using `in` is faster than hasOwnProperty in this hot path.
      if (!(p in cur)) return null;
      cur = cur[p];
    }
    return cur;
  }

  function matchTarget(v, target, divisor) {
    return clamp01(1 - Math.abs(v - target) / divisor);
  }

  function matchRange(v, min, max, pad) {
    if (v >= min && v <= max) return 1;
    const d = v < min ? (min - v) : (v - max);
    return clamp01(1 - d / pad);
  }

  function computeMatch(scale, v, pref) {
    if (!isFiniteNumber(v) || !pref) return null;

    if (pref.type === 'target') {
      const divisor = (scale === 'rating_1_10') ? 9 : 100;
      return matchTarget(v, pref.target, divisor);
    }

    if (pref.type === 'range') {
      return matchRange(v, pref.min, pref.max, pref.pad);
    }

    return null;
  }



  /**
   * Fast match helper for compiled traits.
   *
   * Supports both:
   * - New compiled traits (kind + precomputed inverses)
   * - Legacy compiled traits (pref objects) for backwards compatibility
   */
  function matchTraitValue(tr, v) {
    if (!tr || !isFiniteNumber(v)) return null;

    // New format (preferred): `kind`
    const kind = tr.kind;
    if (kind === 0) {
      // target
      const target = tr.target;
      const invDiv = tr.invDiv;
      if (!isFiniteNumber(target) || !isFiniteNumber(invDiv)) return null;
      let m = 1 - Math.abs(v - target) * invDiv;
      if (m <= 0) return 0;
      if (m >= 1) return 1;
      return m;
    }

    if (kind === 1) {
      // range
      const min = tr.min;
      const max = tr.max;
      const invPad = tr.invPad;
      if (!isFiniteNumber(min) || !isFiniteNumber(max) || !isFiniteNumber(invPad)) return null;
      if (v >= min && v <= max) return 1;
      const d = v < min ? (min - v) : (v - max);
      let m = 1 - d * invPad;
      if (m <= 0) return 0;
      if (m >= 1) return 1;
      return m;
    }

    // Legacy format fallback.
    if (tr.pref) return computeMatch(tr.scale, v, tr.pref);

    return null;
  }

  // -----------------------------
  // Compile the spec once (for speed)
  // -----------------------------

  let COMPILED_SPEC = null;

  function compileSpec(rawSpec) {
    const spec = rawSpec && typeof rawSpec === 'object' ? rawSpec : null;
    const nodes = Array.isArray(spec?.nodes) ? spec.nodes : [];

    const epsilon = toNumber(spec?.scoring_engine?.tie_break?.epsilon) ?? 0.5;
    const defaultMetric = typeof spec?.scoring_engine?.tie_break?.default_metric === 'string'
      ? spec.scoring_engine.tie_break.default_metric
      : 'computed.overall_rankings.overall_companion';

    // 1) Collect all field paths (for pre-flattening).
    const fieldSet = new Set();
    fieldSet.add(defaultMetric);

    let q50TieBreakMap = null;
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      if (!n || typeof n !== 'object') continue;

      if (n.key === 'Q50' && n.tie_break_map_by_option_id && typeof n.tie_break_map_by_option_id === 'object') {
        q50TieBreakMap = n.tie_break_map_by_option_id;
        for (const optId in q50TieBreakMap) {
          const metric = q50TieBreakMap[optId];
          if (typeof metric === 'string' && metric) fieldSet.add(metric);
        }
      }

      const traits = Array.isArray(n.scoring_traits) ? n.scoring_traits : [];
      for (let t = 0; t < traits.length; t++) {
        const tr = traits[t];
        const field = tr && typeof tr.field === 'string' ? tr.field : '';
        if (field) fieldSet.add(field);
      }
    }

    const fieldPaths = Array.from(fieldSet);
    const fieldIndex = Object.create(null);
    for (let i = 0; i < fieldPaths.length; i++) fieldIndex[fieldPaths[i]] = i;
    const fieldParts = fieldPaths.map((p) => String(p).split('.'));

    // 2) Compile each node into option-specific trait lists.
    const compiledNodes = [];
    const nodeIndex = Object.create(null);

    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      if (!n || typeof n !== 'object' || typeof n.key !== 'string') continue;

      const weight = toNumber(n.weight) ?? 0;
      const sectionName = typeof n?.section?.name === 'string' ? n.section.name : '';
      const sectionId = typeof n?.section?.id === 'string' ? n.section.id : '';

      const traits = Array.isArray(n.scoring_traits) ? n.scoring_traits : [];

      // Collect option IDs mentioned anywhere in the prefs.
      const optionIdSet = new Set();
      for (let t = 0; t < traits.length; t++) {
        const prefs = traits[t]?.prefs;
        if (!prefs || typeof prefs !== 'object') continue;
        for (const optId in prefs) optionIdSet.add(optId);
      }
      // Also add tie-break map option IDs if present (Q50).
      if (n.tie_break_map_by_option_id && typeof n.tie_break_map_by_option_id === 'object') {
        for (const optId in n.tie_break_map_by_option_id) optionIdSet.add(optId);
      }

      const options = Object.create(null);
      for (const optId of optionIdSet) {
        options[optId] = {
          scoreTraits: [],
          penaltyTraits: [],
          totalImportance: 0
        };
      }

      for (let t = 0; t < traits.length; t++) {
        const tr = traits[t];
        if (!tr || typeof tr !== 'object') continue;

        const field = typeof tr.field === 'string' ? tr.field : '';
        const fieldIdx = field ? fieldIndex[field] : undefined;
        const scale = typeof tr.scale === 'string' ? tr.scale : '';
        const importance = toNumber(tr.importance) ?? 0;
        const prefs = tr.prefs && typeof tr.prefs === 'object' ? tr.prefs : null;
        if (!prefs) continue;

        for (const optId in prefs) {
          const pref = prefs[optId];
          if (!pref || typeof pref !== 'object') continue;
          if (!(optId in options)) {
            options[optId] = { scoreTraits: [], penaltyTraits: [], totalImportance: 0 };
          }

          if (pref.type === 'filter_min') {
            const min = toNumber(pref.min);
            const penalty = toNumber(pref.penalty);
            const hard = !!pref.hard;
            if (fieldIdx != null && min != null && penalty != null) {
              options[optId].penaltyTraits.push({
                fieldIdx,
                min,
                penalty,
                hard
              });
            }
            continue;
          }

          if (pref.type === 'filter_max') {
            const max = toNumber(pref.max);
            const penalty = toNumber(pref.penalty);
            const hard = !!pref.hard;
            if (fieldIdx != null && max != null && penalty != null) {
              options[optId].penaltyTraits.push({
                fieldIdx,
                max,
                penalty,
                hard
              });
            }
            continue;
          }

          if (pref.type === 'target') {
            const target = toNumber(pref.target);
            if (fieldIdx != null && target != null) {
              // Hot-path optimization: precompute the divisor used by matchTarget().
              // - rating_1_10 uses 1..10 (range = 9)
              // - most other "grade"/"overall" values are on 0..100ish scales
              const divisor = (scale === 'rating_1_10') ? 9 : 100;
              options[optId].scoreTraits.push({
                fieldIdx,
                scale,
                importance,
                kind: 0, // target
                target,
                invDiv: 1 / Math.max(1e-6, divisor)
              });
              options[optId].totalImportance += importance;
            }
            continue;
          }

          if (pref.type === 'range') {
            const min = toNumber(pref.min);
            const max = toNumber(pref.max);
            const pad = toNumber(pref.pad);
            // Guard against invalid pads (would cause divide-by-zero and flatten discrimination).
            if (fieldIdx != null && min != null && max != null && pad != null && pad > 0) {
              options[optId].scoreTraits.push({
                fieldIdx,
                scale,
                importance,
                kind: 1, // range
                min,
                max,
                pad,
                invPad: 1 / pad
              });
              options[optId].totalImportance += importance;
            }
            continue;
          }
        }
      }

      const compiled = {
        id: toNumber(n.id) ?? null,
        key: n.key,
        weight,
        sectionId,
        sectionName,
        options,
        tieBreakMap: n.tie_break_map_by_option_id && typeof n.tie_break_map_by_option_id === 'object' ? n.tie_break_map_by_option_id : null
      };

      compiledNodes.push(compiled);
      nodeIndex[n.key] = compiled;
    }

    return {
      id: typeof spec?.id === 'string' ? spec.id : 'dog_breed_quiz_spec',
      schema: typeof spec?.schema === 'string' ? spec.schema : 'unknown',
      startNodeId: toNumber(spec?.start_node_id) ?? 1,
      resultToken: typeof spec?.result_token === 'string' ? spec.result_token : 'RESULT',
      tieBreak: { epsilon, defaultMetric },
      fieldPaths,
      fieldParts,
      fieldIndex,
      nodes: compiledNodes,
      nodeIndex
    };
  }


  function getPatchedSpecV4() {
    // Apply small, targeted patches without touching the full authoring JSON.
    // This keeps edits safe and localized, while improving match quality.
    let s = null;
    try {
      // Deep-clone so we never mutate the authoring constant.
      s = JSON.parse(JSON.stringify(DBQ_SPEC_V4));
    } catch (_) {
      s = DBQ_SPEC_V4;
    }

    if (!s || !Array.isArray(s.nodes)) return s;

    for (let i = 0; i < s.nodes.length; i++) {
      const n = s.nodes[i];
      if (!n || typeof n !== 'object') continue;
      const key = n.key;

      // Q32_A: "Top-tier kid-friendly (non-negotiable)" => treat as a hard constraint.
      if (key === 'Q32' && Array.isArray(n.scoring_traits)) {
        for (let t = 0; t < n.scoring_traits.length; t++) {
          const tr = n.scoring_traits[t];
          const prefs = tr && tr.prefs;
          if (!prefs || typeof prefs !== 'object') continue;
          const p = prefs['Q32_A'];
          if (p && typeof p === 'object' && p.type === 'filter_min') {
            p.min = 8;
            p.hard = true;
          }
        }
      }

      // Q35_A: "Must be very compatible—no chasing" => hard constraint on cat/small-pet compatibility.
      if (key === 'Q35' && Array.isArray(n.scoring_traits)) {
        for (let t = 0; t < n.scoring_traits.length; t++) {
          const tr = n.scoring_traits[t];
          const prefs = tr && tr.prefs;
          if (!prefs || typeof prefs !== 'object') continue;
          const p = prefs['Q35_A'];
          if (p && typeof p === 'object' && p.type === 'filter_min') {
            p.min = 7;
            p.hard = true;
          }
        }
      }

      // Q39: door/gate escape risk is one-sided (lower roam + higher recall is always better).
      if (key === 'Q39' && Array.isArray(n.scoring_traits)) {
        for (let t = 0; t < n.scoring_traits.length; t++) {
          const tr = n.scoring_traits[t];
          if (!tr || typeof tr !== 'object') continue;
          const field = tr.field;
          const prefs = tr.prefs;
          if (!prefs || typeof prefs !== 'object') continue;

          if (field === 'ratings_1_to_10.chase_wander_tendency') {
            if (prefs['Q39_A'] && prefs['Q39_A'].type === 'target') prefs['Q39_A'] = { type: 'range', min: 0, max: 3, pad: 3 };
            if (prefs['Q39_B'] && prefs['Q39_B'].type === 'target') prefs['Q39_B'] = { type: 'range', min: 0, max: 5, pad: 3 };
            if (prefs['Q39_C'] && prefs['Q39_C'].type === 'target') prefs['Q39_C'] = { type: 'range', min: 0, max: 6, pad: 4 };
          } else if (field === 'ratings_1_to_10.off_leash_reliability') {
            if (prefs['Q39_A'] && prefs['Q39_A'].type === 'target') prefs['Q39_A'] = { type: 'range', min: 7, max: 10, pad: 3 };
            if (prefs['Q39_B'] && prefs['Q39_B'].type === 'target') prefs['Q39_B'] = { type: 'range', min: 6, max: 10, pad: 3 };
            if (prefs['Q39_C'] && prefs['Q39_C'].type === 'target') prefs['Q39_C'] = { type: 'range', min: 5, max: 10, pad: 3 };
          }
        }
      }
    }

    // Mark patched specs (useful in debug logs).
    if (s && typeof s.id === 'string' && s.id.indexOf('_runtimePatch') === -1) {
      s.id = s.id + '_runtimePatch4';
    }

    return s;
  }

  function getCompiledSpec() {
    if (COMPILED_SPEC) return COMPILED_SPEC;
    const patched = getPatchedSpecV4();
    COMPILED_SPEC = compileSpec(patched);
    return COMPILED_SPEC;
  }


  // -----------------------------
  // Questions + answers helpers
  // -----------------------------

  function variantIndexForQuestion(variantMode, seed, questionId) {
    if (variantMode === 'random') {
      const h = fnv1a32(String(seed) + '|' + String(questionId));
      return h % 4;
    }
    const idx = VARIANT_TO_INDEX[variantMode];
    return typeof idx === 'number' && idx >= 0 ? idx : 1;
  }

  function getVariantText(variants, idx) {
    if (!Array.isArray(variants) || variants.length === 0) return '';
    const safeIdx = typeof idx === 'number' ? idx : 0;
    return variants[safeIdx] != null ? String(variants[safeIdx]) : String(variants[0]);
  }

  function optionLabelFallback(opt) {
    const label = opt && opt.label ? String(opt.label).trim() : '';
    return label ? `Option ${label}` : 'Option';
  }

  function buildQuestionMaps(questionsObj) {
    const byNode = new Map();
    const byId = new Map();

    if (!questionsObj || typeof questionsObj !== 'object' || !Array.isArray(questionsObj.questions)) {
      return { ok: false, error: new Error('Questions JSON missing `questions` array.') };
    }

    for (let i = 0; i < questionsObj.questions.length; i++) {
      const q = questionsObj.questions[i];
      if (!q || typeof q !== 'object') continue;
      if (!isFiniteNumber(q.node_id)) continue;
      if (typeof q.question_id !== 'string') continue;

      // Pre-index options for O(1) lookups (avoids repeated Array.find() in hot paths).
      // Stored as a non-enumerable property so it won't leak into theme JSON.
      if (Array.isArray(q.options)) {
        const optById = Object.create(null);
        for (let j = 0; j < q.options.length; j++) {
          const o = q.options[j];
          if (o && typeof o.option_id === 'string') optById[o.option_id] = o;
        }
        try {
          Object.defineProperty(q, '__optById', { value: optById, enumerable: false });
        } catch (_) {
          q.__optById = optById;
        }
      }

      byNode.set(q.node_id, q);
      byId.set(q.question_id, q);
    }

    const start = questionsObj.start_node_id;
    if (!isFiniteNumber(start) || !byNode.has(start)) {
      return { ok: false, error: new Error('Questions JSON has an invalid `start_node_id`.') };
    }

    return { ok: true, byNode, byId, startNodeId: start, resultToken: questionsObj.result_token || 'RESULT' };
  }

  function buildAnswerMap(answersObj) {
    if (!answersObj || typeof answersObj !== 'object' || !answersObj.answers_by_option_id) {
      return { ok: false, error: new Error('Answers JSON missing `answers_by_option_id`.') };
    }
    const map = answersObj.answers_by_option_id;
    if (!map || typeof map !== 'object') {
      return { ok: false, error: new Error('Answers JSON has invalid `answers_by_option_id`.') };
    }
    return { ok: true, map };
  }


  function getEmbeddedDbqQuestionsV2() {
    if (typeof window === 'undefined') return null;
    const data = window.DOG_BREED_QUIZ_COPY_QUESTIONS_V2;
    if (!data || typeof data !== 'object' || !Array.isArray(data.questions)) return null;
    return data;
  }

  function getEmbeddedDbqAnswersV2() {
    if (typeof window === 'undefined') return null;
    const data = window.DOG_BREED_QUIZ_COPY_ANSWERS_V2;
    if (!data || typeof data !== 'object' || !data.answers_by_option_id || typeof data.answers_by_option_id !== 'object') return null;
    return data;
  }

  function averageFinite01(values, fallback) {
    let sum = 0;
    let count = 0;
    if (Array.isArray(values)) {
      for (let i = 0; i < values.length; i++) {
        const v = values[i];
        if (isFiniteNumber(v)) {
          sum += clamp01(v);
          count += 1;
        }
      }
    }
    if (count === 0) return isFiniteNumber(fallback) ? clamp01(fallback) : 0.5;
    return clamp01(sum / count);
  }

  function readNumericPath(obj, path) {
    const n = toNumber(getValue(obj, path));
    return n == null ? null : n;
  }

  function normalizeToUnitValue(value, scale) {
    if (!isFiniteNumber(value)) return null;
    if (scale === 'rating_1_10') return clamp01(value / 10);
    if (scale === 'grade_0_100' || scale === 'overall_0_100ish') return clamp01(value / 100);
    return clamp01(value);
  }

  function normalizeWeightToUnit(weightLbs) {
    if (!isFiniteNumber(weightLbs)) return null;
    const min = 5;
    const max = 150;
    return clamp01((weightLbs - min) / (max - min));
  }

  function stableBreedIdFromRaw(rawBreed, fallbackName) {
    const seed = String(
      (rawBreed && (
        rawBreed.breed_name ||
        rawBreed.handle ||
        rawBreed.breed_display_name ||
        rawBreed.title ||
        rawBreed.name
      )) ||
      fallbackName ||
      'dog-breed'
    ).trim().toLowerCase();

    return seed
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'dog-breed';
  }

  function makeDisplayBreedView(rawBreed, fallbackName) {
    const breedName = String(
      (rawBreed && (
        rawBreed.breed_name ||
        rawBreed.handle ||
        rawBreed.breed_display_name ||
        rawBreed.title ||
        rawBreed.name
      )) || fallbackName || 'Dog breed'
    );
    const displayName = String((rawBreed && (rawBreed.breed_display_name || rawBreed.breed_name)) || fallbackName || breedName);
    const rawArticle = (rawBreed && rawBreed.article && typeof rawBreed.article === 'object') ? rawBreed.article : null;
    const article = rawArticle ? {
      url: normalizeUrlValue(rawArticle.url),
      title: typeof rawArticle.title === 'string' ? rawArticle.title : displayName,
      image: normalizeImageValue(rawArticle.image)
    } : { url: '', title: displayName, image: '' };

    return {
      breed_name: breedName,
      breed_display_name: displayName,
      sortKey: breedName.toLowerCase(),
      article,
      raw: rawBreed || null
    };
  }

  function makeV2BreedAdapter(rawBreed) {
    const title = String(
      (rawBreed && (
        rawBreed.breed_display_name ||
        rawBreed.breed_name ||
        rawBreed.title ||
        rawBreed.name ||
        rawBreed.handle
      )) || 'Dog breed'
    );

    const weightMid = readNumericPath(rawBreed, 'computed.midpoints.weight_lbs_mid');
    const size = normalizeWeightToUnit(weightMid);

    const apartmentGrade = normalizeToUnitValue(readNumericPath(rawBreed, 'computed.grades.apartment_dog.grade_0_100'), 'grade_0_100');
    const apartmentRating = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.apartment_adaptability'), 'rating_1_10');
    const calmness = normalizeToUnitValue(readNumericPath(rawBreed, 'computed.calmness.grade_0_100'), 'grade_0_100');
    const childrenGrade = normalizeToUnitValue(readNumericPath(rawBreed, 'computed.grades.children.grade_0_100'), 'grade_0_100');
    const energyGrade = normalizeToUnitValue(readNumericPath(rawBreed, 'computed.grades.energy_activity.grade_0_100'), 'grade_0_100');
    const cleanHomeGrade = normalizeToUnitValue(readNumericPath(rawBreed, 'computed.grades.grooming_clean_home.grade_0_100'), 'grade_0_100');
    const guardGrade = normalizeToUnitValue(readNumericPath(rawBreed, 'computed.grades.guard_watch.grade_0_100'), 'grade_0_100');
    const multiPetGrade = normalizeToUnitValue(readNumericPath(rawBreed, 'computed.grades.multi_pet.grade_0_100'), 'grade_0_100');
    const smartGrade = normalizeToUnitValue(readNumericPath(rawBreed, 'computed.grades.smart.grade_0_100'), 'grade_0_100');
    const trainingGrade = normalizeToUnitValue(readNumericPath(rawBreed, 'computed.grades.training.grade_0_100'), 'grade_0_100');
    const overallAdventure = normalizeToUnitValue(readNumericPath(rawBreed, 'computed.overall_rankings.overall_adventure'), 'overall_0_100ish');
    const overallCompanion = normalizeToUnitValue(readNumericPath(rawBreed, 'computed.overall_rankings.overall_companion'), 'overall_0_100ish');
    const overallProtection = normalizeToUnitValue(readNumericPath(rawBreed, 'computed.overall_rankings.overall_protection'), 'overall_0_100ish');

    const barking = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.barking_vocalness'), 'rating_1_10');
    const odor = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.bathing_odor_management_needs'), 'rating_1_10');
    const catCompatibilityRating = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.cat_small_pet_compatibility'), 'rating_1_10');
    const chaseWander = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.chase_wander_tendency'), 'rating_1_10');
    const groomingNeed = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.coat_maintenance_grooming_needs'), 'rating_1_10');
    const coldTolerance = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.cold_tolerance'), 'rating_1_10');
    const exerciseRating = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.daily_exercise_needs'), 'rating_1_10');
    const destructiveness = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.destructiveness_risk'), 'rating_1_10');
    const dogFriendliness = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.dog_friendliness'), 'rating_1_10');
    const drooling = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.drooling'), 'rating_1_10');
    const firstTime = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.ease_for_first_time_owners'), 'rating_1_10');
    const energyLevel = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.energy_level'), 'rating_1_10');
    const affection = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.family_affection'), 'rating_1_10');
    const retrieveDrive = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.fetch_retrieve_drive'), 'rating_1_10');
    const protectiveness = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.guarding_protectiveness'), 'rating_1_10');
    const healthRobustness = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.health_robustness'), 'rating_1_10');
    const heatTolerance = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.heat_tolerance'), 'rating_1_10');
    const allergyFriendliness = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.hypoallergenic_tendency'), 'rating_1_10');
    const intelligence = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.intelligence_problem_solving'), 'rating_1_10');
    const kidFriendliness = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.kid_friendliness'), 'rating_1_10');
    const mouthiness = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.mouthiness_nipping_tendency'), 'rating_1_10');
    const offLeash = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.off_leash_reliability'), 'rating_1_10');
    const playfulness = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.playfulness'), 'rating_1_10');
    const preyDrive = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.prey_drive'), 'rating_1_10');
    const responsiveness = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.responsiveness_to_training'), 'rating_1_10');
    const sensitivity = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.sensitivity'), 'rating_1_10');
    const shedding = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.shedding'), 'rating_1_10');
    const strangerFriendliness = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.stranger_friendliness'), 'rating_1_10');
    const pullingPower = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.strength_pulling_power'), 'rating_1_10');
    const aloneTolerance = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.tolerance_for_being_alone'), 'rating_1_10');
    const trainability = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.trainability'), 'rating_1_10');
    const alertness = normalizeToUnitValue(readNumericPath(rawBreed, 'ratings_1_to_10.watchdog_alertness'), 'rating_1_10');

    const apartmentFriendly = averageFinite01([apartmentGrade, apartmentRating, calmness, isFiniteNumber(barking) ? 1 - barking : null], 0.5);
    const exerciseNeed = averageFinite01([exerciseRating, energyLevel, energyGrade], 0.5);
    const trainingFit = averageFinite01([trainability, responsiveness, trainingGrade], 0.5);
    const mentalNeed = averageFinite01([intelligence, smartGrade, trainingFit], 0.5);
    const workingDrive = averageFinite01([overallAdventure, energyGrade, retrieveDrive, trainingGrade], 0.5);
    const dogSociability = averageFinite01([dogFriendliness, multiPetGrade], 0.5);
    const childFriendliness = averageFinite01([kidFriendliness, childrenGrade], 0.5);
    const catCompatibility = averageFinite01([catCompatibilityRating, multiPetGrade, isFiniteNumber(preyDrive) ? 1 - preyDrive : null], 0.5);
    const noviceFriendliness = averageFinite01([firstTime, overallCompanion, apartmentFriendly, isFiniteNumber(protectiveness) ? 1 - protectiveness : null], 0.5);
    const confidence = averageFinite01([isFiniteNumber(sensitivity) ? 1 - sensitivity : null, calmness, isFiniteNumber(chaseWander) ? 1 - (chaseWander * 0.35) : null], 0.5);
    const guestEase = averageFinite01([strangerFriendliness, calmness, isFiniteNumber(protectiveness) ? 1 - protectiveness : null], 0.5);
    const toddlerTolerance = averageFinite01([childFriendliness, calmness, isFiniteNumber(mouthiness) ? 1 - mouthiness : null], 0.5);
    const aloneTimeTolerance = averageFinite01([aloneTolerance, calmness, isFiniteNumber(barking) ? 1 - barking : null], 0.5);
    const indoorCalm = averageFinite01([calmness, isFiniteNumber(energyLevel) ? 1 - energyLevel : null], 0.5);
    const power = averageFinite01([pullingPower, size], 0.5);
    const handlerFocus = averageFinite01([responsiveness, trainability, trainingGrade], 0.5);
    const independence = averageFinite01([aloneTolerance, isFiniteNumber(affection) ? 1 - affection : null], 0.5);
    const odorBurden = averageFinite01([odor, isFiniteNumber(cleanHomeGrade) ? 1 - cleanHomeGrade : null], 0.5);

    return {
      id: stableBreedIdFromRaw(rawBreed, title),
      name: title,
      size: averageFinite01([size], 0.5),
      power,
      exerciseNeed,
      mentalNeed,
      workingDrive,
      indoorCalm,
      independence,
      aloneTimeTolerance,
      apartmentFriendly,
      barkiness: averageFinite01([barking, alertness && barking != null ? (barking * 0.8) : null], 0.5),
      alertness: averageFinite01([alertness, guardGrade], 0.5),
      protectiveness: averageFinite01([protectiveness, overallProtection, guardGrade], 0.5),
      strangerFriendliness: averageFinite01([strangerFriendliness], 0.5),
      guestEase,
      dogSociability,
      childFriendliness,
      toddlerTolerance,
      catCompatibility,
      preyDrive: averageFinite01([preyDrive], 0.5),
      recallPotential: averageFinite01([offLeash, handlerFocus, isFiniteNumber(chaseWander) ? 1 - chaseWander : null], 0.5),
      roamingRisk: averageFinite01([chaseWander], 0.5),
      trainability: trainingFit,
      handlerFocus,
      confidence,
      sensitivity: averageFinite01([sensitivity], 0.5),
      cuddliness: averageFinite01([affection, overallCompanion], 0.5),
      playfulness: averageFinite01([playfulness, energyLevel], 0.5),
      retrieveDrive: averageFinite01([retrieveDrive], 0.5),
      mouthiness: averageFinite01([mouthiness], 0.5),
      chewingRisk: averageFinite01([destructiveness, isFiniteNumber(mouthiness) ? mouthiness * 0.45 : null], 0.5),
      noviceFriendliness,
      shedding: averageFinite01([shedding], 0.5),
      groomingNeed: averageFinite01([groomingNeed, isFiniteNumber(cleanHomeGrade) ? 1 - cleanHomeGrade : null], 0.5),
      drool: averageFinite01([drooling], 0.5),
      odor: odorBurden,
      allergyFriendliness: averageFinite01([allergyFriendliness, isFiniteNumber(shedding) ? 1 - shedding : null], 0.5),
      healthRobustness: averageFinite01([healthRobustness], 0.5),
      heatTolerance: averageFinite01([heatTolerance], 0.5),
      coldTolerance: averageFinite01([coldTolerance], 0.5)
    };
  }

  function getDbqV2Engine() {
    if (typeof window === 'undefined') return null;
    const engine = window.DogQuizRankingEngine;
    if (!engine || typeof engine.rankBreeds !== 'function') return null;
    return engine;
  }

  // -----------------------------
  // Breed preprocessing (dedupe + flatten fields)
  // -----------------------------

  function normalizeUrlValue(v) {
    if (typeof v === 'string') return v;
    if (!v || typeof v !== 'object') return '';
    // Some Shopify objects expose url/src directly.
    if (typeof v.url === 'string') return v.url;
    if (typeof v.src === 'string') return v.src;
    return '';
  }

  function normalizeImageValue(v) {
    if (typeof v === 'string') return v;
    if (!v || typeof v !== 'object') return '';
    // Common Shopify image shapes.
    if (typeof v.src === 'string') return v.src;
    if (typeof v.url === 'string') return v.url;
    if (v.preview_image) return normalizeImageValue(v.preview_image);
    if (v.image) return normalizeImageValue(v.image);
    if (v.featured_image) return normalizeImageValue(v.featured_image);
    return '';
  }

  function preprocessBreeds(rawBreeds, compiledSpec) {
    const out = [];
    if (!Array.isArray(rawBreeds)) return out;

    const seen = new Set();
    const fieldPaths = Array.isArray(compiledSpec.fieldPaths) ? compiledSpec.fieldPaths : [];
    const fieldParts = Array.isArray(compiledSpec.fieldParts) ? compiledSpec.fieldParts : fieldPaths.map((p) => String(p).split('.'));

    for (let i = 0; i < rawBreeds.length; i++) {
      const b = rawBreeds[i];
      if (!b || typeof b !== 'object') continue;

      const breedName = String(b.breed_name || '').trim();
      if (!breedName) continue;
      const dedupeKey = breedName.toLowerCase();
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);

      const displayName = String(b.breed_display_name || breedName);

      const rawArticle = (b.article && typeof b.article === 'object') ? b.article : null;
      const article = rawArticle ? {
        url: normalizeUrlValue(rawArticle.url),
        title: typeof rawArticle.title === 'string' ? rawArticle.title : displayName,
        image: normalizeImageValue(rawArticle.image)
      } : { url: '', title: displayName, image: '' };

      // Flatten only the fields referenced by the scoring spec (plus tie-break metrics).
      const values = new Float32Array(fieldPaths.length);
      for (let p = 0; p < fieldPaths.length; p++) {
        const raw = getValueByParts(b, fieldParts[p]);
        const num = toNumber(raw);
        values[p] = (num == null) ? NaN : num;
      }

      out.push({
        breed_name: breedName,
        breed_display_name: displayName,
        // Cached key used for fast alphabetical tie-break sorting.
        sortKey: dedupeKey,
        article,
        values
      });
    }

    return out;
  }

  // -----------------------------
  // Scoring (contract: Spec v4)
  // -----------------------------

  /**
   * Build a compact scoring plan for the current run.
   *
   * Performance note:
   * - The spec contains 50 nodes.
   * - In the short flow, we intentionally ask far fewer questions (typically 12–16).
   * - We compile only the answered nodes into a plan, then score breeds against that plan.
   */
  function buildScoringPlan(spec, answersByQuestionKey) {
    const items = [];
    let maxPossible = 0;

    const ans = (answersByQuestionKey && typeof answersByQuestionKey === 'object')
      ? answersByQuestionKey
      : Object.create(null);

    for (let i = 0; i < spec.nodes.length; i++) {
      const node = spec.nodes[i];
      const selectedOptionId = ans[node.key];
      if (!selectedOptionId) continue;

      const opt = node.options[selectedOptionId];
      if (!opt) continue;

      const scoreTraits = (opt.scoreTraits && opt.scoreTraits.length) ? opt.scoreTraits : null;
      const penaltyTraits = (opt.penaltyTraits && opt.penaltyTraits.length) ? opt.penaltyTraits : null;
      if (!scoreTraits && !penaltyTraits) continue;

      items.push({
        key: node.key,
        weight: node.weight,
        tag: tagLabelForNode(node.key, selectedOptionId),
        // Store compiled trait lists directly to avoid per-breed nested property lookups.
        scoreTraits,
        penaltyTraits,
        // Helpful for debugging / future extensions (kept small; not used in hot loops).
        optionId: selectedOptionId
      });

      // Max possible is only based on scoring contribution (penalties do not increase max score).
      if (node.weight > 0 && scoreTraits && scoreTraits.length) maxPossible += node.weight;
    }

    if (!(maxPossible > 0)) maxPossible = 1;

    return { items, maxPossible };
  }

  function scoreBreedFromPlan(plan, breed) {
    const values = breed && breed.values;
    if (!values) return 0;
    let score = 0;
    let penalty = 0;

    const items = plan && Array.isArray(plan.items) ? plan.items : [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // 1) penalties (filter_min)
      const pTraits = item.penaltyTraits;
      if (pTraits && pTraits.length) {
        for (let p = 0; p < pTraits.length; p++) {
          const pt = pTraits[p];
          const v = values[pt.fieldIdx];

          // Hard filters immediately disqualify (e.g., allergy must-have).
          // Soft filters apply a score penalty but do not auto-disqualify.
          if (v !== v) {
            if (pt.hard) return -1e9;
            penalty += pt.penalty;
            continue;
          }

          // Support both lower-bound and upper-bound filters.
          if (pt.min != null && v < pt.min) {
            if (pt.hard) return -1e9;
            penalty += pt.penalty;
          } else if (pt.max != null && v > pt.max) {
            if (pt.hard) return -1e9;
            penalty += pt.penalty;
          }
        }
      }

      // 2) match scoring
      const sTraits = item.scoreTraits;
      if (!sTraits || sTraits.length === 0) continue;

      let num = 0;
      let den = 0;

      for (let t = 0; t < sTraits.length; t++) {
        const tr = sTraits[t];
        const v = values[tr.fieldIdx];
        if (v !== v) {
          // Missing values should never provide a "free" advantage by shrinking the denominator.
          // Treat missing as neutral (0.5 match) so incomplete data doesn't float to the top.
          const imp = tr.importance;
          if (imp > 0) {
            num += imp * 0.5;
            den += imp;
          }
          continue;
        }

        let m = null;
        // Hot path: avoid function calls inside the scoring inner loop.
        if (tr.kind === 0) {
          // target
          const target = tr.target;
          const invDiv = tr.invDiv;
          if (target === target && invDiv === invDiv) {
            m = 1 - Math.abs(v - target) * invDiv;
            if (m <= 0) m = 0;
            else if (m >= 1) m = 1;
          }
        } else if (tr.kind === 1) {
          // range
          const min = tr.min;
          const max = tr.max;
          const invPad = tr.invPad;
          if (min === min && max === max && invPad === invPad) {
            if (v >= min && v <= max) {
              m = 1;
            } else {
              const d = v < min ? (min - v) : (v - max);
              m = 1 - d * invPad;
              if (m <= 0) m = 0;
              else if (m >= 1) m = 1;
            }
          }
        } else {
          // Fallback (legacy compiled traits).
          m = matchTraitValue(tr, v);
        }

        if (m == null) continue;
        num += tr.importance * m;
        den += tr.importance;
      }

      if (den > 0) {
        score += item.weight * (num / den);
      }
    }

    return score - penalty;
  }

  function getTieBreakMetric(spec, answersByQuestionKey) {
    const sel = answersByQuestionKey['Q50'];
    const q50 = spec.nodeIndex['Q50'];
    if (sel && q50 && q50.tieBreakMap && typeof q50.tieBreakMap[sel] === 'string') {
      return q50.tieBreakMap[sel];
    }
    return spec.tieBreak.defaultMetric;
  }

  function getMetricValue(spec, breed, metricPath) {
    const idx = spec.fieldIndex[metricPath];
    if (idx != null) {
      const v = breed.values[idx];
      return isFiniteNumber(v) ? v : null;
    }

    // Backwards-compatible fallback (older builds kept the full raw object).
    if (breed && breed.raw) {
      const v2 = toNumber(getValue(breed.raw, metricPath));
      return v2 == null ? null : v2;
    }

    return null;
  }

  function applyTopTieBreak(spec, scored, answersByQuestionKey) {
    if (!Array.isArray(scored) || scored.length < 2) return;

    const a = scored[0];
    const b = scored[1];
    if (!a || !b) return;

    // IMPORTANT:
    // Never let the tie-break logic override a visible score difference.
    // The site displays integer score percentages (0-100), so keep strict
    // descending score order unless the top scores are effectively identical.
    if (a.scorePct != null && b.scorePct != null && a.scorePct !== b.scorePct) return;

    const specEps = (spec && spec.tieBreak && isFiniteNumber(spec.tieBreak.epsilon)) ? spec.tieBreak.epsilon : 0;
    const eps = Math.min(specEps, 1e-9);
    if (Math.abs(a.score - b.score) > eps) return;

    const metric = getTieBreakMetric(spec, answersByQuestionKey);
    const va = getMetricValue(spec, a.breed, metric);
    const vb = getMetricValue(spec, b.breed, metric);

    if (va != null && vb != null && va !== vb) {
      if (vb > va) {
        scored[0] = b;
        scored[1] = a;
      }
      return;
    }

    const nameA = String(a.breed.breed_name || '');
    const nameB = String(b.breed.breed_name || '');
    if (nameA.localeCompare(nameB) > 0) {
      scored[0] = b;
      scored[1] = a;
    }
  }


  function compareScoredEntries(a, b) {
    // Negative => a ranks ahead of b
    if (a.score > b.score) return -1;
    if (a.score < b.score) return 1;
    const an = String((a.breed && (a.breed.sortKey || a.breed.breed_name)) || '');
    const bn = String((b.breed && (b.breed.sortKey || b.breed.breed_name)) || '');
    return an.localeCompare(bn);
  }

  /**
   * Small top-K accumulator.
   *
   * Why this exists:
   * - The breed list can be large.
   * - K is tiny (usually 5).
   * - Allocating an object per breed is unnecessary GC pressure on mobile.
   *
   * This collector only allocates result objects for candidates that actually enter
   * the top-K set.
   */
  function createTopKCollector(k, invMax) {
    const heap = [];

    const keyForBreed = (breed) => String((breed && (breed.sortKey || breed.breed_name)) || '');
    const scorePctFor = (score) => roundPct(clamp01(score * invMax) * 100);

    // "Better" means it should rank ahead in the final output.
    function betterThan(score, key, entry) {
      if (!entry) return true;
      const es = entry.score;
      if (score > es) return true;
      if (score < es) return false;
      // Ties: earlier alphabetical key wins.
      return key.localeCompare(entry.key) < 0;
    }

    // "Worse" means it should be closer to the root of the min-heap.
    // Root is the worst of the current top-K.
    function worseThan(a, b) {
      if (a.score < b.score) return true;
      if (a.score > b.score) return false;
      // For ties, later alphabetical key is "worse".
      return a.key.localeCompare(b.key) > 0;
    }

    function siftUp(i) {
      while (i > 0) {
        const p = (i - 1) >> 1;
        if (!worseThan(heap[i], heap[p])) break;
        const tmp = heap[i];
        heap[i] = heap[p];
        heap[p] = tmp;
        i = p;
      }
    }

    function siftDown(i) {
      const n = heap.length;
      while (true) {
        const l = (i << 1) + 1;
        if (l >= n) break;
        const r = l + 1;
        let w = l;
        if (r < n && worseThan(heap[r], heap[l])) w = r;
        if (!worseThan(heap[w], heap[i])) break;
        const tmp = heap[i];
        heap[i] = heap[w];
        heap[w] = tmp;
        i = w;
      }
    }

    function consider(breed, score) {
      // Defensive: never allow NaN/Infinity into ranking.
      if (!Number.isFinite(score)) score = -1e9;
      const key = keyForBreed(breed);

      if (heap.length < k) {
        heap.push({ breed, score, key });
        siftUp(heap.length - 1);
        return;
      }

      // Replace root only if the candidate is better than the worst current entry.
      if (betterThan(score, key, heap[0])) {
        heap[0] = { breed, score, key };
        siftDown(0);
      }
    }

    function finalize(spec, answersByQuestionKey) {
      // Convert heap to a sorted array and compute display fields lazily.
      const out = new Array(heap.length);
      for (let i = 0; i < heap.length; i++) {
        const e = heap[i];
        out[i] = { breed: e.breed, score: e.score, scorePct: scorePctFor(e.score) };
      }

      out.sort(compareScoredEntries);
      applyTopTieBreak(spec, out, answersByQuestionKey);
      return out;
    }

    return { consider, finalize };
  }

  function scoreAllBreeds(spec, breeds, answersByQuestionKey, resultsCount) {
    const k = Math.max(1, resultsCount || 5);

    const plan = buildScoringPlan(spec, answersByQuestionKey);
    const maxPossible = plan.maxPossible > 0 ? plan.maxPossible : 1;
    const invMax = 1 / maxPossible;

    const collector = createTopKCollector(k, invMax);

    for (let i = 0; i < breeds.length; i++) {
      const breed = breeds[i];
      const s = scoreBreedFromPlan(plan, breed);
      collector.consider(breed, s);
    }

    const scored = collector.finalize(spec, answersByQuestionKey);

    return { maxPossible, plan, scored };
  }

  function tagLabelForNode(nodeKey, selectedOptionId) {
    // Keep tags short (they render as small rectangles in results).
    switch (nodeKey) {
      case 'Q01':
        if (selectedOptionId === 'Q01_A') return 'Apartment-friendly';
        return 'Home setup fit';
      case 'Q07':
        return 'Containment fit';
      case 'Q02':
      case 'Q05':
      case 'Q24':
        return 'Barking level fit';
      case 'Q04':
      case 'Q23':
        if (selectedOptionId === 'Q04_A' || selectedOptionId === 'Q04_B' || selectedOptionId === 'Q23_A' || selectedOptionId === 'Q23_B') {
          return 'Calm indoors';
        }
        return 'Energy match';
      case 'Q10':
        return 'Climate fit';
      case 'Q11':
      case 'Q13':
        return 'Size match';
      case 'Q12':
        return 'Leash handling fit';
      case 'Q14':
      case 'Q15':
      case 'Q03':
        return 'Alone-time fit';
      case 'Q16':
      case 'Q17':
      case 'Q18':
        return 'Activity match';
      case 'Q19':
      case 'Q20':
        return 'Experience fit';
      case 'Q21':
      case 'Q22':
        return 'Trainability match';
      case 'Q27':
        return 'Affection match';
      case 'Q28':
        return 'Play style match';
      case 'Q29':
      case 'Q30':
      case 'Q31':
      case 'Q32':
        return 'Great with kids';
      case 'Q33':
      case 'Q34':
      case 'Q36':
        return 'Multi-pet friendly';
      case 'Q35':
        return 'Cat/small-pet friendly';
      case 'Q37':
      case 'Q39':
        return 'Low chase/roam';
      case 'Q38':
        return 'Recall fit';
      case 'Q40':
      case 'Q43':
        return 'Stranger-friendly';
      case 'Q41':
      case 'Q42':
        return 'Watch/guard fit';
      case 'Q44':
        if (selectedOptionId === 'Q44_A') return 'Low shedding';
        if (selectedOptionId === 'Q44_B') return 'Some shedding OK';
        return 'Shedding fit';
      case 'Q45':
        return 'Grooming fit';
      case 'Q46':
        return 'Low drool fit';
      case 'Q47':
        return 'Odor/bathing fit';
      case 'Q48':
        if (selectedOptionId === 'Q48_A' || selectedOptionId === 'Q48_B') return 'Allergy-friendly';
        return 'Allergy preferences';
      case 'Q49':
        return 'Health & longevity';
      default:
        return null;
    }
  }

  function buildWhyTagsFromPlan(plan, breed, maxTags) {
    const limit = Math.max(1, maxTags || 3);

    const items = plan && Array.isArray(plan.items) ? plan.items : [];
    const tagScores = Object.create(null);
    const tagFirstSeen = Object.create(null);
    let seenIdx = 0;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const tag = item.tag;
      if (!tag) continue;

      const scoreTraits = item.scoreTraits;
      if (!scoreTraits || scoreTraits.length === 0) continue;

      // Compute the same normalized contribution used in scoring.
      let num = 0;
      let den = 0;
      for (let t = 0; t < scoreTraits.length; t++) {
        const tr = scoreTraits[t];
        const v = breed.values[tr.fieldIdx];
        if (v !== v) continue;
        const m = matchTraitValue(tr, v);
        if (m == null) continue;
        num += tr.importance * m;
        den += tr.importance;
      }
      if (den <= 0) continue;

      const contribution = item.weight * (num / den);
      if (!(contribution > 0)) continue;

      if (tagFirstSeen[tag] == null) {
        tagFirstSeen[tag] = seenIdx++;
        tagScores[tag] = contribution;
      } else if (contribution > tagScores[tag]) {
        // Keep the strongest supporting node for this tag.
        tagScores[tag] = contribution;
      }
    }

    const entries = Object.keys(tagScores)
      .map((tag) => ({ tag, v: tagScores[tag], o: tagFirstSeen[tag] }))
      .filter((x) => isFiniteNumber(x.v) && x.v > 0)
      .sort((a, b) => {
        if (b.v !== a.v) return b.v - a.v;
        return a.o - b.o;
      });

    const out = entries.slice(0, limit).map((x) => x.tag);
    if (out.length >= limit) return out;

    const fallback = ['Lifestyle fit', 'Temperament', 'Grooming'];
    for (let i = 0; i < fallback.length && out.length < limit; i++) {
      if (!out.includes(fallback[i])) out.push(fallback[i]);
    }
    return out;
  }

  // -----------------------------
  // UI helpers
  // -----------------------------

  function createAnswerNode({ domIdPrefix, qid, option, text, checked }) {
    const wrap = document.createElement('div');
    wrap.className = 'ml-9665-answer';

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = `${domIdPrefix}-${qid}`;
    input.value = option.option_id;
    input.id = `${domIdPrefix}-${option.option_id}`;
    if (checked) input.checked = true;

    const card = document.createElement('label');
    card.className = 'ml-9665-answer-card';
    card.setAttribute('for', input.id);

    const key = document.createElement('span');
    key.className = 'ml-9665-answer-card__key';
    key.textContent = option.label;

    const tick = document.createElement('span');
    tick.className = 'ml-9665-answer-card__tick';
    tick.setAttribute('aria-hidden', 'true');

    const body = document.createElement('span');
    body.className = 'ml-9665-answer-card__text';
    body.textContent = text;

    card.appendChild(key);
    card.appendChild(tick);
    card.appendChild(body);

    wrap.appendChild(input);
    wrap.appendChild(card);
    return wrap;
  }


  function ensureLoaderOverlay(root) {
    if (!root) return null;
    const existing = root.querySelector('[data-ml-9665-dbq-loader]');
    if (existing) return existing;

    const loader = document.createElement('div');
    loader.className = 'ml-9665-dbq-loader';
    loader.hidden = true;
    loader.setAttribute('data-ml-9665-dbq-loader', '');

    const panel = document.createElement('div');
    panel.className = 'ml-9665-dbq-loader__panel';
    panel.setAttribute('role', 'status');
    panel.setAttribute('aria-live', 'polite');
    panel.setAttribute('aria-label', 'Loading');

    const spinner = document.createElement('div');
    spinner.className = 'ml-9665-dbq-loader__spinner';
    spinner.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 3; i++) {
      spinner.appendChild(document.createElement('span'));
    }

    const msg = document.createElement('div');
    msg.className = 'ml-9665-dbq-loader__text';
    msg.setAttribute('data-ml-9665-dbq-loader-text', '');
    msg.textContent = 'Loading The Best Dog Breeds For You.';

    const bar = document.createElement('div');
    bar.className = 'ml-9665-dbq-loader__bar';
    bar.setAttribute('aria-hidden', 'true');

    const fill = document.createElement('i');
    bar.appendChild(fill);

    const pct = document.createElement('div');
    pct.className = 'ml-9665-dbq-loader__pct';
    pct.setAttribute('data-ml-9665-dbq-loader-pct', '');
    pct.textContent = '0%';

    panel.appendChild(spinner);
    panel.appendChild(msg);
    panel.appendChild(bar);
    panel.appendChild(pct);
    loader.appendChild(panel);
    root.appendChild(loader);

    return loader;
  }

  /**
   * Keep visual + functional disabled states in sync.
   *
   * The theme button CSS uses `.is-disabled` / `[aria-disabled]` (not `:disabled`),
   * so we set all three for reliable UX, especially on mobile.
   */

  function isButtonDisabled(btn) {
    if (!btn) return true;
    // Native disabled flag (buttons/inputs).
    if (btn.disabled === true) return true;
    try {
      if (btn.getAttribute && btn.getAttribute('aria-disabled') === 'true') return true;
    } catch (_) {
      // ignore
    }
    try {
      if (btn.classList && btn.classList.contains('is-disabled')) return true;
    } catch (_) {
      // ignore
    }
    return false;
  }

  /**
   * Keep visual + functional disabled states in sync.
   *
   * The theme button CSS uses `.is-disabled` / `[aria-disabled]` (not `:disabled`),
   * so we set all three for reliable UX, especially on mobile.
   */
  function setButtonDisabled(btn, disabled) {
    if (!btn) return;
    const d = !!disabled;

    // Functional disabling (works for real <button> elements; safe no-op for others).
    try { btn.disabled = d; } catch (_) { /* ignore */ }

    // Visual styling used by the theme.
    if (btn.classList) btn.classList.toggle('is-disabled', d);

    if (d) btn.setAttribute('aria-disabled', 'true');
    else btn.removeAttribute('aria-disabled');

    // Some themes render controls as <a> / <div>. For those, ensure keyboard users
    // cannot tab to a disabled control.
    const tag = btn.tagName ? String(btn.tagName).toUpperCase() : '';
    const isNativeButton = tag === 'BUTTON' || (tag === 'INPUT' && /^(BUTTON|SUBMIT|RESET)$/i.test(btn.type || ''));

    if (!isNativeButton) {
      const key = 'data-ml-9665-prev-tabindex';
      if (d) {
        if (!btn.hasAttribute(key)) {
          btn.setAttribute(key, btn.hasAttribute('tabindex') ? (btn.getAttribute('tabindex') || '') : '');
        }
        btn.setAttribute('tabindex', '-1');
      } else {
        if (btn.hasAttribute(key)) {
          const prev = btn.getAttribute(key);
          btn.removeAttribute(key);
          if (prev == null || prev === '') btn.removeAttribute('tabindex');
          else btn.setAttribute('tabindex', prev);
        }
      }
    }
  }


  function computeTotalUpperBound(questionGraph, knownAnswersByQuestionId) {
    // Upper bound on remaining questions in this run.
    // If a branching question isn't answered yet, we take the longest remaining path.

    const byNode = questionGraph.byNode;
    const byId = questionGraph.byId;
    const resultToken = questionGraph.resultToken;

    const memo = new Map();

    function resolveNextToken(next) {
      if (next === resultToken) return resultToken;
      if (typeof next === 'number') return next;
      if (typeof next === 'string') {
        const t = next.trim();
        if (!t) return null;
        if (t === resultToken) return resultToken;
        const asNum = Number(t);
        if (Number.isFinite(asNum)) return asNum;

        // Support flows that store `next` as a question_id (e.g. 'Q10') instead of a node_id.
        try {
          if (byId && typeof byId.get === 'function') {
            const q = byId.get(t);
            if (q && isFiniteNumber(q.node_id)) return q.node_id;
          }
        } catch (_) {
          // ignore
        }
      }
      return null;
    }

    function maxRemainingFrom(nodeId, visiting) {
      if (memo.has(nodeId)) return memo.get(nodeId);
      if (visiting.has(nodeId)) return 0; // guard cycles
      visiting.add(nodeId);

      const q = byNode.get(nodeId);
      if (!q) {
        visiting.delete(nodeId);
        memo.set(nodeId, 0);
        return 0;
      }

      const nexts = [];
      if (Array.isArray(q.options)) {
        const answered = knownAnswersByQuestionId && knownAnswersByQuestionId[q.question_id];
        if (answered) {
          let opt = null;
          const map = q.__optById;
          if (map && typeof map === 'object' && map[answered]) {
            opt = map[answered];
          } else {
            opt = q.options.find((o) => o && o.option_id === answered);
          }
          if (opt && opt.next != null) nexts.push(opt.next);
        } else {
          for (let i = 0; i < q.options.length; i++) {
            const o = q.options[i];
            if (o && o.next != null) nexts.push(o.next);
          }
        }
      }

      let best = 0;
      for (let i = 0; i < nexts.length; i++) {
        const r = resolveNextToken(nexts[i]);
        if (r === resultToken) {
          best = Math.max(best, 0);
          continue;
        }
        if (typeof r === 'number') {
          best = Math.max(best, 1 + maxRemainingFrom(r, visiting));
        }
      }

      visiting.delete(nodeId);
      memo.set(nodeId, best);
      return best;
    }

    return 1 + maxRemainingFrom(questionGraph.startNodeId, new Set());
  }

  // -----------------------------
  // Short-flow model (refinement question selection)
  // -----------------------------

  function scaleRangeForStd(scale) {
    switch (scale) {
      case 'rating_1_10':
        return 9;
      case 'grade_0_100':
        return 100;
      case 'overall_0_100ish':
        return 100;
      case 'lbs':
        return 120;
      case 'years':
        return 10;
      default:
        return 100;
    }
  }

  function computeFieldStd(breeds, fieldCount) {
    const n = Math.max(0, fieldCount | 0);
    const count = new Uint32Array(n);
    const mean = new Float64Array(n);
    const m2 = new Float64Array(n);

    for (let i = 0; i < breeds.length; i++) {
      const v = breeds[i] && breeds[i].values;
      if (!v) continue;
      for (let j = 0; j < n; j++) {
        const x = v[j];
        if (!Number.isFinite(x)) continue;
        const c = (count[j] += 1);
        const delta = x - mean[j];
        mean[j] += delta / c;
        const delta2 = x - mean[j];
        m2[j] += delta * delta2;
      }
    }

    const std = new Float32Array(n);
    for (let j = 0; j < n; j++) {
      const c = count[j];
      if (c <= 1) {
        std[j] = 0;
        continue;
      }
      const variance = m2[j] / (c - 1);
      std[j] = variance > 0 ? Math.sqrt(variance) : 0;
    }
    return std;
  }

  function computeNodeDiscriminationScore(node, fieldStd) {
    if (!node || !node.options) return 0;

    let score = 0;
    const seen = new Set();

    const optIds = Object.keys(node.options);
    for (let i = 0; i < optIds.length; i++) {
      const opt = node.options[optIds[i]];
      if (!opt || !opt.scoreTraits) continue;
      const traits = opt.scoreTraits;
      for (let t = 0; t < traits.length; t++) {
        const tr = traits[t];
        if (!tr) continue;
        const key = `${tr.fieldIdx}|${tr.scale}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const std = fieldStd && fieldStd[tr.fieldIdx];
        if (!Number.isFinite(std) || std <= 0) continue;

        const range = scaleRangeForStd(tr.scale);
        const norm = Math.min(1, std / Math.max(1e-6, range));
        score += (tr.importance || 0) * norm;
      }
    }

    return (node.weight || 0) * score;
  }

  function buildShortFlowModel(spec, breeds) {
    try {
      const fieldCount = (spec && Array.isArray(spec.fieldPaths)) ? spec.fieldPaths.length : 0;
      const fieldStd = computeFieldStd(breeds || [], fieldCount);
      const priorityByQuestionId = Object.create(null);

      if (spec && spec.nodeIndex) {
        const keys = Object.keys(spec.nodeIndex);
        for (let i = 0; i < keys.length; i++) {
          const k = keys[i];
          priorityByQuestionId[k] = computeNodeDiscriminationScore(spec.nodeIndex[k], fieldStd);
        }
      }

      // The model is per-section so we can safely keep small caches here.
      return {
        priorityByQuestionId,
        fieldStdByIndex: fieldStd,
        breeds: Array.isArray(breeds) ? breeds : [],
        spec,
        // Cache for adaptive routing decisions (keyed by hash of answers so far).
        _confidenceCache: new Map()
      };
    } catch (_) {
      return {
        priorityByQuestionId: Object.create(null),
        fieldStdByIndex: null,
        breeds: Array.isArray(breeds) ? breeds : [],
        spec: spec && typeof spec === 'object' ? spec : null,
        _confidenceCache: new Map()
      };
    }
  }

  /**
   * Hash answers into a stable 32-bit key.
   * Used only for memoization (not security).
   *
   * Optimized: iterates the compiled spec question order (no Object.keys().sort()).
   */
  function hashAnswersKeyFast(spec, answersByQuestionId) {
    if (!answersByQuestionId || typeof answersByQuestionId !== 'object') return 0;
    if (!spec || !Array.isArray(spec.nodes) || spec.nodes.length === 0) return 0;

    let h = 0x811c9dc5;
    const upd = (code) => {
      h ^= code;
      h = Math.imul(h, 0x01000193);
    };

    for (let i = 0; i < spec.nodes.length; i++) {
      const node = spec.nodes[i];
      const k = node && node.key ? node.key : null;
      if (!k) continue;
      const v = answersByQuestionId[k];
      if (typeof v !== 'string') continue;

      for (let j = 0; j < k.length; j++) upd(k.charCodeAt(j));
      upd(61); // '='
      for (let j = 0; j < v.length; j++) upd(v.charCodeAt(j));
      upd(38); // '&'
    }

    return h >>> 0;
  }

  // Backwards-compat wrapper (kept for any legacy call sites).
  function hashAnswersKey(answersByQuestionId) {
    return hashAnswersKeyFast(getCompiledSpec(), answersByQuestionId);
  }

  function stdForFieldAmongBreeds(breeds, fieldIdx) {
    if (!Array.isArray(breeds) || breeds.length === 0) return 0;
    let n = 0;
    let mean = 0;
    let m2 = 0;

    for (let i = 0; i < breeds.length; i++) {
      const b = breeds[i];
      if (!b || !b.values) continue;
      const v = b.values[fieldIdx];
      if (!Number.isFinite(v)) continue;
      n += 1;
      const d = v - mean;
      mean += d / n;
      m2 += d * (v - mean);
    }

    if (n < 2) return 0;
    return Math.sqrt(m2 / (n - 1));
  }

  function computeLocalDiscriminationScore(node, topBreeds, getStdForFieldIdx) {
    if (!node || !node.options || !Array.isArray(topBreeds) || topBreeds.length === 0) return 0;
    if (typeof getStdForFieldIdx !== 'function') return 0;

    // Consolidate traits by (field,scale) and use the max importance across options.
    const impByKey = Object.create(null);
    const optIds = Object.keys(node.options);

    for (let i = 0; i < optIds.length; i++) {
      const opt = node.options[optIds[i]];
      if (!opt || !opt.scoreTraits) continue;
      const traits = opt.scoreTraits;
      for (let t = 0; t < traits.length; t++) {
        const tr = traits[t];
        if (!tr) continue;
        const imp = tr.importance || 0;
        if (!(imp > 0)) continue;
        const key = `${tr.fieldIdx}|${tr.scale}`;
        if (!(key in impByKey) || imp > impByKey[key]) impByKey[key] = imp;
      }
    }

    let s = 0;
    for (const key in impByKey) {
      const pipe = key.indexOf('|');
      if (pipe === -1) continue;
      const idx = Number(key.slice(0, pipe));
      const scale = key.slice(pipe + 1);
      if (!Number.isFinite(idx)) continue;

      const std = getStdForFieldIdx(idx);
      if (!Number.isFinite(std) || std <= 0) continue;

      const range = scaleRangeForStd(scale);
      const norm = Math.min(1, std / Math.max(1e-6, range));
      s += impByKey[key] * norm;
    }

    return (node.weight || 0) * s;
  }

  function getConfidenceForAnswers(refinementModel, answersByQuestionId) {
    const model = refinementModel && typeof refinementModel === 'object' ? refinementModel : null;
    const cache = model && model._confidenceCache ? model._confidenceCache : null;
    const spec = model && model.spec ? model.spec : null;
    const breeds = model && Array.isArray(model.breeds) ? model.breeds : null;

    if (!spec || !breeds || breeds.length === 0) {
      return { level: 'low', top1Pct: 0, spreadPct: 0, topBreeds: [] };
    }

    const h = hashAnswersKeyFast(spec, answersByQuestionId);

    // LRU memoization for confidence calculations (can be expensive on mobile).
    if (cache) {
      if (cache instanceof Map) {
        if (cache.has(h)) {
          const hit = cache.get(h);
          // Refresh LRU ordering.
          cache.delete(h);
          cache.set(h, hit);
          return hit;
        }
      } else if (cache.hash === h && cache.value) {
        // Legacy single-entry cache fallback.
        return cache.value;
      }
    }

    // We keep this reasonably small; we only need enough top candidates to estimate confidence
    // and to compute targeted refinement-question discrimination.
    const TOP_K = 24;

    let scoredRes = null;
    try {
      scoredRes = scoreAllBreeds(spec, breeds, answersByQuestionId, TOP_K);
    } catch (_) {
      scoredRes = null;
    }

    let top1Pct = 0;
    let spreadPct = 0;
    let top2GapPct = 0;
    let topBreeds = [];

    if (scoredRes && Array.isArray(scoredRes.scored) && scoredRes.scored.length > 0) {
      const top = scoredRes.scored;
      const maxPossible = (scoredRes.maxPossible && scoredRes.maxPossible > 0) ? scoredRes.maxPossible : 1;
      const invMax = 1 / maxPossible;

      // Use *unrounded* normalization to avoid threshold jitter that can cause
      // inconsistent refinement routing (especially noticeable on mobile).
      const normPct = (rawScore) => clamp01(rawScore * invMax) * 100;

      top1Pct = normPct(top[0].score);
      const k = Math.min(5, top.length);
      spreadPct = top1Pct - normPct(top[k - 1].score);
      if (top.length >= 2) top2GapPct = top1Pct - normPct(top[1].score);

      topBreeds = top.map((x) => x && x.breed).filter(Boolean);
    }

    // Confidence heuristic:
    // - Refinement questions are only asked when the current top candidates are tightly clustered.
    // - Incorporating the top1-vs-top2 gap prevents us from asking extra questions when a
    //   clear #1 already exists.
    let level = 'low';
    if (top1Pct >= 78 && spreadPct >= 10 && top2GapPct >= 2) level = 'high';
    else if (top1Pct >= 70 && spreadPct >= 8 && top2GapPct >= 1.5) level = 'medium';

    const value = { level, top1Pct, spreadPct, top2GapPct, topBreeds };

    if (cache) {
      if (cache instanceof Map) {
        cache.set(h, value);
        // Cap the cache to a tiny size (prevents growth when users backtrack a lot).
        const MAX = 10;
        while (cache.size > MAX) {
          const firstKey = cache.keys().next().value;
          cache.delete(firstKey);
        }
      } else {
        cache.hash = h;
        cache.value = value;
      }
    }

    return value;
  }

  // -----------------------------
  // Short flow (<= 16 questions)
  // -----------------------------

  function nodeIdForQuestionId(questionGraph, questionId) {
    if (!questionGraph || !questionId) return null;
    const byId = questionGraph.byId;
    if (!byId || typeof byId.get !== 'function') return null;
    const q = byId.get(questionId);
    return q && isFiniteNumber(q.node_id) ? q.node_id : null;
  }

  function computeShortUpperBound(knownAnswersByQuestionId, maxQuestions, targetQuestions) {
    // Upper-bound estimate used ONLY for the progress indicator.
    // The router enforces the actual max question cap.

    const ans = knownAnswersByQuestionId || {};
    const cap = isFiniteNumber(maxQuestions) ? Math.max(1, Math.floor(maxQuestions)) : DEFAULT_MAX_QUESTIONS;
    const target = isFiniteNumber(targetQuestions) ? Math.max(1, Math.floor(targetQuestions)) : DEFAULT_TARGET_QUESTIONS;
    const desiredTarget = Math.min(cap, target);

    // Base short flow always asks:
    // Q01, (env follow-up), Q09, Q04, Q10, Q11, Q14, (maybe Q15), Q16, Q29, Q33, Q44, Q48
    let total = 12;

    // Yard/property setups insert one extra question (Q07 + Q08) compared to apartment/neighbor flows.
    const q01 = ans['Q01'];
    if (!q01) total += 1; // worst-case until Q01 is answered
    else if (q01 === 'Q01_C' || q01 === 'Q01_D') total += 1;

    // Alone-time follow-up: if the home is empty 4+ hours, we ask Q15 about support.
    const q14 = ans['Q14'];
    if (!q14) total += 1; // worst-case until Q14 is answered
    else if (q14 === 'Q14_C' || q14 === 'Q14_D') total += 1;

    // Kids follow-ups: Q32 always for B/C/D, and Q31 for C/D (or C when kid-friendliness is important)
    const q29 = ans['Q29'];
    if (!q29) {
      total += 2; // worst case: Q32 + Q31
    } else if (q29 !== 'Q29_A') {
      total += 1; // Q32
      if (q29 === 'Q29_D') {
        total += 1;
      } else if (q29 === 'Q29_C') {
        const q32 = ans['Q32'];
        if (!q32) total += 1; // worst-case until answered
        else if (q32 === 'Q32_A' || q32 === 'Q32_B') total += 1;
      }
    }

    // Pet follow-up: one of Q34/Q35/Q36 unless Q33_A
    const q33 = ans['Q33'];
    if (!q33) {
      total += 1; // worst case: will need a pet follow-up
    } else if (q33 !== 'Q33_A') {
      total += 1;
    }

    // Grooming follow-up: Q45 when allergies are a must/strong preference OR minimal shedding.
    const q44 = ans['Q44'];
    const q48 = ans['Q48'];
    if (!q44 || !q48) {
      total += 1; // worst case
    } else {
      const need = (q48 === 'Q48_A' || q48 === 'Q48_B' || q44 === 'Q44_A');
      if (need) total += 1;
    }

    // If the run would otherwise be short (e.g., no kids/pets follow-ups),
    // we may insert a small number of refinement questions to reach the target.
    total = Math.max(total, desiredTarget);

    if (total > cap) total = cap;
    return Math.max(1, total);
  }

  function computeShortNextStep(questionGraph, currentQuestionId, selectedOptionId, answersByQuestionId, askedCount, maxQuestions, targetQuestions, refinementModel) {
    const cap = isFiniteNumber(maxQuestions) ? Math.max(1, Math.floor(maxQuestions)) : DEFAULT_MAX_QUESTIONS;
    const target = isFiniteNumber(targetQuestions) ? Math.max(1, Math.floor(targetQuestions)) : DEFAULT_TARGET_QUESTIONS;
    const desiredTarget = Math.min(cap, target);

    // Hard cap: once we've asked cap questions, we must finish.
    if (askedCount >= cap) return questionGraph.resultToken;

    const toNode = (qid) => nodeIdForQuestionId(questionGraph, qid);

    const hasAnswered = (qid) => !!(answersByQuestionId && typeof answersByQuestionId[qid] === 'string');

    // Helper: decide whether to ask Q31 after Q32.
    // - Always for toddlers (Q29_D)
    // - For kids-at-home (Q29_C) only when Q32 indicates kid-friendliness is important (A/B)
    function shouldAskQ31(q29, q32) {
      if (q29 === 'Q29_D') return true;
      if (q29 !== 'Q29_C') return false;
      return q32 === 'Q32_A' || q32 === 'Q32_B';
    }

    // Helper: decide whether to ask Q45 after Q48.
    function shouldAskQ45(q44, q48) {
      if (q48 === 'Q48_A' || q48 === 'Q48_B') return true;
      if (q44 === 'Q44_A') return true;
      return false;
    }

    // Helper: decide whether to ask the tie-break question (Q50) at the end.
    // We only ask this when the top candidates are extremely close.
    function shouldAskTieBreak() {
      if (hasAnswered('Q50')) return false;
      if ((askedCount + 1) > cap) return false;
      const c = getConfidenceForAnswers(refinementModel, answersByQuestionId);
      if (c && isFiniteNumber(c.top2GapPct)) return c.top2GapPct < 1.25;
      return false;
    }

    // Optional refinement questions (asked only when we have question-budget).
    // These improve accuracy on shorter runs without increasing burnout risk.
    //
    // We choose refinement questions using a blend of:
    // - Global discrimination (precomputed across all breeds)
    // - Local discrimination (computed across the *current top candidates*)
    // - Small contextual nudges based on earlier answers
    function pickRefinementQuestionId(topBreeds) {
      const q11 = answersByQuestionId && answersByQuestionId['Q11'];
      const q14 = answersByQuestionId && answersByQuestionId['Q14'];
      const q16 = answersByQuestionId && answersByQuestionId['Q16'];
      const q09 = answersByQuestionId && answersByQuestionId['Q09'];
      const q04 = answersByQuestionId && answersByQuestionId['Q04'];

      /** @type {string[]} */
      const raw = [];

      // Core differentiators that tend to materially improve match quality.
      raw.push('Q19'); // experience
      raw.push('Q21'); // training commitment

      // Lifting/carrying: relevant for medium/large dogs (stairs, car, vet).
      if (q11 === 'Q11_B' || q11 === 'Q11_C') raw.push('Q13');

      // Leash handling: especially relevant for medium/large dogs.
      if (!q11 || q11 === 'Q11_B' || q11 === 'Q11_C') raw.push('Q12');

      // Mental enrichment: helps separate high-drive breeds.
      if (!q16 || q16 === 'Q16_C' || q16 === 'Q16_D' || q14 === 'Q14_C' || q14 === 'Q14_D') raw.push('Q18');

      // Activity style: helps distinguish between "adventure" breeds that otherwise look similar.
      if (q09 === 'Q09_D' || q16 === 'Q16_C' || q16 === 'Q16_D' || q04 === 'Q04_C' || q04 === 'Q04_D') raw.push('Q17');

      // Play style: useful tie-breaker for active households.
      if (q09 === 'Q09_D' || q04 === 'Q04_C' || q04 === 'Q04_D') raw.push('Q28');

      // Cuddliness/affection: matters most when you want a true companion.
      if (q09 === 'Q09_A') raw.push('Q27');

      // Stranger / protection vibe: choose the most relevant one.
      raw.push((q09 === 'Q09_B' || q09 === 'Q09_C') ? 'Q41' : 'Q40');

      // Health robustness preference.
      raw.push('Q49');

      // Drool (large dogs vary a lot here).
      if (q11 === 'Q11_C') raw.push('Q46');

      // Destructiveness / chewing tolerance.
      raw.push('Q25');

      // Outdoors & recall (separates high chase / roam breeds).
      raw.push('Q37');
      raw.push('Q38');
      raw.push('Q39');

      // De-dupe while preserving order.
      /** @type {string[]} */
      const candidates = [];
      for (let i = 0; i < raw.length; i++) {
        const qid = raw[i];
        if (candidates.indexOf(qid) === -1) candidates.push(qid);
      }

      const pri = refinementModel && refinementModel.priorityByQuestionId ? refinementModel.priorityByQuestionId : null;
      const localSpec = refinementModel && refinementModel.spec ? refinementModel.spec : null;

      // Memoize per-field standard deviations for the current top set.
      // This keeps the refinement chooser extremely fast on mobile.
      const stdCache = new Map();
      const getStd = (idx) => {
        if (stdCache.has(idx)) return stdCache.get(idx);
        const s = stdForFieldAmongBreeds(topBreeds, idx);
        stdCache.set(idx, s);
        return s;
      };

      let bestQid = null;
      let bestScore = -Infinity;
      let bestOrder = Infinity;

      for (let i = 0; i < candidates.length; i++) {
        const qid = candidates[i];
        if (hasAnswered(qid)) continue;
        const nodeId = toNode(qid);
        if (typeof nodeId !== 'number') continue;

        let s = 0;
        // Global (dataset-wide) discrimination
        const global = (pri && isFiniteNumber(pri[qid])) ? pri[qid] : 0;

        // Local (top-candidate) discrimination
        let local = 0;
        if (localSpec && localSpec.nodeIndex && Array.isArray(topBreeds) && topBreeds.length > 0) {
          const node = localSpec.nodeIndex[qid];
          if (node) local = computeLocalDiscriminationScore(node, topBreeds, getStd);
        }

        // Combine. Local signal matters most (it tells us what will separate the current leaders).
        s += (local * 1.15) + (global * 0.65);

        // Context bonuses (small; variance drives the majority of the choice).
        if (qid === 'Q12' && q11 === 'Q11_C') s += 0.35;
        else if (qid === 'Q12' && q11 === 'Q11_B') s += 0.15;

        if (qid === 'Q13' && q11 === 'Q11_C') s += 0.25;
        else if (qid === 'Q13' && q11 === 'Q11_B') s += 0.15;

        if (qid === 'Q18' && (q16 === 'Q16_D' || q16 === 'Q16_C')) s += 0.25;
        if (qid === 'Q18' && q09 === 'Q09_D') s += 0.15;

        if (qid === 'Q17' && q09 === 'Q09_D') s += 0.2;
        if (qid === 'Q28' && q09 === 'Q09_D') s += 0.1;
        if (qid === 'Q27' && q09 === 'Q09_A') s += 0.2;

        if (qid === 'Q41' && (q09 === 'Q09_B' || q09 === 'Q09_C')) s += 0.35;
        if (qid === 'Q40' && (q09 !== 'Q09_B' && q09 !== 'Q09_C')) s += 0.15;

        if (qid === 'Q46' && q11 === 'Q11_C') s += 0.15;
        if (qid === 'Q49' && q11 === 'Q11_C') s += 0.15;

        const order = i;
        if (s > bestScore || (s === bestScore && order < bestOrder)) {
          bestScore = s;
          bestOrder = order;
          bestQid = qid;
        }
      }

      return bestQid;
    }

    function nextAfterPets(askedSoFar) {
      // Before grooming we still need:
      // - Q44 (shedding/hair)
      // - Q48 (allergies)
      // - and *maybe* Q45 (coat maintenance). We reserve space for Q45 by keeping at least 3 questions of runway.

      const runwayForGrooming = 3; // Q44 + Q48 + (possible) Q45
      if (askedSoFar >= cap) return questionGraph.resultToken;
      // If we don't have enough runway left to safely ask Q44 + Q48 + (maybe) Q45, skip refinement.
      if (askedSoFar >= (cap - runwayForGrooming)) {
        const n = toNode('Q44');
        return (typeof n === 'number') ? n : questionGraph.resultToken;
      }

      const remainingMandatory = 2; // Q44 + Q48 (Q45 is conditional and handled after Q48)
      const needToHitTarget = desiredTarget - (askedSoFar + remainingMandatory);

      // Prevent "refinement chains" that can feel like the quiz won't end.
      // We cap the number of optional refinement questions, independent of targetQuestions.
      const MAX_REFINEMENTS = 2;
      const refinementIds = ['Q12', 'Q13', 'Q17', 'Q18', 'Q19', 'Q21', 'Q25', 'Q27', 'Q28', 'Q37', 'Q38', 'Q39', 'Q40', 'Q41', 'Q46', 'Q49'];
      let refinementsAnswered = 0;
      for (let i = 0; i < refinementIds.length; i++) {
        if (hasAnswered(refinementIds[i])) refinementsAnswered += 1;
      }
      const canAskRefinement = refinementsAnswered < MAX_REFINEMENTS;

      // Adaptive refinement:
      // If we already have a confident separation in the current top matches,
      // we skip extra questions even if we haven't hit the target length.
      // This prevents burnout while keeping accuracy high.
      const confidence = getConfidenceForAnswers(refinementModel, answersByQuestionId);
      const isLow = confidence.level === 'low';
      const isMed = confidence.level === 'medium';

      let shouldAskRefinement = false;
      if (canAskRefinement) {
        if (needToHitTarget > 0) {
          if (isLow) shouldAskRefinement = true;
          else if (isMed) shouldAskRefinement = needToHitTarget >= 2;
          else shouldAskRefinement = false; // high confidence
        } else {
          // Even if we hit the target, we may ask one more if confidence is very low
          // and we still have ample runway. (Rare; keeps results from feeling random.)
          if (isLow && askedSoFar <= (cap - runwayForGrooming - 2)) shouldAskRefinement = true;
        }
      }

      if (shouldAskRefinement) {
        const qid = pickRefinementQuestionId(confidence.topBreeds);
        if (qid) {
          const n = toNode(qid);
          return (typeof n === 'number') ? n : questionGraph.resultToken;
        }
      }

      const n = toNode('Q44');
      return (typeof n === 'number') ? n : questionGraph.resultToken;
    }

    switch (currentQuestionId) {
      case 'Q01': {
        if (selectedOptionId === 'Q01_A') return toNode('Q02');
        if (selectedOptionId === 'Q01_B') return toNode('Q05');
        // Yard / property setups benefit from checking containment first, then chase-drive.
        if (selectedOptionId === 'Q01_C' || selectedOptionId === 'Q01_D') return toNode('Q07');
        return toNode('Q09');
      }

      case 'Q07':
        return toNode('Q08');

      case 'Q02':
      case 'Q05':
      case 'Q08':
        return toNode('Q09');

      case 'Q09':
        return toNode('Q04');

      case 'Q04':
        return toNode('Q10');

      case 'Q10':
        return toNode('Q11');

      case 'Q11':
        return toNode('Q14');

      case 'Q14': {
        // If the home is empty for 4+ hours, ask the support question (walker/daycare) before activity level.
        if (selectedOptionId === 'Q14_C' || selectedOptionId === 'Q14_D') return toNode('Q15');
        return toNode('Q16');
      }

      case 'Q15':
        return toNode('Q16');

      case 'Q16':
        return toNode('Q29');

      case 'Q29': {
        if (selectedOptionId === 'Q29_A') return toNode('Q33');
        return toNode('Q32');
      }

      case 'Q32': {
        const q29 = answersByQuestionId && answersByQuestionId['Q29'];
        if (shouldAskQ31(q29, selectedOptionId)) return toNode('Q31');
        return toNode('Q33');
      }

      case 'Q31':
        return toNode('Q33');

      case 'Q33': {
        if (selectedOptionId === 'Q33_A') return nextAfterPets(askedCount);
        if (selectedOptionId === 'Q33_B') return toNode('Q34');
        if (selectedOptionId === 'Q33_C') return toNode('Q35');
        if (selectedOptionId === 'Q33_D') return toNode('Q36');
        return nextAfterPets(askedCount);
      }

      case 'Q34':
      case 'Q35':
      case 'Q36':
        return nextAfterPets(askedCount);

      // Refinement questions route back through the same pre-grooming gate.
      case 'Q12':
      case 'Q18':
      case 'Q19':
      case 'Q21':
      case 'Q25':
      case 'Q37':
      case 'Q38':
      case 'Q39':
      case 'Q40':
      case 'Q41':
      case 'Q49':
        return nextAfterPets(askedCount);

      case 'Q44': {
        const n = toNode('Q48');
        return (typeof n === 'number') ? n : questionGraph.resultToken;
      }

      case 'Q48': {
        const q44 = answersByQuestionId && answersByQuestionId['Q44'];
        if (shouldAskQ45(q44, selectedOptionId) && (askedCount + 1) <= cap) {
          const n = toNode('Q45');
          return (typeof n === 'number') ? n : questionGraph.resultToken;
        }
        if (shouldAskTieBreak()) {
          const n = toNode('Q50');
          return (typeof n === 'number') ? n : questionGraph.resultToken;
        }
        return questionGraph.resultToken;
      }

      case 'Q45': {
        if (shouldAskTieBreak()) {
          const n = toNode('Q50');
          return (typeof n === 'number') ? n : questionGraph.resultToken;
        }
        return questionGraph.resultToken;
      }

      case 'Q50':
        return questionGraph.resultToken;

      default:
        return null;
    }
  }

  // -----------------------------
  // Root init (per section)
  // -----------------------------

  function initRoot(root) {
    if (!root || root.__ml9665DbqInit) return;
    root.__ml9665DbqInit = true;

    const spec = getCompiledSpec();

    const configNode = root.querySelector('[data-ml-9665-dbq-config]');
    const qNode = root.querySelector('[data-ml-9665-dbq-questions]');
    const aNode = root.querySelector('[data-ml-9665-dbq-answers]');
    const bNode = root.querySelector('[data-ml-9665-dbq-breeds]');

    const configRes = safeJsonParse(configNode ? configNode.textContent : null, 'config');
    const qRes = safeJsonParse(qNode ? qNode.textContent : null, 'questions');
    const aRes = safeJsonParse(aNode ? aNode.textContent : null, 'answers');
    const bRes = safeJsonParse(bNode ? bNode.textContent : null, 'breeds');

    const startError = root.querySelector('[data-ml-9665-dbq-start-error]');

    function showStartError(msg) {
      if (!startError) return;
      startError.hidden = false;
      startError.textContent = msg;
    }

    if (!configRes.ok) {
      showStartError(configRes.error.message);
      return;
    }

    const config = configRes.value || {};
    let sectionId = String(config.sectionId || root.getAttribute('data-section-id') || '').trim();
    if (!sectionId) {
      // If the section id is unavailable (e.g. non-section usage), generate a stable per-instance id
      // to avoid input-name / storage collisions when multiple quizzes are present.
      sectionId = String(root.getAttribute('data-ml-9665-dbq-instance') || '').trim();
      if (!sectionId) {
        sectionId = `inst-${Math.random().toString(36).slice(2, 10)}`;
        root.setAttribute('data-ml-9665-dbq-instance', sectionId);
      }
    }

    // Use a DOM-safe prefix for input ids/names (prevents edge-case issues with unusual section ids).
    const domIdPrefix = `ml-9665-dbq-${sanitizeDomToken(sectionId)}`;

    const enableResume = !!config.enableResume;
    const enableDebug = !!config.enableDebug;
    const variantMode = typeof config.variantMode === 'string' ? config.variantMode : 'friendly';
    // This quiz UX is fixed to the top five matches. Theme config cannot
    // expand or reduce the result count, which keeps ranking behavior stable.
    const resultsCount = DEFAULT_RESULTS_COUNT;

    // We keep the quiz shorter on small screens by default to reduce mobile fatigue.
    // Theme config can always override this via `targetQuestions`.
    const isMobileViewport = !!(window.matchMedia && window.matchMedia('(max-width: 680px)').matches);

    const flowMode = (config.flowMode === 'full' || config.flowMode === 'short') ? config.flowMode : DEFAULT_FLOW_MODE;
    const maxQuestions = isFiniteNumber(config.maxQuestions) ? Math.max(1, Math.floor(config.maxQuestions)) : DEFAULT_MAX_QUESTIONS;
    const defaultTargetQuestions = isMobileViewport ? Math.min(maxQuestions, 12) : DEFAULT_TARGET_QUESTIONS;
    const targetQuestionsRaw = isFiniteNumber(config.targetQuestions)
      ? Math.max(1, Math.floor(config.targetQuestions))
      : defaultTargetQuestions;
    const targetQuestions = Math.min(maxQuestions, targetQuestionsRaw);

    const runtimeQuestionsData = getEmbeddedDbqQuestionsV2() || (qRes.ok ? qRes.value : null);
    const runtimeAnswersData = getEmbeddedDbqAnswersV2() || (aRes.ok ? aRes.value : null);

    if (!runtimeQuestionsData) {
      showStartError(qRes.ok ? 'Questions JSON missing `questions` array.' : qRes.error.message);
      return;
    }
    if (!runtimeAnswersData) {
      showStartError(aRes.ok ? 'Answers JSON missing `answers_by_option_id`.' : aRes.error.message);
      return;
    }
    if (!bRes.ok) {
      showStartError(bRes.error.message);
      return;
    }

    const graphRes = buildQuestionMaps(runtimeQuestionsData);
    if (!graphRes.ok) {
      showStartError(graphRes.error.message);
      return;
    }

    const answersMapRes = buildAnswerMap(runtimeAnswersData);
    if (!answersMapRes.ok) {
      showStartError(answersMapRes.error.message);
      return;
    }

    const rawBreeds = bRes.value;
    const breeds = preprocessBreeds(rawBreeds, spec);
    if (breeds.length === 0) {
      showStartError('No breed data found. Ensure your dog_breed metaobjects include the expected fields.');
      return;
    }

    if (enableDebug) {
      // eslint-disable-next-line no-console
      console.debug('[ML-9665 DBQ] Init', { sectionId, variantMode, flowMode, maxQuestions, targetQuestions, resultsCount, spec: spec.id, breeds: breeds.length });
    }

    const shortFlowModel = (flowMode === 'short') ? buildShortFlowModel(spec, breeds) : null;

    const questionGraph = {
      byNode: graphRes.byNode,
      byId: graphRes.byId,
      startNodeId: graphRes.startNodeId,
      resultToken: graphRes.resultToken
    };

    const answersByOptionId = answersMapRes.map;

    const v2ScoringEngine = getDbqV2Engine();
    const rawBreedDisplayIndex = new Map();
    if (Array.isArray(rawBreeds)) {
      for (let i = 0; i < rawBreeds.length; i++) {
        const rawBreed = rawBreeds[i];
        rawBreedDisplayIndex.set(stableBreedIdFromRaw(rawBreed), rawBreed);
      }
    }

    function scoreAllBreedsForAnswers(answersByQuestionId, limitOverride) {
      const runtimeLimit = DEFAULT_RESULTS_COUNT;

      if (v2ScoringEngine) {
        try {
          const ranking = v2ScoringEngine.rankBreeds({
            answers: answersByQuestionId,
            breeds: Array.isArray(rawBreeds) ? rawBreeds : [],
            limit: runtimeLimit,
            includeExcluded: false,
            breedAdapter: makeV2BreedAdapter
          });

          if (ranking && Array.isArray(ranking.results) && ranking.results.length) {
            const scored = ranking.results.map((entry) => {
              const rawBreed = rawBreedDisplayIndex.get(entry.id) || null;
              const safeScore = isFiniteNumber(entry.score) ? entry.score : 0;
              return {
                breed: makeDisplayBreedView(rawBreed, entry.name),
                score: safeScore,
                scorePct: Math.max(0, Math.min(100, Math.round(safeScore))),
                reasons: Array.isArray(entry.reasons) ? entry.reasons.slice() : [],
                cautions: Array.isArray(entry.cautions) ? entry.cautions.slice() : [],
                hardConflicts: Array.isArray(entry.hardConflicts) ? entry.hardConflicts.slice() : [],
                criticalConflicts: Array.isArray(entry.criticalConflicts) ? entry.criticalConflicts.slice() : [],
                breakdown: (entry.breakdown && typeof entry.breakdown === 'object') ? entry.breakdown : null,
                confidenceScore: isFiniteNumber(entry.confidenceScore) ? entry.confidenceScore : null,
                excluded: !!entry.excluded,
                rawV2: entry
              };
            });

            return {
              maxPossible: 100,
              plan: null,
              scored,
              __engineVersion: 'v2',
              ranking
            };
          }
        } catch (err) {
          if (enableDebug && typeof console !== 'undefined' && console && typeof console.warn === 'function') {
            console.warn('[ML-9665 DBQ] V2 scoring fallback triggered', err);
          }
        }
      }

      return scoreAllBreeds(spec, breeds, answersByQuestionId, runtimeLimit);
    }

    function resultReasonTags(resultEntry, fallbackPlan, breedView) {
      if (resultEntry && Array.isArray(resultEntry.reasons) && resultEntry.reasons.length) {
        return resultEntry.reasons.slice(0, 3);
      }
      return buildWhyTagsFromPlan(fallbackPlan, breedView, 3);
    }


    // DOM refs
    const startScreen = root.querySelector('[data-ml-9665-start-screen]');
    const quizScreen = root.querySelector('[data-ml-9665-quiz-screen]');
    const questionSection = root.querySelector('.ml-9665-question');
    const formEl = root.querySelector('[data-ml-9665-form]');
    const navEl = root.querySelector('[data-ml-9665-nav]');
    const topbar = root.querySelector('[data-ml-9665-topbar]');
    const questionText = root.querySelector('[data-ml-9665-question-text]');
    const choicesFieldset = root.querySelector('[data-ml-9665-choices]');
    const choicesLegend = choicesFieldset ? choicesFieldset.querySelector('legend') : null;
    const inlineError = root.querySelector('[data-ml-9665-error]');
    const backBtn = root.querySelector('[data-ml-9665-back]');
    const nextBtn = root.querySelector('[data-ml-9665-next]');
    const nextLabel = root.querySelector('[data-ml-9665-next-label]');
    const qcountEl = root.querySelector('[data-ml-9665-qcount]');
    if (qcountEl) {
      // The numeric counter is intentionally hidden; keep it empty for safety if CSS fails to load.
      qcountEl.textContent = '';
      qcountEl.setAttribute('aria-hidden', 'true');
    }
    const progressbar = root.querySelector('[data-ml-9665-progressbar]');
    const resultsBox = root.querySelector('[data-ml-9665-results]');
    const resultsBody = root.querySelector('[data-ml-9665-results-body]');
    const restartBtn = root.querySelector('[data-ml-9665-restart]');
    const startBtn = root.querySelector('[data-ml-9665-start]');
    const resumeBtn = root.querySelector('[data-ml-9665-resume]');

    // Use theme-configured labels when available. Avoid overwriting server-rendered text.
    const labels = (config && config.labels && typeof config.labels === 'object') ? config.labels : Object.create(null);
    const defaultNextLabelText = (typeof labels.next === 'string' && labels.next.trim())
      ? labels.next.trim()
      : (nextLabel && nextLabel.textContent ? nextLabel.textContent.trim() : 'Next');

    // Loading overlay (injected for portability across theme versions)
    const loaderOverlay = ensureLoaderOverlay(root);
    const loaderTextEl = loaderOverlay ? loaderOverlay.querySelector('[data-ml-9665-dbq-loader-text]') : null;
    const loaderPctEl = loaderOverlay ? loaderOverlay.querySelector('[data-ml-9665-dbq-loader-pct]') : null;

    const storageKey = `ml-9665-dbq:${sectionId}`;

    /** @type {path:number[], index:number, answersByQuestionId:Record<string,string>, completed:boolean} */
    let state = {
      path: [questionGraph.startNodeId],
      index: 0,
      answersByQuestionId: Object.create(null),
      completed: false
    };


    // Submission (real-time loader; no artificial delay)
    let isSubmitting = false;
    let submitTimerId = null;
    let idleScoreId = null;
    let rafScoreId = null;
    let cachedScoredRes = null;
    let cachedScoredResHash = 0;
    let inFlightScoreHash = 0;
    let scoreToken = 0;

    // Respect reduced motion for scroll/transition behaviors.
    const prefersReducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const mqMobile = window.matchMedia ? window.matchMedia('(max-width: 680px)') : null;

    // A tiny global scroll lock so the loader feels like a true modal on mobile.
    // Uses a reference counter so multiple quiz instances won't fight each other.
    function getGlobalScrollLock() {
      if (!window.__ml9665DbqScrollLock) {
        window.__ml9665DbqScrollLock = { count: 0, y: 0 };
      }
      return window.__ml9665DbqScrollLock;
    }

    function lockPageScroll() {
      try {
        const g = getGlobalScrollLock();
        if (g.count === 0) {
          const y = window.scrollY || window.pageYOffset || 0;
          g.y = y;

          // Preserve any existing inline styles so we don't clobber theme behavior.
          const body = document.body;
          g.prev = {
            position: body.style.position,
            top: body.style.top,
            left: body.style.left,
            right: body.style.right,
            width: body.style.width,
            overflow: body.style.overflow,
            paddingRight: body.style.paddingRight
          };

          // Compensate for scrollbar removal to prevent layout shift.
          const docEl = document.documentElement;
          const scrollbar = Math.max(0, (window.innerWidth || 0) - (docEl ? docEl.clientWidth : 0));
          if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;

          body.classList.add('ml-9665-dbq-scroll-lock');
          body.style.overflow = 'hidden';
          body.style.position = 'fixed';
          body.style.top = `-${y}px`;
          body.style.left = '0';
          body.style.right = '0';
          body.style.width = '100%';
        }
        g.count += 1;
      } catch (_) {
        // ignore
      }
    }

    function unlockPageScroll() {
      try {
        const g = getGlobalScrollLock();
        if (g.count <= 0) return;
        g.count -= 1;
        if (g.count === 0) {
          const y = g.y || 0;
          const body = document.body;
          body.classList.remove('ml-9665-dbq-scroll-lock');

          const prev = g.prev || {};
          body.style.position = prev.position || '';
          body.style.top = prev.top || '';
          body.style.left = prev.left || '';
          body.style.right = prev.right || '';
          body.style.width = prev.width || '';
          body.style.overflow = prev.overflow || '';
          body.style.paddingRight = prev.paddingRight || '';

          g.prev = null;
          window.scrollTo(0, y);
        }
      } catch (_) {
        // ignore
      }
    }

    function clearIdleScore() {
      if (idleScoreId != null) {
        try {
          if (window.cancelIdleCallback) window.cancelIdleCallback(idleScoreId);
        } catch (_) {
          // ignore
        }
        idleScoreId = null;
      }

      if (rafScoreId != null) {
        try {
          window.cancelAnimationFrame(rafScoreId);
        } catch (_) {
          // ignore
        }
        rafScoreId = null;
      }
    }
    function shallowCloneAnswers(obj) {
      const out = Object.create(null);
      if (!obj) return out;
      const keys = Object.keys(obj);
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        const v = obj[k];
        if (typeof v === 'string') out[k] = v;
      }
      return out;
    }

    function scheduleScoreCompute(answersSnapshot, opts) {
      const options = (opts && typeof opts === 'object') ? opts : null;
      const eager = !!(options && options.eager);
      const onDone = (options && typeof options.onDone === 'function') ? options.onDone : null;

      clearIdleScore();
      const token = ++scoreToken;

      const answers = answersSnapshot
        ? shallowCloneAnswers(answersSnapshot)
        : shallowCloneAnswers(state.answersByQuestionId);

      const answersHash = hashAnswersKeyFast(spec, answers);

      if (cachedScoredRes && cachedScoredResHash === answersHash) {
        inFlightScoreHash = 0;
        if (root && root.classList.contains('ml-9665-dbq--loading')) setLoaderProgress01(1, true);
        if (onDone) onDone(cachedScoredRes);
        return;
      }

      cachedScoredRes = null;
      cachedScoredResHash = 0;
      inFlightScoreHash = answersHash;

      const run = () => {
        if (token !== scoreToken) return;

        let res = null;
        try {
          if (root && root.classList.contains('ml-9665-dbq--loading')) setLoaderProgress01(0.18, true);
          res = scoreAllBreedsForAnswers(answers, resultsCount);
        } catch (_) {
          res = null;
        }

        if (token !== scoreToken) return;

        if (res) {
          res.__hash = answersHash;
          cachedScoredRes = res;
          cachedScoredResHash = answersHash;
        } else {
          cachedScoredRes = null;
          cachedScoredResHash = 0;
        }

        inFlightScoreHash = 0;
        idleScoreId = null;
        rafScoreId = null;
        if (root && root.classList.contains('ml-9665-dbq--loading')) setLoaderProgress01(1, true);
        if (onDone) onDone(cachedScoredRes);
      };

      if (eager) {
        rafScoreId = window.requestAnimationFrame(run);
        return;
      }

      try {
        if (window.requestIdleCallback) {
          idleScoreId = window.requestIdleCallback(() => run(), { timeout: 1200 });
          return;
        }
      } catch (_) {
        // ignore
      }

      rafScoreId = window.requestAnimationFrame(run);
    }

    function scrollIntoViewIfNeeded(el) {
      // Primarily helps mobile: after tapping Next at the bottom of the page, the next UI
      // should appear with the important context visible without requiring a manual scroll.
      if (!el) return;
      if (!mqMobile || !mqMobile.matches) return;

      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      const needs = rect.top < 10 || rect.top > vh * 0.35;
      if (!needs) return;

      try {
        const behavior = prefersReducedMotion ? 'auto' : 'smooth';
        el.scrollIntoView({ block: 'start', behavior });
      } catch (_) {
        // ignore
      }
    }

    function scrollRootToTopIfNeeded() {
      if (!root || !questionText) return;
      scrollIntoViewIfNeeded(questionText);
    }

    // Loader progress (0..1) -> CSS var + text. Throttled to avoid layout churn.
    let lastLoaderProgressTs = 0;
    function setLoaderProgress01(p01, force) {
      if (!root) return;
      const now = (window.performance && typeof window.performance.now === 'function')
        ? window.performance.now()
        : Date.now();

      if (!force && (now - lastLoaderProgressTs) < 80) return;
      lastLoaderProgressTs = now;

      const p = clamp01(p01);
      const pct = Math.round(p * 100);
      try { root.style.setProperty('--ml-9665-dbq-loader-progress', `${pct}%`); } catch (_) { /* ignore */ }
      if (loaderPctEl) loaderPctEl.textContent = `${pct}%`;
    }

    function showLoader() {
      if (!loaderOverlay) return;
      if (loaderTextEl) loaderTextEl.textContent = 'Finding the best dog breeds for you...';
      setLoaderProgress01(0, true);
      loaderOverlay.hidden = false;
      root.classList.add('ml-9665-dbq--loading');
      root.setAttribute('aria-busy', 'true');
      lockPageScroll();
    }

    function hideLoader() {
      if (!loaderOverlay) return;
      loaderOverlay.hidden = true;
      root.classList.remove('ml-9665-dbq--loading');
      root.setAttribute('aria-busy', 'false');
      unlockPageScroll();
      setLoaderProgress01(0, true);
    }

    function clearSubmitTimer() {
      if (submitTimerId != null) {
        window.clearTimeout(submitTimerId);
        submitTimerId = null;
      }
    }

    function cancelSubmitFlow() {
      clearSubmitTimer();
      isSubmitting = false;
      clearIdleScore();
      scoreToken += 1;
      cachedScoredRes = null;
      cachedScoredResHash = 0;
      clearScheduledPersist();
      hideLoader();
      setButtonDisabled(nextBtn, false);
      setButtonDisabled(backBtn, false);
    }
    function submitQuiz() {
      if (isSubmitting) return;
      isSubmitting = true;

      // Mark as completed so refresh/resume can land on results.
      state.completed = true;
      persist();

      // Submit is an explicit Next-button action, so completing the bar here still
      // follows the rule that progress does not move on answer selection alone.
      setProgress(state.index, state.index + 1);

      setInlineError('');

      setButtonDisabled(nextBtn, true);
      setButtonDisabled(backBtn, true);

      // Always compute against a snapshot of answers at submit-time.
      const answersAtSubmit = shallowCloneAnswers(state.answersByQuestionId);
      const submitHash = hashAnswersKeyFast(spec, answersAtSubmit);

      showLoader();

      const MIN_LOADER_MS = 320;
      const MAX_WAIT_MS = 1400;
      const startedAt = (window.performance && typeof window.performance.now === 'function')
        ? window.performance.now()
        : Date.now();

      let finished = false;
      const finish = (preScored) => {
        if (finished) return;
        finished = true;
        const now = (window.performance && typeof window.performance.now === 'function')
          ? window.performance.now()
          : Date.now();
        const delay = Math.max(0, MIN_LOADER_MS - (now - startedAt));
        clearSubmitTimer();
        submitTimerId = window.setTimeout(() => {
          showResults(preScored || undefined);
        }, delay);
      };

      // If we already have a precomputed score for this exact answer set (e.g.
      // the user paused on the final question), reuse it immediately.
      if (cachedScoredRes && cachedScoredResHash === submitHash) {
        // Make sure no stale background callbacks continue running.
        scoreToken += 1;
        clearIdleScore();
        inFlightScoreHash = 0;
        setLoaderProgress01(1, true);
        finish(cachedScoredRes);
        return;
      }

      // Compute as soon as possible (chunked across frames), update loader progress,
      // and finish the moment a score is ready.
      scheduleScoreCompute(answersAtSubmit, {
        eager: true,
        onDone: (res) => {
          // Only accept results that match this submit attempt.
          if (!isSubmitting) return;
          if (cachedScoredRes && cachedScoredResHash === submitHash) {
            finish(cachedScoredRes);
          } else if (res && res.__hash === submitHash) {
            finish(res);
          }
        }
      });

      // Safety net: if we still don't have results shortly after showing the loader,
      // compute synchronously as a last resort and finish.
      clearSubmitTimer();
      submitTimerId = window.setTimeout(() => {
        if (finished) return;
        const haveFreshCache = !!(cachedScoredRes && cachedScoredResHash === submitHash);
        if (!haveFreshCache) {
          scoreToken += 1;
          clearIdleScore();
          inFlightScoreHash = 0;
          try {
            cachedScoredRes = scoreAllBreedsForAnswers(answersAtSubmit, resultsCount);
            if (cachedScoredRes) cachedScoredRes.__hash = submitHash;
            cachedScoredResHash = submitHash;
          } catch (_) {
            cachedScoredRes = null;
            cachedScoredResHash = 0;
          }
        }
        setLoaderProgress01(1, true);
        finish(cachedScoredRes || undefined);
      }, MAX_WAIT_MS);
    }

    const storage = enableResume ? getBestAvailableStorage() : null;

    // Persist writes can be expensive on mobile (synchronous storage I/O).
    // We schedule them during idle time where possible, but keep the underlying
    // `persist()` function synchronous for critical checkpoints (submit).
    let persistIdleId = null;
    let persistTimeoutId = null;

    function clearScheduledPersist() {
      if (persistIdleId != null) {
        try { if (window.cancelIdleCallback) window.cancelIdleCallback(persistIdleId); } catch (_) { /* ignore */ }
        persistIdleId = null;
      }
      if (persistTimeoutId != null) {
        window.clearTimeout(persistTimeoutId);
        persistTimeoutId = null;
      }
    }

    function schedulePersist() {
      if (!enableResume || !storage) return;
      if (persistIdleId != null || persistTimeoutId != null) return;

      const flush = () => {
        persistIdleId = null;
        persistTimeoutId = null;
        persist();
      };

      try {
        if (window.requestIdleCallback) {
          persistIdleId = window.requestIdleCallback(flush, { timeout: 1200 });
          return;
        }
      } catch (_) {
        // ignore
      }

      // Fallback: short delay so we don't block tap-to-tap interaction.
      persistTimeoutId = window.setTimeout(flush, 160);
    }

    function persist() {
      if (!enableResume || !storage) return;
      try {
        const payload = {
          v: 3,
          path: state.path,
          index: state.index,
          answersByQuestionId: state.answersByQuestionId,
          completed: state.completed
        };
        storage.setItem(storageKey, JSON.stringify(payload));
      } catch (_) {
        // ignore
      }
    }

    function clearPersisted() {
      try { if (storage) storage.removeItem(storageKey); } catch (_) { /* ignore */ }
    }

    function sanitizeAnswers(obj) {
      const out = Object.create(null);
      if (!obj || typeof obj !== 'object') return out;

      for (const qid in obj) {
        const optId = obj[qid];
        if (typeof optId !== 'string') continue;
        const q = questionGraph.byId.get(qid);
        if (!q || !Array.isArray(q.options)) continue;

        let ok = false;
        const map = q.__optById;
        if (map && typeof map === 'object') {
          ok = !!map[optId];
        } else {
          for (let i = 0; i < q.options.length; i++) {
            const o = q.options[i];
            if (o && o.option_id === optId) {
              ok = true;
              break;
            }
          }
        }
        if (ok) out[qid] = optId;
      }

      return out;
    }

    function sanitizePath(path) {
      const out = [];
      if (Array.isArray(path)) {
        for (let i = 0; i < path.length; i++) {
          const n = path[i];
          if (typeof n !== 'number') continue;
          if (!questionGraph.byNode.has(n)) continue;
          out.push(n);
        }
      }

      // Ensure we always start from the canonical start node.
      const start = questionGraph.startNodeId;
      if (out.length === 0 || out[0] !== start) {
        out.length = 0;
        out.push(start);
      }

      return out;
    }

    function rebuildShortPathFromAnswers(answersByQuestionId) {
      // Reconstruct a short-mode path purely from answers.
      // This avoids stale/invalid persisted paths after theme updates and ensures
      // the router remains the single source of truth.
      const answers = (answersByQuestionId && typeof answersByQuestionId === 'object')
        ? answersByQuestionId
        : Object.create(null);

      const cap = isFiniteNumber(maxQuestions) ? Math.max(1, Math.floor(maxQuestions)) : DEFAULT_MAX_QUESTIONS;
      const out = [questionGraph.startNodeId];
      const seen = new Set(out);

      for (let safety = 0; safety < cap; safety++) {
        const nodeId = out[out.length - 1];
        const q = questionGraph.byNode.get(nodeId);
        if (!q || !q.question_id) break;

        const selected = answers[q.question_id];
        if (!selected) break;

        const askedCount = out.length;
        const next = computeShortNextStep(
          questionGraph,
          q.question_id,
          selected,
          answers,
          askedCount,
          maxQuestions,
          targetQuestions,
          shortFlowModel
        );

        if (next === questionGraph.resultToken || next == null) break;
        if (typeof next !== 'number' || !questionGraph.byNode.has(next)) break;
        if (seen.has(next)) break;

        seen.add(next);
        out.push(next);
      }

      return out;
    }

    function filterAnswersToPath(answersByQuestionId, path) {
      const out = Object.create(null);
      if (!answersByQuestionId || typeof answersByQuestionId !== 'object') return out;

      const keep = new Set();
      if (Array.isArray(path)) {
        for (let i = 0; i < path.length; i++) {
          const q = questionGraph.byNode.get(path[i]);
          if (q && q.question_id) keep.add(q.question_id);
        }
      }

      keep.forEach((qid) => {
        const v = answersByQuestionId[qid];
        if (typeof v === 'string') out[qid] = v;
      });

      return out;
    }

    function isPathFullyAnswered(path, answersByQuestionId) {
      if (!Array.isArray(path) || path.length === 0) return false;
      const ans = answersByQuestionId || {};
      for (let i = 0; i < path.length; i++) {
        const q = questionGraph.byNode.get(path[i]);
        if (!q || !q.question_id) continue;
        if (!ans[q.question_id]) return false;
      }
      return true;
    }

    function firstUnansweredIndex(path, answersByQuestionId) {
      if (!Array.isArray(path) || path.length === 0) return 0;
      const ans = answersByQuestionId || {};
      for (let i = 0; i < path.length; i++) {
        const q = questionGraph.byNode.get(path[i]);
        if (!q || !q.question_id) continue;
        if (!ans[q.question_id]) return i;
      }
      return Math.max(0, path.length - 1);
    }

    function loadPersisted() {
      if (!enableResume || !storage) return null;
      try {
        const raw = storage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || (parsed.v !== 2 && parsed.v !== 3)) return null;

        const rawAnswers = sanitizeAnswers(parsed.answersByQuestionId);
        const path = (flowMode === 'short')
          ? rebuildShortPathFromAnswers(rawAnswers)
          : sanitizePath(parsed.path);

        // Drop answers that are no longer reachable in the current flow.
        // This prevents stale answers from influencing results in newer versions.
        const answersByQuestionId = filterAnswersToPath(rawAnswers, path);

        // Prefer landing on the first unanswered question (more intuitive than
        // preserving a potentially stale index after edits/back-navigation).
        const idx = firstUnansweredIndex(path, answersByQuestionId);

        return {
          path,
          index: idx,
          answersByQuestionId,
          completed: !!parsed.completed && isPathFullyAnswered(path, answersByQuestionId)
        };
      } catch (_) {
        return null;
      }
    }

    function setPanelVisibility({ showStart, showQuiz, showTopbar, showResults }) {
      const showingResults = !!showResults;

      if (startScreen) startScreen.hidden = !showStart;
      if (quizScreen) quizScreen.hidden = !showQuiz;
      if (topbar) topbar.hidden = !showTopbar;
      if (resultsBox) resultsBox.hidden = !showingResults;

      // When results are shown, hide the question UI + nav to avoid confusion and reduce clutter.
      if (questionSection) questionSection.hidden = showingResults;
      if (formEl) formEl.hidden = showingResults;
      if (navEl) navEl.hidden = showingResults;

      if (showingResults && inlineError) {
        inlineError.hidden = true;
        inlineError.textContent = '';
      }
    }

    function setInlineError(msg) {
      if (!inlineError) return;
      if (!msg) {
        inlineError.hidden = true;
        inlineError.textContent = '';
        return;
      }
      inlineError.hidden = false;
      inlineError.textContent = msg;
    }

    function setProgress(currentIndex, totalUpperBound) {
      const cur = Math.max(1, currentIndex + 1);
      const total = Math.max(cur, totalUpperBound || cur);

      // Numeric counter is intentionally hidden visually (question count is dynamic),
      // but we still keep the numbers in the DOM for screen readers.
      if (qcountEl) qcountEl.textContent = `${cur} / ${total}`;

      // Match the default ML-9665 quiz behavior: reflect the current step
      // (question N of total) rather than "completed questions".
      const ratio = clamp01(cur / Math.max(1, total));
      root.style.setProperty('--ml-9665-progress', ratio.toFixed(4));

      if (progressbar) {
        const pct = Math.round(ratio * 100);
        progressbar.setAttribute('aria-valuenow', String(pct));
        progressbar.setAttribute('aria-valuetext', `Question ${cur} of ${total} (${pct}%)`);
      }
    }

    function getCurrentQuestion() {
      const nodeId = state.path[state.index];
      return questionGraph.byNode.get(nodeId) || null;
    }

    function getSelectedOptionIdForCurrentQuestion() {
      // We fully re-render the fieldset each question, so querying within it is safe and fast.
      if (choicesFieldset) {
        const checked = choicesFieldset.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      }

      // Fallback (should rarely be needed).
      const q = getCurrentQuestion();
      if (!q) return null;
      const name = `${domIdPrefix}-${q.question_id}`;
      const checked = root.querySelector(`input[type="radio"][name="${escapeCssAttrValue(name)}"]:checked`);
      return checked ? checked.value : null;
    }

    function getAnswersUpToIndex(idx) {
      const out = Object.create(null);
      const limit = Math.min(idx, state.path.length - 1);
      for (let i = 0; i <= limit; i++) {
        const nodeId = state.path[i];
        const qq = questionGraph.byNode.get(nodeId);
        if (!qq || !qq.question_id) continue;
        const sel = state.answersByQuestionId[qq.question_id];
        if (sel) out[qq.question_id] = sel;
      }
      return out;
    }

    function normalizeNextToken(next) {
      if (next === questionGraph.resultToken) return questionGraph.resultToken;
      if (typeof next === 'number') return next;
      if (typeof next === 'string') {
        const t = next.trim();
        if (!t) return null;
        if (t === questionGraph.resultToken) return questionGraph.resultToken;
        const asNum = Number(t);
        if (Number.isFinite(asNum)) return asNum;

        // Support flows that store `next` as a question_id (e.g. 'Q10') instead of a node_id.
        try {
          if (questionGraph.byId && typeof questionGraph.byId.get === 'function') {
            const q = questionGraph.byId.get(t);
            if (q && isFiniteNumber(q.node_id)) return q.node_id;
          }
        } catch (_) {
          // ignore
        }
      }
      return null;
    }

    function computeNextStepFromSelection(q, selectedOptionId, answersSnapshot, askedCount) {
      if (!q || !selectedOptionId) return null;
      if (flowMode === 'short') {
        const n = computeShortNextStep(questionGraph, q.question_id, selectedOptionId, answersSnapshot, askedCount, maxQuestions, targetQuestions, shortFlowModel);
        return (n == null) ? questionGraph.resultToken : normalizeNextToken(n);
      }
      let opt = null;
      const map = q.__optById;
      if (map && typeof map === 'object' && map[selectedOptionId]) {
        opt = map[selectedOptionId];
      } else if (Array.isArray(q.options)) {
        opt = q.options.find((o) => o && o.option_id === selectedOptionId) || null;
      }
      return opt ? normalizeNextToken(opt.next) : null;
    }
    function updateNextButtonLabelForCurrentSelection(selectedOverride) {
      const q = getCurrentQuestion();
      if (!q) return;

      const selectedOptionId = selectedOverride || getSelectedOptionIdForCurrentQuestion();

      // Disable Next until an answer is selected (prevents accidental taps on mobile).
      setButtonDisabled(nextBtn, isSubmitting || !selectedOptionId);

      if (!nextLabel) return;

      if (!selectedOptionId) {
        nextLabel.textContent = defaultNextLabelText;
        return;
      }

      const answersSnapshot = getAnswersUpToIndex(state.index);
      answersSnapshot[q.question_id] = selectedOptionId;

      const askedCount = state.index + 1;
      const next = computeNextStepFromSelection(q, selectedOptionId, answersSnapshot, askedCount);

      if (next === questionGraph.resultToken || next == null) {
        nextLabel.textContent = 'Submit';

        // Do not update the visible question progress when a radio answer is selected.
        // The progress bar now advances only when the user clicks Next/Submit and the
        // quiz advances or completes.

        // Proactively precompute results while the user is on the final question.
        // This keeps the submit experience snappy on slower mobile devices.
        if (!isSubmitting) {
          const h = hashAnswersKeyFast(spec, answersSnapshot);
          if (!(cachedScoredRes && cachedScoredResHash === h) && inFlightScoreHash !== h) {
            scheduleScoreCompute(answersSnapshot);
          }
        }
      } else {
        nextLabel.textContent = defaultNextLabelText;

        // Keep progress unchanged while the user is merely changing/selecting answers.
        // Branching can still change the total question count; that recalculation is
        // applied in renderQuestion() after the Next button is clicked.

        // If we previously kicked off a background score job (e.g., the user changed
        // their final answer), cancel it to avoid wasting battery/CPU.
        //
        // IMPORTANT: we may be mid-slice (idle callback currently executing), in which
        // case `idleScoreId`/`rafScoreId` can be null even though work is in-flight.
        // We still bump the token so the scorer bails at the next safe check.
        if (!isSubmitting && (inFlightScoreHash !== 0 || idleScoreId != null || rafScoreId != null)) {
          scoreToken += 1;
          clearIdleScore();
          inFlightScoreHash = 0;
        }
      }
    }

    function renderQuestion() {
      const q = getCurrentQuestion();
      if (!q) {
        setInlineError('Could not load this question.');
        return;
      }

      setPanelVisibility({ showStart: false, showQuiz: true, showTopbar: true, showResults: false });

      const variantIdx = variantIndexForQuestion(variantMode, sectionId, q.question_id);
      const prompt = getVariantText(q.variants, variantIdx).trim();
      if (questionText) {
        questionText.textContent = prompt;
        // Ensure the prompt can receive programmatic focus for a11y + mobile context.
        if (!questionText.hasAttribute('tabindex')) questionText.setAttribute('tabindex', '-1');
      }

      setButtonDisabled(backBtn, state.index <= 0);

      if (choicesFieldset) {
        // Preserve the (visually hidden) legend for screen readers.
        replaceChildrenSafe(choicesFieldset, choicesLegend);
        const previouslySelected = state.answersByQuestionId[q.question_id];

        const frag = document.createDocumentFragment();

        for (let i = 0; i < q.options.length; i++) {
          const opt = q.options[i];
          if (!opt || typeof opt !== 'object') continue;
          const optId = opt.option_id;
          const variants = answersByOptionId[optId];
          let ansText = getVariantText(variants, variantIdx);
          ansText = ansText ? String(ansText).trim() : '';
          // Defensive fallback if an option is missing from the answers map.
          if (!ansText) ansText = optionLabelFallback(opt);

          const checked = previouslySelected === optId;
          const node = createAnswerNode({
            domIdPrefix,
            qid: q.question_id,
            option: opt,
            text: ansText,
            checked
          });
          frag.appendChild(node);
        }

        choicesFieldset.appendChild(frag);
      }

      setInlineError('');

      const answersSnapshot = getAnswersUpToIndex(state.index);
      const totalUpper = (flowMode === 'short')
        ? computeShortUpperBound(answersSnapshot, maxQuestions, targetQuestions)
        : computeTotalUpperBound(questionGraph, answersSnapshot);
      setProgress(state.index, totalUpper);

      updateNextButtonLabelForCurrentSelection();

      if (questionText) {
        try { questionText.focus({ preventScroll: true }); } catch (_) { /* ignore */ }
      }

      // After re-render, ensure the prompt is visible on mobile.
      window.requestAnimationFrame(() => scrollRootToTopIfNeeded());

      schedulePersist();
    }

    function saveAnswerForCurrentQuestion(optionId) {
      const q = getCurrentQuestion();
      if (!q) return;
      state.answersByQuestionId[q.question_id] = optionId;
    }

    function truncateFromIndex(idx) {
      const toRemove = state.path.slice(idx + 1);
      for (let i = 0; i < toRemove.length; i++) {
        const nodeId = toRemove[i];
        const qq = questionGraph.byNode.get(nodeId);
        if (qq && qq.question_id && state.answersByQuestionId[qq.question_id]) {
          delete state.answersByQuestionId[qq.question_id];
        }
      }
      state.path = state.path.slice(0, idx + 1);
      if (state.index > idx) state.index = idx;
    }
    function goNext() {
      if (isSubmitting || isButtonDisabled(nextBtn)) return;
      const q = getCurrentQuestion();
      if (!q) return;

      const selected = getSelectedOptionIdForCurrentQuestion();
      if (!selected) {
        setInlineError('Please choose an answer to continue.');
        return;
      }

      // If the user is moving forward through already-answered questions without changing
      // their selection, we can *sometimes* advance without rebuilding the rest of the path.
      //
      // Guard: ensure the cached "next" node is still what the router would choose.
      // This prevents edge cases where a persisted path becomes stale after theme updates.
      const prev = state.answersByQuestionId[q.question_id];
      const hasForward = state.index < (state.path.length - 1);
      if (hasForward && prev === selected) {
        const nextNode = state.path[state.index + 1];
        const answersSnapshot = getAnswersUpToIndex(state.index);
        const askedCount = state.index + 1;
        const expectedNext = computeNextStepFromSelection(q, selected, answersSnapshot, askedCount);

        if (expectedNext === questionGraph.resultToken) {
          submitQuiz();
          return;
        }

        if (typeof expectedNext === 'number' && expectedNext === nextNode && questionGraph.byNode.has(nextNode)) {
          setInlineError('');
          state.index += 1;
          renderQuestion();
          return;
        }
      }

      setInlineError('');

      // Recompute the path forward from this point (answer changed or no safe forward node).
      truncateFromIndex(state.index);
      saveAnswerForCurrentQuestion(selected);

      let next = null;
      if (flowMode === 'short') {
        const askedCount = state.path.length; // after truncateFromIndex() this is the count up to the current question
        next = computeShortNextStep(questionGraph, q.question_id, selected, state.answersByQuestionId, askedCount, maxQuestions, targetQuestions, shortFlowModel);

        // In short mode we never fall back to the full graph (prevents accidental 40+ question runs).
        if (next == null) next = questionGraph.resultToken;

        // Absolute cap (defensive): finish once we hit maxQuestions.
        if (askedCount >= maxQuestions) next = questionGraph.resultToken;
      } else {
        let opt = null;
        const map = q.__optById;
        if (map && typeof map === 'object' && map[selected]) {
          opt = map[selected];
        } else if (Array.isArray(q.options)) {
          opt = q.options.find((o) => o && o.option_id === selected) || null;
        }
        next = opt ? opt.next : null;
      }

      next = normalizeNextToken(next);

      if (next === questionGraph.resultToken || next == null) {
        submitQuiz();
        return;
      }

      if (!questionGraph.byNode.has(next)) {
        submitQuiz();
        return;
      }

      state.path.push(next);
      state.index = state.path.length - 1;
      renderQuestion();
    }

    function goBack() {
      if (isSubmitting || isButtonDisabled(backBtn)) return;
      if (state.index <= 0) return;
      state.index -= 1;
      setInlineError('');
      renderQuestion();
    }


    function showResults(preScoredRes) {
      // If a submit timer is still pending, cancel it (results are now visible).
      clearSubmitTimer();
      // Abort any background scoring work; results are now being rendered.
      scoreToken += 1;
      clearIdleScore();
      inFlightScoreHash = 0;
      isSubmitting = false;
      hideLoader();

      // Re-enable nav controls for future runs.
      setButtonDisabled(nextBtn, false);
      setButtonDisabled(backBtn, false);

      setPanelVisibility({ showStart: false, showQuiz: true, showTopbar: false, showResults: true });
      setInlineError('');

      if (restartBtn) restartBtn.textContent = 'Start the Quiz Over';

      const titleEl = root.querySelector('.ml-9665-results__title');
      if (titleEl) {
        titleEl.textContent = 'The Best Dog Breeds For You';
        // Make focus management reliable across themes.
        if (!titleEl.hasAttribute('tabindex')) titleEl.setAttribute('tabindex', '-1');
      }

      // Ensure results are immediately visible on mobile after the loader finishes.
      scrollIntoViewIfNeeded(titleEl || resultsBox);

      replaceChildrenSafe(resultsBody);
      if (!resultsBody) return;

      // Render a simple note into the results container without using `innerHTML`.
      // (Avoids accidental HTML injection from runtime error messages.)
      const renderResultsNote = (message) => {
        replaceChildrenSafe(resultsBody);
        const p = document.createElement('p');
        p.className = 'ml-9665-dbq-note';
        p.textContent = String(message == null ? '' : message);
        resultsBody.appendChild(p);
      };

      const currentHash = hashAnswersKeyFast(spec, state.answersByQuestionId);

      let scoredRes = null;
      try {
        if (preScoredRes && Array.isArray(preScoredRes.scored) && preScoredRes.__hash === currentHash) {
          scoredRes = preScoredRes;
        } else if (cachedScoredRes && cachedScoredResHash === currentHash && Array.isArray(cachedScoredRes.scored)) {
          scoredRes = cachedScoredRes;
        } else {
          scoredRes = scoreAllBreedsForAnswers(state.answersByQuestionId, resultsCount);
          if (scoredRes) {
            scoredRes.__hash = currentHash;
            cachedScoredRes = scoredRes;
            cachedScoredResHash = currentHash;
          }
        }
      } catch (err) {
        const msg = (err && err.message) ? err.message : 'Unable to calculate results.';
        renderResultsNote(msg);
        return;
      }

      if (!scoredRes || !Array.isArray(scoredRes.scored)) {
        renderResultsNote('No results available. Please try again.');
        return;
      }

      const top = scoredRes.scored;
      const plan = (scoredRes && scoredRes.__engineVersion === 'v2') ? null : (scoredRes.plan || buildScoringPlan(spec, state.answersByQuestionId));

      if (enableDebug) {
        // eslint-disable-next-line no-console
        console.debug('[ML-9665 DBQ] Results', {
          maxPossible: scoredRes.maxPossible,
          top: top.slice(0, 5).map((x) => [x.breed.breed_name, x.score, x.scorePct])
        });
      }

      if (!top || top.length === 0) {
        renderResultsNote('No results available. Please try again.');
        return;
      }

      // If every breed was disqualified by hard constraints, the score will be extremely negative.
      // In that case, show a helpful message instead of 5 "matches" that aren't actually matches.
      const HARD_DISQUALIFIED_CUTOFF = -1e8;
      if (top[0] && typeof top[0].score === 'number' && top[0].score <= HARD_DISQUALIFIED_CUTOFF) {
        const a = state.answersByQuestionId || {};
        const flags = [];
        if (a['Q48'] === 'Q48_A') flags.push('allergy-friendly');
        if (a['Q32'] === 'Q32_A') flags.push('top-tier kid-friendly');
        if (a['Q35'] === 'Q35_A') flags.push('cat/small-pet compatible');
        const detail = flags.length ? ` (dealbreakers selected: ${flags.join(', ')})` : '';
        renderResultsNote(`We couldn't find any breeds that match your must-have requirements${detail}. Try restarting and loosening one or two dealbreakers to see more options.`);
        return;
      }

      const totalShown = Math.min(5, top.length);

      const list = document.createElement('ol');
      list.className = 'ml-9665-dbq-results-list';

      for (let i = 0; i < totalShown; i++) {
        const r = top[i];
        const b = r.breed;

        const url = b.article && b.article.url ? b.article.url : '';
        const title = b.breed_display_name || b.breed_name || 'Dog breed';
        const img = b.article && b.article.image ? b.article.image : '';

        const item = document.createElement('li');
        item.className = 'ml-9665-dbq-result';

        const grid = document.createElement('div');
        grid.className = 'ml-9665-dbq-result__grid';

        const media = document.createElement(url ? 'a' : 'div');
        media.className = 'ml-9665-dbq-result__media';
        if (url) {
          media.href = url;
          media.setAttribute('aria-label', `View ${title}`);
        }

        if (img) {
          const image = document.createElement('img');
          // Show the top match ASAP (especially important on mobile).
          image.loading = i === 0 ? 'eager' : 'lazy';
          image.decoding = 'async';
          image.alt = title;
          // Prevent CLS by setting a stable 3:2 box. (All breed photos are 3:2.)
          image.width = 600;
          image.height = 400;
          image.src = img;
          // fetchpriority is safe to set even when unsupported.
          if (i === 0) image.setAttribute('fetchpriority', 'high');
          media.appendChild(image);
        } else {
          const ph = document.createElement('div');
          ph.className = 'ml-9665-dbq-result__media-ph';
          ph.setAttribute('aria-hidden', 'true');
          media.appendChild(ph);
        }

        const body = document.createElement('div');
        body.className = 'ml-9665-dbq-result__body';

        const headline = document.createElement('div');
        headline.className = 'ml-9665-dbq-result__headline';

        const rank = document.createElement('span');
        rank.className = 'ml-9665-dbq-result__rank';
        rank.textContent = `${i + 1}/${totalShown}`;

        const name = document.createElement(url ? 'a' : 'span');
        name.className = 'ml-9665-dbq-result__name';
        if (url) name.href = url;
        name.textContent = title;

        const pipe = document.createElement('span');
        pipe.className = 'ml-9665-dbq-result__pipe';
        pipe.setAttribute('aria-hidden', 'true');
        pipe.textContent = '|';

        const score = document.createElement('span');
        score.className = 'ml-9665-dbq-result__score';
        score.textContent = `${r.scorePct} / 100`;

        headline.appendChild(rank);
        headline.appendChild(name);
        headline.appendChild(pipe);
        headline.appendChild(score);

        const why = document.createElement('div');
        why.className = 'ml-9665-dbq-result__why';
        why.textContent = 'Why this dog breed is a good fit for you:';

        const tags = document.createElement('ul');
        tags.className = 'ml-9665-dbq-result__tags';

        // Pick the top 3 reasons (short tags) for this breed based on the strongest
        // scoring contributions from the questions the user actually answered.
        const entries = resultReasonTags(r, plan, b);

        for (let j = 0; j < entries.length; j++) {
          const li = document.createElement('li');
          li.textContent = entries[j];
          tags.appendChild(li);
        }

        body.appendChild(headline);
        body.appendChild(why);
        body.appendChild(tags);

        grid.appendChild(media);
        grid.appendChild(body);
        item.appendChild(grid);
        list.appendChild(item);
      }

      resultsBody.appendChild(list);

      const sharePanel = createDogResultSharePanel(top[0]);
      if (sharePanel) resultsBody.appendChild(sharePanel);

      const note = document.createElement('p');
      note.className = 'ml-9665-dbq-note';
      note.textContent = 'These suggestions are based on your answers and breed averages. Individual dogs vary — meet in person and talk with a breeder/rescue before deciding.';
      resultsBody.appendChild(note);

      if (titleEl) {
        try { titleEl.focus({ preventScroll: true }); } catch (_) { /* ignore */ }
        window.requestAnimationFrame(() => {
          try {
            const behavior = prefersReducedMotion ? 'auto' : 'smooth';
            titleEl.scrollIntoView({ block: 'start', behavior });
          } catch (_) {
            // ignore
          }
        });
      }
    }

    function reset() {
      cancelSubmitFlow();
      state = {
        path: [questionGraph.startNodeId],
        index: 0,
        answersByQuestionId: Object.create(null),
        completed: false
      };
      clearPersisted();
      setPanelVisibility({ showStart: true, showQuiz: false, showTopbar: false, showResults: false });
      setInlineError('');
      if (startError) {
        startError.hidden = true;
        startError.textContent = '';
      }
    }

    function startFresh() {
      reset();
      setPanelVisibility({ showStart: false, showQuiz: true, showTopbar: true, showResults: false });
      renderQuestion();
    }

    function resume() {
      const persisted = loadPersisted();
      if (!persisted) {
        startFresh();
        return;
      }
      state = {
        path: persisted.path,
        index: persisted.index,
        answersByQuestionId: persisted.answersByQuestionId,
        completed: persisted.completed
      };

      if (state.completed) showResults();
      else renderQuestion();
    }

    // Wire events
    if (startBtn) startBtn.addEventListener('click', startFresh);
    if (resumeBtn) resumeBtn.addEventListener('click', resume);
    if (backBtn) backBtn.addEventListener('click', goBack);
    if (nextBtn) nextBtn.addEventListener('click', goNext);
    if (restartBtn) restartBtn.addEventListener('click', startFresh);

    root.addEventListener('change', (e) => {
      const t = e && e.target;
      if (!t || t.tagName !== 'INPUT' || t.type !== 'radio') return;

      // Make the current selection part of state immediately so refresh/resume works
      // even if the user hasn't tapped Next yet.
      const q = getCurrentQuestion();
      const selected = typeof t.value === 'string' ? t.value : null;
      if (q && selected) {
        const prev = state.answersByQuestionId[q.question_id] || null;
        // If the user changes an earlier answer, invalidate any downstream path/answers.
        if (prev && prev !== selected && state.index < (state.path.length - 1)) {
          truncateFromIndex(state.index);
        }
        state.answersByQuestionId[q.question_id] = selected;
        state.completed = false;
        schedulePersist();
      }

      updateNextButtonLabelForCurrentSelection(selected || undefined);
      setInlineError('');
    });

    // Treat Enter as a 'Next/Submit' shortcut when the quiz screen is active.
    root.addEventListener('keydown', (e) => {
      if (!e) return;
      const key = e.key;
      if (key !== 'Enter') return;

      // Don't hijack Enter in text inputs/textarea (not used here, but safe).
      const target = e.target;
      const tag = target && target.tagName ? String(target.tagName).toUpperCase() : '';
      if (tag === 'TEXTAREA' || tag === 'INPUT' && target.type === 'text') return;

      if (!quizScreen || quizScreen.hidden) return;
      if (resultsBox && !resultsBox.hidden) return;
      if (isButtonDisabled(nextBtn)) return;

      e.preventDefault();
      goNext();
    });

    if (formEl) {
      formEl.addEventListener('submit', (e) => {
        if (e) e.preventDefault();
        if (!quizScreen || quizScreen.hidden) return;
        if (resultsBox && !resultsBox.hidden) return;
        if (isButtonDisabled(nextBtn)) return;
        goNext();
      });
    }

    // Initialize start screen and resume affordance.
    const persisted = loadPersisted();
    if (persisted && resumeBtn) resumeBtn.hidden = false;

    // Expose a tiny cancel hook for Shopify theme-editor section lifecycle events.
    // (Used to clear timers/idle callbacks if the section is removed.)
    root.__ml9665DbqCancel = cancelSubmitFlow;

    setPanelVisibility({ showStart: true, showQuiz: false, showTopbar: false, showResults: false });
  }

  function initAll() {
    const roots = document.querySelectorAll(ROOT_SELECTOR);
    for (let i = 0; i < roots.length; i++) initRoot(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  // Shopify theme editor: sections can be (re)loaded without a full page refresh.
  // Initialize any newly-added quiz roots, and cancel timers when sections unload.
  document.addEventListener('shopify:section:load', (event) => {
    try {
      const container = event && event.target;
      if (!container || typeof container.querySelectorAll !== 'function') return;
      const roots = container.querySelectorAll(ROOT_SELECTOR);
      for (let i = 0; i < roots.length; i++) initRoot(roots[i]);
    } catch (_) {
      // ignore
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    try {
      const container = event && event.target;
      if (!container || typeof container.querySelectorAll !== 'function') return;
      const roots = container.querySelectorAll(ROOT_SELECTOR);
      for (let i = 0; i < roots.length; i++) {
        const r = roots[i];
        if (r && typeof r.__ml9665DbqCancel === 'function') r.__ml9665DbqCancel();
      }
    } catch (_) {
      // ignore
    }
  });
})();
